import _ from "lodash";
import baseAxios from "axios";
import moment from "moment";
import i18n, { t, changeLanguage } from "i18next";

import {
  setPolicies,
  setSelectedBadgeItemId,
  setBadgeSwitchItems,
  setFilterDates,
  setAvailableDates,
  setSelectedDate,
  setVideos,
  setTransStatus,
  setUnitFilters,
  setShowAllUnit,
  setSelfTourLink,
  setSelectedVideoSwitchId,
  setBedRooms,
  setBudgets,
  setBadgeSwitchVideos,
  updateFaq,
  setShowLangChange,
  setActiveOptionComp,
  addNewMessage,
  removeAndAddMessages,
  freezeGreeting,
  restartChatMessages,
  moveToTop,
  setReplies,
  setOptionsTypeLogs,
  filterEditMessages,
  setShowAllOptions,
  setLabelItems,
  setAllData,
  setAvailableSlots,
  setSelectedOptions,
  setSlotsLoading,
  setShouldFetchSlots,
  setEntryId,
  setEmail,
  setConversationID,
  setAMAReply,
  setAMAResponse,
  setIsSpanishLanguage,
  resetSession,
} from "slices";
import {
  WHEN_MOVE_IN_REPLIES,
  ITEM_RECORD_TOOL_ALIASES,
  getVisibleQuickActions,
  VISIT_RESIDENT_PORTAL_REPLIES,
  FORM_4_CONTACT_US_REPLIES,
  FORM_4_SCHEDULE_TOUR_REPLIES,
  FORM_4_UPDATE_SELECTIONS_REPLIES,
  POST_CONTACT_US_REPLIES,
  POST_SCHEDULE_TOUR_REPLIES,
  POST_UPDATE_SELECTIONS_REPLIES,
  SHOW_ALL_UNITS_OPTION,
  SELF_TOUR_REPLIES,
  FAQS_REPLIES,
  FEEDBACK_TOUR_REPLIES,
  FEEDBACK_CONTACT_REPLIES,
  loaders,
  dotLoader,
  filterFaqOptions,
  INITIAL_REPLIES,
  INITIAL_REPLY_OPTIONS,
  EXPLORE_MORE_OPTION,
  defaultEditFormats,
  detectAndGenerateLinks,
  CUSTOM_BUTTON,
  DO_I_QUALIFY_REPLIES,
  textConstants,
  axios,
  cancelAllRequests,
  isManualCancellation,
  graphqlFetch,
  resolveGreetingText,
} from "utils";
import FriendlyBot from "components/Mascots/FriendlyBot";
import TheHayley from "components/Mascots/TheHayley";
import TheMinimalist from "components/Mascots/TheMinimalist";
import {
  AMA_INPUT_MAX_LENGTH,
  APPOINTMENT_TYPES,
  CANCEL_TOUR_REPLIES,
  // chatData,
  compMsgKeys,
  DEFAULT_SESSION_ID,
  ERROR_MSG,
  LEGACY_ERROR_MSG,
  filterRepliesByOptions,
  FOLLOW_UP_KEYS,
  FORM_4_CONTACT_US_OPTIONS,
  HANDOFF_EXCLUDED_SUBJECTS,
  HAYLEY_EXTRA_OPTION_SOURCES,
  HAYLEY_MESSAGE_EDIT_TYPES,
  LANG_CHANGE_REPLIES,
  optionsBySubject,
  reportIds,
  RESCHEDULE_TOUR_REPLIES,
  SCHEDULE_TOUR_INQUIRIES,
  SCHEDULE_TOUR_SUBJECTS,
  TOUR_MSGS_KEY,
} from "utils/constants/index";
import { withResolvedScopeNames } from "utils/fees/index";
import { setSearchResult } from "slices/units-slice/index";
import { setIsDisabled } from "slices/option-box-slice/index";
import { setTourAction, resetScheduleTourState, setTourPreference, selectTourPreference } from "slices/schedule-tour-slice/index";
import { reportHandledError } from "utils/api/reportApiError";

let store;

export const injectStore = (_store) => {
  store = _store;
};

/** The app stores "en" / "sp"; the API contract is ISO "en" / "es". */
export const getApiLocale = () => {
  try {
    const property = store?.getState?.()?.property;

    // Authoritative once get_management answers: an English-only property must never request
    // Spanish, even with a stale "sp" preference left in localStorage.
    if (property?.isMultiLanguage === false) {
      return "en";
    }

    // Gated at the source (App.js). Checked before the null case because the two flags are
    // dispatched separately, leaving a window where this is set and isMultiLanguage is not.
    if (property?.isSpanishLanguage === true) {
      return "es";
    }

    // Flag unknown: get_management is the call that delivers it, so fall back to the boot
    // preference i18n was initialised from.
    if (property?.isMultiLanguage == null) {
      return i18n.language === "sp" ? "es" : "en";
    }

    return "en";
  } catch (error) {
    return "en";
  }
};

/** Append `locale` to a caller-supplied query string, picking `?` or `&` for it. */
const withLocale = (queryParams = "") => `${queryParams}${queryParams.includes("?") ? "&" : "?"}locale=${getApiLocale()}`;

export const getMascotManagement = (propertyId, apiKey) => {
  const state = store.getState();
  const isJAI = state.property.isJAI || "";

  const locale = getApiLocale();

  if (isJAI) {
    return axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/properties/get_management?property_id=${propertyId}&api_key=${encodeURIComponent(
        apiKey
      )}&from=preview&locale=${locale}`
    );
  }

  return axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/properties/get_management?property_id=${propertyId}&api_key=${encodeURIComponent(
      apiKey
    )}&from=property_website&locale=${locale}`
  );
};

export const updateLanguage = (propertyId, apiKey, isSpanish) => {
  return axios.post(
    `${process.env.REACT_APP_API_BASE_URL}/properties/update_haylay_language?property_id=${propertyId}&api_key=${encodeURIComponent(
      apiKey
    )}&set_spanish_language=${isSpanish}`
  );
};

export const getPromotionInfo = (propertyId, apiKey, config = {}) => {
  return axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/properties/promotion_info?api_key=${encodeURIComponent(apiKey)}&property_id=${propertyId}&locale=${getApiLocale()}`,
    config
  );
};

const getGraphQLHeaders = () => {
  const myHeaders = new Headers();
  myHeaders.append("Authorization", store?.getState()?.property?.api_key || "myhyly-hyblast-sync");
  myHeaders.append("Content-Type", "application/json");

  return myHeaders;
};

export const getPrivacyPolicyDisclaimer = (orgId) => {
  const myHeaders = getGraphQLHeaders();

  const graphql = JSON.stringify({
    query: `
        query ($orgId: ID!) {
          privacyPolicyDisclaimer(orgId: $orgId) {
            disclaimer
            disclaimerEnabled
          }
        }
      `,
    variables: {
      orgId: String(orgId),
    },
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions);
};

export const fetchPropertyData = (propertyId, forceRefresh = false) => {
  const params = `property_id=${propertyId}${forceRefresh ? "&force_refresh=true" : ""}`;

  return baseAxios.get(`${process.env.REACT_APP_API_BASE_URL}/properties/api_token?${params}`);
};

export const getBedroomTypes = (queryParams) => {
  return axios.get(`${process.env.REACT_APP_API_BASE_URL}/apartments/bedroom_types${queryParams}`, { cancellable: true });
};

export const getPriceBrackets = (queryParams) => {
  const queryString = getFirstBedroomTypeQuery(queryParams);

  return axios.get(`${process.env.REACT_APP_API_BASE_URL}/apartments/price_brackets${queryString}`, { cancellable: true });
};

export const isValidPriceRange = (value) => Array.isArray(value) && value.length === 2 && value.every((n) => typeof n === "number" && Number.isFinite(n));

export const normalizePriceBrackets = (data) => {
  const safe = data && typeof data === "object" ? data : {};

  return {
    ...safe,
    _custom_price_range: isValidPriceRange(safe._custom_price_range) ? safe._custom_price_range : null,
    _form_desired_price_range: Array.isArray(safe._form_desired_price_range) ? safe._form_desired_price_range : [],
  };
};

export const getMoveInDates = (queryParams) => {
  const queryString = getFirstBedroomTypeQuery(queryParams);

  return axios.get(`${process.env.REACT_APP_API_BASE_URL}/apartments/time_options${queryString}`, { cancellable: true });
};

export const getUnits = (queryParams) => {
  const filterQueryParams = (params) => {
    const urlParams = new URLSearchParams(params);

    const bedroomType = urlParams.get("bedroom_type");

    if (bedroomType) {
      urlParams.delete("bedroom_type");
    }

    // Add JSON-style array format if valid
    if (bedroomType && bedroomType !== "all") {
      const types = bedroomType.startsWith("[") ? JSON.parse(bedroomType) : [bedroomType];
      const jsonArray = JSON.stringify(types.map((type) => type.trim()));
      urlParams.append("bedroom_type", jsonArray);
    }

    // Add buffers if desired_move_in is present
    if (urlParams.get("desired_move_in")) {
      urlParams.append("lower_buffer", 10);
      urlParams.append("upper_buffer", 10);
    }

    return urlParams.toString();
  };

  const filteredParams = filterQueryParams(queryParams);

  return axios.get(`${process.env.REACT_APP_API_BASE_URL}/properties/find_apartments?${filteredParams}&per_page=100&locale=${getApiLocale()}`, {
    cancellable: true,
  });
};

export const getFloorPlans = (queryParams) => {
  return axios.get(`${process.env.REACT_APP_API_BASE_URL}/properties/floor_plans${queryParams}`, { cancellable: true });
};

export const getMoveInOptions = (queryParams) => {
  return axios.get(`${process.env.REACT_APP_API_BASE_URL}/apartments/move_in_options${queryParams}`, { cancellable: true });
};

export const getResidentPortalLink = (queryParams) => {
  return axios.get(`${process.env.REACT_APP_API_BASE_URL}/apartments/user_portal${queryParams}`, { cancellable: true });
};

export const getAvailableDates = (queryParams) => {
  return axios.get(`${process.env.REACT_APP_API_BASE_URL}/properties/tour_availability${withLocale(queryParams)}`);
};

export const createNewContact = (data) => {
  return axios.post(`${process.env.REACT_APP_API_BASE_URL}/contacts`, data, { cancellable: true });
};

export const scheduleTour = (data) => {
  return axios.post(`${process.env.REACT_APP_API_BASE_URL}/properties/tour_confirmation?locale=${getApiLocale()}`, data, { cancellable: true });
};

export const findUserEntry = (queryParams) => {
  return axios.get(`${process.env.REACT_APP_API_BASE_URL}/contacts/find_entry${queryParams}`, { cancellable: true });
};

export const getGallery = (queryParams) => {
  return axios.get(`${process.env.REACT_APP_API_BASE_URL}/properties/images${queryParams}`, { cancellable: true });
};

export const getPolicies = (queryParams) => {
  return axios.get(`${process.env.REACT_APP_API_BASE_URL}/properties/policy${withLocale(queryParams)}`, { cancellable: true });
};

export const getSelfTourLink = (queryParams) => {
  return axios.get(`${process.env.REACT_APP_API_BASE_URL}/properties/get_tour_url${queryParams}`);
};

export const setFirstEntryFlag = () => {
  const propertyId = store?.getState()?.property?.property_id || "";

  if (propertyId) {
    localStorage.setItem(`${propertyId}-is_first_entry`, "true");
  }
};

export const checkAndClearFirstEntryFlag = () => {
  const propertyId = store?.getState()?.property?.property_id || "";
  const isFirstEntry = localStorage.getItem(`${propertyId}-is_first_entry`) === "true";

  if (isFirstEntry && propertyId) {
    localStorage.setItem(`${propertyId}-is_first_entry`, "false");
  }

  return isFirstEntry;
};

export const getFAQsReply = (data) => {
  const isFirstEntry = checkAndClearFirstEntryFlag();

  // Add the flag to payload if it exists
  const payload = {
    ...data,
    ...(data?.spanish_toggle &&
      typeof data?.message === "string" && {
        message: getLocalizedPayloadText(data.message, true),
      }),
    ...(isFirstEntry && { is_first_entry: true }),
  };

  return axios.post(`${process.env.REACT_APP_WS_BASE_URL}`, payload, { cancellable: true });
};

export const buildEmergencyMessage = (phone) => {
  if (phone) {
    return t("call_directly_for_assistance", { phoneNumber: `**${phone}**` });
  }

  // fallback mesage if no phone number
  return t("leasing_agent_will_contact");
};

// mockApi.js

// export const getFAQsReply = (data) => {
//   return new Promise((resolve) => {
//     setTimeout(() => {
//       resolve({
//         data: chatData,
//       });
//     }, 500); // Simulate API delay
//   });
// };

export const getPreferenceSelections = (data) => {
  const wsUrl = new URL(process.env.REACT_APP_WS_BASE_URL);

  return axios.post(`${wsUrl.origin}/hayleyaai/chat/get_preference_selections`, data);
};

export const updatePreferences = (data) => {
  const wsUrl = new URL(process.env.REACT_APP_WS_BASE_URL);

  return axios.post(`${wsUrl.origin}/hayleyaai/chat/update_preferences`, data);
};

/**
 * Format selected bedroom_type value for the update_preferences API.
 * "2-bedrooms" → '["2-bedrooms"]', "" → '[]'
 */
const formatBedroomForAPI = (value) => {
  if (!value || value === "") {
    return "[]";
  }

  return JSON.stringify([value]);
};

/**
 * Format selected desired_move_in value for the update_preferences API.
 * Accepts i18n keys (matching WHEN_MOVE_IN_OPTIONS labels) or a custom "MMM DD, YYYY" date.
 * "asap" → "today..", "next30Days" → "today..today+30", "notSure" → "", "May 01, 2026" → "2026-05-01"
 */
const formatMoveInForAPI = (value) => {
  if (!value || value === "notSure") {
    return "";
  }

  if (value === "asap") {
    return `${moment().format("YYYY-MM-DD")}..`;
  }

  if (value === "next30Days") {
    return `${moment().format("YYYY-MM-DD")}..${moment().add(30, "days").format("YYYY-MM-DD")}`;
  }

  if (value === "next60Days") {
    return `${moment().format("YYYY-MM-DD")}..${moment().add(60, "days").format("YYYY-MM-DD")}`;
  }

  // Custom date in "MMM DD, YYYY" format
  const parsed = moment(value, "MMM DD, YYYY", true);

  if (parsed.isValid()) {
    return parsed.format("YYYY-MM-DD");
  }

  return value;
};

/**
 * Format selected price value for the update_preferences API.
 * "Any" → "", [1200, 1500] → "1200-1500", "1200-1500" → "1200-1500"
 */
const formatPriceForAPI = (value) => {
  if (!value || value === "Any") {
    return "";
  }

  if (Array.isArray(value)) {
    return `${value[0]}-${value[1]}`;
  }

  return String(value);
};

export const buildUpdatePreferencesPayload = ({ propertyId, entryId, sessionId, bedroomType, desiredMoveIn, price }) => ({
  property_id: propertyId,
  entry_id: entryId,
  session_id: sessionId,
  source: "hayleyChat",
  bedroomPreference: formatBedroomForAPI(bedroomType),
  desiredMoveInDate: formatMoveInForAPI(desiredMoveIn),
  priceRange: formatPriceForAPI(price),
});

export const getVideos = (queryParams) => {
  return axios.get(`${process.env.REACT_APP_API_BASE_URL}/properties/videos${queryParams}`, { cancellable: true });
};

function updateApartmentContext(propertyId, options) {
  const apartmentContextKey = `${propertyId}-context`;
  const apartmentContext = localStorage.getItem(apartmentContextKey);

  if (!!apartmentContext) {
    const appContext = JSON.parse(apartmentContext);

    if (appContext?.length > 0) {
      const { desiredMoveIn, bedroomType, priceRange } = options;

      if (desiredMoveIn) {
        appContext[0].desiredMoveInDate = desiredMoveIn;
      }

      if (bedroomType) {
        appContext[0].bedroomPreference = bedroomType;
      }

      if (priceRange) {
        appContext[0].priceRange = priceRange;
      }

      localStorage.setItem(apartmentContextKey, JSON.stringify(appContext));
    }
  }
}

//Global cancel option
const CANCEL_OPTION = {
  content: {
    component: "NormalOptionTile",
    componentOptions: {
      icon: "",
      label: "cancel",
    },
  },
  nextOptionsType: "Cancel",
  message: [
    {
      message: "cancel",
      speaker: "user",
    },
  ],
  key: "cancel",
  disablePlaceholerMessage: false,
  data: null,
};

const scheduleTourFormInfo = (props) => {
  const { skip, isPhoneNumberRequired } = props;

  return new Promise((resolve, reject) => {
    const updatedContactUsOptions = updatePhoneNumberOptional(FORM_4_SCHEDULE_TOUR_REPLIES.options, isPhoneNumberRequired);

    const updatedContactUsReplies = {
      ...FORM_4_SCHEDULE_TOUR_REPLIES,
      options: updatedContactUsOptions,
    };
    resolve({ data: updatedContactUsReplies, skip: skip });
  });
};

// The chosen slot was taken: keep the form open, select the next free slot and tell the user.
// Used by guided (tour_confirmation) and AMA (bookingFailureCause).
export const recoverFromUnavailableSlot = ({ availableSlots, payload, isPhoneNumberRequired, extraReplies = {} }) => {
  const failedSlot = moment(`${payload?.date} ${payload?.time}`, "YYYY-MM-DD hh:mm:ss A");
  const slotsList = Array.isArray(availableSlots) ? availableSlots.filter((entry) => entry && typeof entry === "object") : [];

  // Never offer the slot that just failed again.
  const withoutFailedSlot = (slots) =>
    Array.isArray(slots) && failedSlot.isValid() ? slots.filter((s) => !moment(s?.time_slot, "YYYY-MM-DD hh:mm:ss A").isSame(failedSlot, "minute")) : slots;

  // Searches every object, since the list can hold one object per tour type.
  const slotsOf = (type) => withoutFailedSlot(findTourData(slotsList, type));

  let actualTourType = payload?.appointmentType;
  let slotsForType = slotsOf(actualTourType);

  // No slots for the chosen type: use the first type that has some.
  if (!slotsForType?.length) {
    for (const type of TOUR_TYPE_ORDER) {
      const typeSlots = slotsOf(type);

      if (typeSlots?.length > 0) {
        slotsForType = typeSlots;
        actualTourType = type;
        break;
      }
    }
  }

  let nextAvailableSlotFormatted = "";

  if (slotsForType?.length > 0) {
    const slotMoment = moment(slotsForType[0].time_slot, "YYYY-MM-DD hh:mm:ss A");
    nextAvailableSlotFormatted = `${slotMoment.format("ddd, MMM Do")} at ${slotMoment.format("h:mm A")}`;

    // Clear the chat's preferred time so the form selects the slot named in the message.
    store.dispatch(setTourPreference({}));

    // handleTourResponse reads the first object, so the chosen type goes first.
    const withFilteredType = slotsList.map((entry) =>
      Object.prototype.hasOwnProperty.call(entry, actualTourType) ? { ...entry, [actualTourType]: slotsForType } : entry
    );

    handleTourResponse(moveTourTypeToFront(withFilteredType, actualTourType), true, actualTourType);
  } else {
    // Nothing left: clear the failed selection so the form loads fresh slots.
    store.dispatch(resetScheduleTourState());
    store.dispatch(setShouldFetchSlots(true));
  }

  const expiredSlotFormatted = failedSlot.format("h:mm A");

  const botMessages = [
    nextAvailableSlotFormatted ? t("selectedSlotNotAvailable", { expiredSlotFormatted, nextAvailableSlotFormatted }) : "sorrySelectSlotNoAvail",
  ];

  return {
    data: {
      ...FORM_4_SCHEDULE_TOUR_REPLIES,
      options: updatePhoneNumberOptional(FORM_4_SCHEDULE_TOUR_REPLIES.options, isPhoneNumberRequired),
      ...extraReplies,
    },
    extraData: { botMessages },
  };
};

const allUnitsInfo = (props) => {
  const { priceRange, propertyId, encodedApiKey, bedroomType, desiredMoveIn, skip } = props;
  const state = store.getState();
  const appSSID = state.window.appSSID || "";

  return new Promise((resolve, reject) => {
    store.dispatch(
      setUnitFilters({
        bedrooms: bedroomType,
        budgets: priceRange,
        moveIns: desiredMoveIn || "any",
      })
    );

    const length = priceRange?.length || 0;

    const min = length === 0 ? null : length > 1 ? priceRange[0] : 0;
    const max = length === 0 ? null : length > 1 ? priceRange[1] : priceRange[0];

    let convertedDate = desiredMoveIn;

    if (moment(desiredMoveIn, "MMM DD, YYYY", true).isValid()) {
      convertedDate = moment(desiredMoveIn, "MMM DD, YYYY").format("YYYY-MM-DD");
    }

    const isPriceFilter = store.getState().property.isPriceFilter;
    let queryParams = `?property_id=${propertyId}&api_key=${encodedApiKey}${bedroomType ? `&bedroom_type=${bedroomType}` : ""}${
      desiredMoveIn ? `&desired_move_in=${convertedDate || desiredMoveIn}` : ""
    }`;

    if (isPriceFilter) {
      queryParams = `?property_id=${propertyId}&api_key=${encodedApiKey}${bedroomType ? `&bedroom_type=${bedroomType}` : ""}${
        desiredMoveIn ? `&desired_move_in=${convertedDate || desiredMoveIn}` : ""
      }${min ? `&min_price=${min}` : ""}${max ? `&max_price=${max}` : ""}`;
    }

    store.dispatch(setShowAllUnit(false));

    updateApartmentContext(propertyId, {
      desiredMoveIn: convertedDate || desiredMoveIn,
      bedroomType: bedroomType,
      priceRange: priceRange,
    });

    getUnits(queryParams)
      .then((res) => {
        if (!res.data) {
          const requestOption = {
            tag: "NO_FLOOR_PLAN_SHOWED",
            metric: "Surprise",
            propertyId: propertyId,
            sessionId: appSSID,
            appSubsection: "No floor plan showed",
          };
          createEventSignal(requestOption);
        }

        store.dispatch(setSearchResult(res?.data?.search_result || false));

        resolve({
          data: res.data || {},
          extraData: {
            enableExtraOption: !_.isEmpty(desiredMoveIn) && !_.isEmpty(priceRange),
          },
          skip: skip,
        });
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const getApartmentRightInfo = (context, propertyId, encodedApiKey) => {
  let bedroomType = `${context?.bedroomPreference}`;

  if (context?.bedroomPreference && (!context?.desiredMoveInDate || !context?.priceRange)) {
    if (context?.bedroomPreference === "studio") {
      bedroomType = "studio";
    }

    const isPriceFilter = store.getState().property.isPriceFilter;

    if (isPriceFilter && !context?.priceRange) {
      if (bedroomType) {
        return new Promise((resolve, reject) => {
          getPriceBrackets(`?property_id=${propertyId}&api_key=${encodedApiKey}&bedroom_type=${bedroomType}`).then((res) => {
            if (!res.data || Object.keys(res.data).length === 0) {
              resolve({
                data: {
                  _custom_price_range: [100, 5000],
                  _form_desired_price_range: [],
                },
                skip: "bedroomType",
              });
            }

            resolve({ data: res.data, skip: "bedroomType" });
          });
        });
      }

      return new Promise((resolve) => {
        getPriceBrackets(`?api_key=${encodedApiKey}&property_id=${propertyId}`).then((res) => {
          if (!res.data || Object.keys(res.data).length === 0) {
            resolve({
              data: {
                _custom_price_range: [100, 5000],
                _form_desired_price_range: [],
              },
            });
          }

          resolve({ data: res.data });
        });
      });
    }

    if (!context?.desiredMoveInDate) {
      return new Promise((resolve, reject) => {
        getMoveInDates(`?property_id=${propertyId}&api_key=${encodedApiKey}&bedroom_type=${bedroomType}`).then((res) => {
          const options = filterRepliesByOptions(WHEN_MOVE_IN_REPLIES, res?.data);
          resolve({ data: options, skip: "getPriceRange" });
        });
      });
    }
  } else if (context?.priceRange && !context?.desiredMoveInDate) {
    return new Promise((resolve, reject) => {
      getMoveInDates(`?property_id=${propertyId}&api_key=${encodedApiKey}&bedroom_type=${bedroomType}`).then((res) => {
        const options = filterRepliesByOptions(WHEN_MOVE_IN_REPLIES, res?.data);
        resolve({ data: options, skip: "getPriceRange" });
      });
    });
  } else if (context?.desiredMoveInDate) {
    const desiredMoveIn = context?.desiredMoveInDate.replace(/^\.*/, "");
    const price = context?.priceRange;
    let priceRange = "";

    if (price) {
      priceRange = price;
    }

    let bedroomType = parseInt(context?.bedroomPreference) <= 1 ? `${context?.bedroomPreference}-bedroom` : `${context?.bedroomPreference}-bedrooms`;

    if (context?.bedroomPreference === "studio") {
      bedroomType = "studio";
    }

    return allUnitsInfo({
      propertyId: propertyId,
      encodedApiKey: encodedApiKey,
      priceRange: priceRange,
      bedroomType: bedroomType,
      desiredMoveIn: desiredMoveIn,
      skip: "DesiredMoveInDate",
    });
  }

  return Promise.resolve({
    data: [],
    skip: "noConditionMatched",
  });
};

const getRightInfoForApartment = (context, propertyId, encodedApiKey, getPriceRange, bedroomType, moveIn) => {
  const isPriceFilter = store.getState().property.isPriceFilter;

  if (isPriceFilter && !context?.priceRange && !getPriceRange) {
    const queryParams = new URLSearchParams({
      property_id: propertyId,
      api_key: encodedApiKey,
      ...(bedroomType && { bedroom_type: bedroomType }),
    });

    return new Promise((resolve, reject) => {
      getPriceBrackets(`?${queryParams}`).then((res) => {
        if (!res.data || Object.keys(res.data).length === 0) {
          resolve({
            data: {
              _custom_price_range: [100, 5000],
              _form_desired_price_range: [],
            },
          });
        }

        resolve({ data: res.data });
      });
    });
  }

  if (!context?.desiredMoveInDate && !moveIn) {
    return new Promise((resolve, reject) => {
      getMoveInDates(`?property_id=${propertyId}&api_key=${encodedApiKey}&bedroom_type=${bedroomType}`).then((res) => {
        const options = filterRepliesByOptions(WHEN_MOVE_IN_REPLIES, res?.data);
        resolve({ data: options, skip: "getPriceRange" });
      });
    });
  }

  const desiredMoveIn = context?.desiredMoveInDate || moveIn;
  const price = getPriceRange || context?.priceRange;
  let priceRange = "";

  if (price) {
    priceRange = price;
  }

  return allUnitsInfo({
    propertyId: propertyId,
    encodedApiKey: encodedApiKey,
    priceRange: priceRange,
    bedroomType: bedroomType,
    desiredMoveIn: desiredMoveIn,
    skip: "DesiredMoveInDate",
  });
};

export const updatePhoneNumberOptional = (options, isPhoneNumberRequired) => {
  return options.map((option) => {
    if (option.key === "phone") {
      return {
        ...option,
        content: {
          ...option.content,
          componentOptions: {
            ...option.content.componentOptions,
            isOptional: !isPhoneNumberRequired,
          },
        },
        isRequired: isPhoneNumberRequired ? true : false,
      };
    }

    return option;
  });
};

// function areFloorPlansValid(floorplans) {
//   if (!Array.isArray(floorplans) || floorplans.length === 0) {
//     return false;
//   }

//   for (const fp of floorplans) {
//     const hasValidUnitsCount = typeof fp.units_count === "number" && fp.units_count > 0;
//     const hasValidUnitsArray = Array.isArray(fp.units) && fp.units?.length > 0;

//     if (!hasValidUnitsCount || !hasValidUnitsArray) {
//       return false;
//     }
//   }

//   return true;
// }

const getScheduleTourFormReplies = (spfCtx, isPhoneNumberRequired) => {
  const tourAction = spfCtx?.tourAction ?? null;
  const required_info = spfCtx?.required_info ?? null;
  const need_info = spfCtx?.need_info ?? null;
  const error_code = spfCtx?.error_code ?? null;
  const booking_found = spfCtx?.booking_found ?? null;
  const tourType = spfCtx?.tourType ?? null;

  const buildContactForm = () => {
    const options = updatePhoneNumberOptional(FORM_4_CONTACT_US_REPLIES.options, isPhoneNumberRequired);

    return Promise.resolve({ ...FORM_4_CONTACT_US_REPLIES, options });
  };

  const buildScheduleTourForm = (extra = {}) =>
    scheduleTourFormInfo({ skip: "confirmation", isPhoneNumberRequired }).then((formRes) => ({
      ...formRes?.data,
      ...extra,
    }));

  if (tourAction === "cancel_inquiry") {
    if (required_info && need_info === "entryId") {
      return buildContactForm();
    }

    // Booking not found (404) — no point asking for contact info again; offer to schedule a new tour
    if (required_info && !booking_found && error_code === 404) {
      return buildScheduleTourForm();
    }

    // !required_info && booking_found → makeNextOptions uses CANCEL_TOUR_REPLIES (static)
    return Promise.resolve(null);
  }

  if (tourAction === "reschedule_inquiry") {
    if (need_info === "entryId" && required_info) {
      return buildContactForm();
    }

    if (booking_found && !required_info) {
      return buildScheduleTourForm({ isReschedule: true });
    }

    if (!booking_found && required_info && error_code === 404) {
      return buildScheduleTourForm({ isReschedule: true });
    }

    return Promise.resolve(null);
  }

  if (tourAction === "status_inquiry") {
    if (required_info && need_info === "entryId" && tourType === "") {
      return buildContactForm();
    }

    if (required_info && !booking_found && error_code === 404) {
      return buildContactForm();
    }

    // booking_found → makeNextOptions uses RESCHEDULE_TOUR_REPLIES (static)
    return Promise.resolve(null);
  }

  // cancel_confirmation / reschedule_confirmation / schedule_confirmation /
  // tour_type_inquiry / cancel_rejection → all use static constants in makeNextOptions
  if (["cancel_confirmation", "reschedule_confirmation", "schedule_confirmation", "tour_type_inquiry", "cancel_rejection"].includes(tourAction)) {
    return Promise.resolve(null);
  }

  // Default: contact form as a safe fallback for any unrecognised context
  return buildContactForm();
};

// Link as the browser should open it: trimmed, with https:// added when no http(s) scheme is given.
export const normalizeLinkUrl = (link) => {
  const trimmed = typeof link === "string" ? link.trim() : "";

  if (!trimmed) {
    return "";
  }

  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const isValidLink = (link) => {
  try {
    const { hostname } = new URL(normalizeLinkUrl(link));

    return hostname.includes(".") || hostname === "localhost";
  } catch {
    return false;
  }
};

const hasVisibleText = (text) =>
  typeof text === "string" &&
  htmlToPlainText(text)
    .replace(/&#160;/g, " ")
    .trim() !== "";

// The dashboard can save a button without a label, url or text; such a button is not shown.
const isValidCustomButton = (custom_button) => {
  if (!custom_button?.state || typeof custom_button.button_label !== "string" || !custom_button.button_label.trim()) {
    return false;
  }

  if (custom_button.action === "url") {
    return isValidLink(custom_button.url);
  }

  if (custom_button.action === "markdown") {
    return hasVisibleText(custom_button.text);
  }

  return false;
};

// Build the option tile for one custom button. "url" buttons open a link; "markdown" buttons carry
// the label as the visitor's message, so it shows on click (before the typing placeholder) like
// every other option, and the text follows as the bot's answer.
const buildCustomButtonOption = (custom_button) => {
  const isUrl = custom_button.action === "url";
  const label = custom_button.button_label.trim();

  const button = structuredClone(CUSTOM_BUTTON);
  button.content.componentOptions.label = label;
  button.content.componentOptions.id = custom_button.id;

  if (isUrl) {
    button.content.componentOptions.link = normalizeLinkUrl(custom_button.url);
  } else {
    button.content.component = "NormalOptionTile";
    button.content.componentOptions.icon = null;
    button.content.componentOptions.text = custom_button.text;
    button.nextOptionsType = "customButton";
    button.message = [{ message: label, key: "text", optionsType: "Init", speaker: "user" }];
  }

  return button;
};

// Option tiles for the active, complete custom buttons from get_management.
export const buildCustomButtonOptions = (custom_buttons) =>
  Array.isArray(custom_buttons) ? custom_buttons.filter(isValidCustomButton).map(buildCustomButtonOption) : [];

export const getNextOptions = (optionType, data, isAMA, context, available_tourTypes, customButtonId, isAsap, photos) => {
  const state = store.getState();
  const isPhoneNumberRequired = state.property.isPhoneNumberRequired;
  const propertyId = state.property.property_id || "";
  const appSSID = state.window.appSSID || "";
  const apiKey = state.property.api_key || "";
  const encodedApiKey = encodeURIComponent(apiKey);
  const customButtonData = store.getState().property.customButtons;
  /** Extra query params for getting filtered out units data */
  const bedroomType = state.params.label.bedroom_type || "";
  const desiredMoveIn = state.params.label.desired_move_in || "";
  const price = state.params.label.price || "";
  let priceRange = "";
  /** Split price into min and max value */

  if (price) {
    priceRange = price;
  }

  /** Inputted user info */
  const userEmail = state.params.label.email || "";
  const phoneNumber = state.params.label.phone_number || state.params.label.phone || "";
  const firstName = state.params.label.first_name || "";
  const lastName = state.params.label.last_name || "";
  const appointmentType = state.params.label.appointment_type || state.scheduleTour.selectedOptions.tourType || "";
  const custom_questions = state.params.label.custom_questions || null;
  const allMessages = store.getState().messages.messages || [];
  const orgId = store.getState().property.org_id || "";
  const conversationID = store.getState().window.conversationID || "";
  const enabledScheduleTour = store.getState().property.enabled_schedule_a_tour || false;

  const amaResponse = state.replies.amaResponse || null;
  const isMultiLanguageEnabled = state.property.isMultiLanguage || false;
  const isSpanishCurrentLanguage = state.property.isSpanishLanguage || false;
  const bookingId = localStorage.getItem(`${propertyId}-bookingId`) || "";
  const usersFaqMsg = localStorage.getItem(`${propertyId}-userFaq-msg`) || "";
  const storedContext = localStorage.getItem(`${propertyId}-context`);
  const entryId = localStorage.getItem(`${propertyId}-entry_id`) || "";
  const localStorageContext = JSON.parse(storedContext) || [];

  const { tourType: selectedTourType, time: selectedTourTime } = state.scheduleTour.selectedOptions;

  const checkCustomQues = custom_questions ? { custom_questions } : {};
  const urlParams = new URLSearchParams(window.location.search);
  const isJAI = urlParams.get("isjai");

  if (context?.length > 0) {
    localStorage.setItem(`${propertyId}-context`, JSON.stringify(context));
  }

  switch (optionType) {
    case "BedroomTypes": {
      if (context?.length > 0 && context[0]?.bedroomPreference) {
        return getApartmentRightInfo(context[0], propertyId, encodedApiKey, price);
      }

      return getBedroomTypes(`?api_key=${encodedApiKey}&property_id=${propertyId}`);
    }
    case "PriceBrackets": {
      const apartment_context = localStorage.getItem(`${propertyId}-context`);

      if (!!apartment_context) {
        const appContext = JSON.parse(apartment_context);

        if (appContext?.length > 0) {
          return getRightInfoForApartment(appContext[0], propertyId, encodedApiKey, priceRange, bedroomType, desiredMoveIn);
        }
      }

      if (bedroomType) {
        return new Promise((resolve) => {
          getPriceBrackets(`?property_id=${propertyId}&api_key=${encodedApiKey}&bedroom_type=${bedroomType}`).then((res) => {
            if (!res.data || Object.keys(res.data).length === 0) {
              resolve({
                data: {
                  _custom_price_range: [100, 5000],
                  _form_desired_price_range: [],
                },
              });
            }

            resolve({ data: res.data });
          });
        });
      }

      return new Promise((resolve) => {
        getPriceBrackets(`?api_key=${encodedApiKey}&property_id=${propertyId}`).then((res) => {
          if (!res.data || Object.keys(res.data).length === 0) {
            resolve({
              data: {
                _custom_price_range: [100, 5000],
                _form_desired_price_range: [],
              },
            });
          }

          resolve({ data: res.data });
        });
      });
    }

    case "noLangChange": {
      store.dispatch(setReplies(INITIAL_REPLIES));

      setTimeout(() => {
        // Runs in a detached setTimeout (no promise chain), so a throw here would be an
        // unhandled error with no telemetry — report it and surface ERROR_MSG inline.
        try {
          handleAMAResponse(amaResponse, true);
        } catch (err) {
          reportHandledError(err, { area: "conversation_flow", kind: "ama_response" });
          store.dispatch(removeAndAddMessages([{ message: ERROR_MSG, speaker: "bot", subject: "", createdAt: new Date() }]));
          store.dispatch(setIsDisabled(false));
        }
      }, 600);

      return Promise.resolve({ skipHandleSuccess: true });
    }

    case "yesLangChange": {
      store.dispatch(setReplies(INITIAL_REPLIES));

      const history = getLastConversations(allMessages, 5)
        ?.map((i) => ({
          role: i.speaker === "bot" || i.speaker === "faqBot" ? "assistant" : "user",
          content: i.message,
        }))
        .filter((i) => i.content && i.content.trim() !== "");

      const faqReply = {
        history,
        propertyId,
        orgId: orgId,
        booking_id: bookingId,
        // Directive appended to the ML prompt, not UI copy. Built here — before changeLanguage("sp")
        // below — so t() resolves against the still-active "en" bundle and the model keeps receiving
        // the English instruction. Externalised as a key so the wording is tunable from nimda.
        message: usersFaqMsg + " " + t("replyInSpanish"),
        conversation_id: conversationID,
        session_id: appSSID || DEFAULT_SESSION_ID,
        graphql_api_key: encodedApiKey,
        tourScheduleEnabled: enabledScheduleTour,
        spanish_support_enabled: !!isMultiLanguageEnabled,
        spanish_toggle: true,
      };

      const langChangeAndApiCall = new Promise((resolve, reject) => {
        // Visitor-driven, exactly like the header toggle: freeze the welcome bubble they have
        // already read instead of retranslating it under them.
        store.dispatch(freezeGreeting());

        changeLanguage("sp")
          .then(() => {
            store.dispatch(setIsSpanishLanguage(true));
            store.dispatch(addNewMessage([{ message: "supportSpanishToo", speaker: "bot", createdAt: new Date().toISOString() }]));

            // Persist language preference to localStorage
            try {
              localStorage.setItem(`${propertyId}-hayley_language_preference`, "sp");
            } catch (error) {
              console.error("Failed to save language preference to localStorage:", error);
            }

            // Update language preference on backend
            // updateLanguage(propertyId, apiKey, true);

            // Typing must be the LAST message when handleAMAResponse runs — its removeAndAddMessages strips
            // the last entry. Dispatching it after supportSpanishToo preserves that invariant.
            store.dispatch(addNewMessage([{ message: "typing", speaker: "bot", createdAt: new Date().toISOString() }]));

            // Add flag to identify chat-adapter response
            return getFAQsReply(faqReply).then((res) => ({ ...res, isChatAdapterResponse: true }));
          })
          .then((res) => {
            // Execute handleAMAResponse with the API response.
            try {
              handleAMAResponse(res, true);
            } catch (err) {
              // 200 response but the client-side handler threw — off the API path, so no
              // chokepoint sees it. Report, then rethrow into the .catch for the ERROR_MSG UX.
              reportHandledError(err, { area: "conversation_flow", kind: "ama_response" });
              throw err;
            }

            resolve({ skipHandleSuccess: true }); // Resolve the main promise
          })
          .catch((err) => {
            if (!isManualCancellation(err)) {
              store.dispatch(removeAndAddMessages([{ message: ERROR_MSG, speaker: "bot", subject: "", createdAt: new Date() }]));
              store.dispatch(setIsDisabled(false));
            }

            reject(err);
          });
      });

      return langChangeAndApiCall;
    }

    case "cancelTour":
    case "noDoNotCancel": {
      const history = getLastConversations(allMessages, 5)
        ?.map((i) => ({
          role: i.speaker == "bot" || i.speaker == "faqBot" ? "assistant" : "user",
          content: i.message,
        }))
        .filter((i) => i.content && i.content.trim() !== "");

      const faqReply = {
        history,
        propertyId,
        orgId: orgId,
        booking_id: bookingId,
        message: t(TOUR_MSGS_KEY[optionType]),
        conversation_id: conversationID,
        session_id: appSSID || DEFAULT_SESSION_ID,
        graphql_api_key: encodedApiKey,
        tourScheduleEnabled: enabledScheduleTour,
        entry_id: String(entryId ?? ""),
        spanish_support_enabled: !!isMultiLanguageEnabled,
        spanish_toggle: !!isSpanishCurrentLanguage,
      };

      return getFAQsReply(faqReply).then((res) => {
        const spfCtx = res?.data?.context?.spf;
        const subIntent = res?.data?.sub_intent;

        if (spfCtx && subIntent !== "end_of_chat") {
          localStorage.setItem(`${propertyId}-context`, JSON.stringify([spfCtx]));
        }

        // Skip form computation for end_of_chat — makeNextOptions uses INITIAL_REPLIES (static)
        if (subIntent === "end_of_chat") {
          return { ...res, isChatAdapterResponse: true };
        }

        return getScheduleTourFormReplies(spfCtx, isPhoneNumberRequired).then((formReplies) => ({
          ...res,
          isChatAdapterResponse: true,
          ...(formReplies && { formReplies }),
        }));
      });
    }
    case "WhenMoveIn": {
      const apartmentContextM = localStorage.getItem(`${propertyId}-context`);

      if (!!apartmentContextM) {
        const appContext = JSON.parse(apartmentContextM);

        if (appContext?.length > 0 && appContext[0]?.desiredMoveInDate) {
          localStorage.removeItem(`${propertyId}-context`);

          return getRightInfoForApartment(appContext[0], propertyId, encodedApiKey, priceRange, bedroomType);
        }
      }

      return new Promise((resolve, reject) => {
        getMoveInDates(`?property_id=${propertyId}&api_key=${encodedApiKey}&bedroom_type=${bedroomType}`)
          .then((res) => {
            const options = filterRepliesByOptions(WHEN_MOVE_IN_REPLIES, res?.data);
            resolve({ data: options });
          })
          .catch((err) => {
            reject(err);
          });
      });
    }
    case "Units":
      return new Promise((resolve, reject) => {
        store.dispatch(
          setUnitFilters({
            bedrooms: bedroomType,
            budgets: priceRange,
            moveIns: desiredMoveIn || "any",
          })
        );

        const length = priceRange?.length || 0;

        const min = length === 0 ? null : length > 1 ? priceRange[0] : 0;
        const max = length === 0 ? null : length > 1 ? priceRange[1] : priceRange[0];

        let convertedDate = desiredMoveIn;
        let alreadyDots = false;

        if (moment(desiredMoveIn, "MMM DD, YYYY", true).isValid()) {
          convertedDate = moment(desiredMoveIn, "MMM DD, YYYY").format("YYYY-MM-DD");
        }

        if (desiredMoveIn?.includes("..") || convertedDate?.includes("..")) {
          alreadyDots = true;
        }

        const queryParams = `?property_id=${propertyId}&api_key=${encodedApiKey}${bedroomType ? `&bedroom_type=${bedroomType}` : ""}${
          desiredMoveIn ? `&desired_move_in=${!alreadyDots ? "..." + convertedDate || desiredMoveIn : convertedDate || desiredMoveIn}` : ""
        }${min ? `&min_price=${min}` : ""}${max ? `&max_price=${max}` : ""}`;

        updateApartmentContext(propertyId, {
          desiredMoveIn: convertedDate || desiredMoveIn,
          bedroomType: bedroomType,
          priceRange: priceRange,
        });

        store.dispatch(setShowAllUnit(false));

        getUnits(queryParams)
          .then((res) => {
            if (!res.data) {
              const requestOption = {
                tag: "NO_FLOOR_PLAN_SHOWED",
                metric: "Surprise",
                propertyId: propertyId,
                sessionId: appSSID,
                appSubsection: "No floor plan showed",
              };
              createEventSignal(requestOption);
            }

            store.dispatch(setSearchResult(res?.data?.search_result || false));

            resolve({
              data: res.data || {},
              extraData: {
                enableExtraOption: !_.isEmpty(desiredMoveIn) && !_.isEmpty(price),
              },
            });
          })
          .catch((err) => {
            reject(err);
          });
      });
    case "UnitsByRoom":
      return new Promise((resolve, reject) => {
        if (_.isEmpty(bedroomType)) {
          resolve({
            data: [],
            extraData: {
              enableExtraOption: !_.isEmpty(desiredMoveIn) && !_.isEmpty(price),
            },
          });
        }

        // const queryParams = `?property_id=${propertyId}&api_key=${encodedApiKey}&bedroom_type=${bedroomType}`;
        const queryParamsObj = new URLSearchParams({
          property_id: propertyId,
          api_key: encodedApiKey,
        });

        // Ensure bedroomType is always treated as an array
        const bedroomTypeArray = Array.isArray(bedroomType) ? bedroomType : [bedroomType];

        // Append each bedroom type as array-style query param
        bedroomTypeArray.forEach((type) => {
          queryParamsObj.append("bedroom_type[]", type);
        });

        const queryParams = queryParamsObj.toString();
        store.dispatch(setShowAllUnit(true));

        updateApartmentContext(propertyId, {
          bedroomType: bedroomType,
        });

        getUnits(queryParams)
          .then((res) => {
            if (!res.data) {
              const requestOption = {
                tag: "NO_FLOOR_PLAN_SHOWED",
                metric: "Surprise",
                propertyId: propertyId,
                sessionId: appSSID,
                appSubsection: "No floor plan showed",
              };
              createEventSignal(requestOption);
            }

            store.dispatch(setSearchResult(res?.data?.search_result || false));

            resolve({
              data: res.data || {},
              extraData: {
                enableExtraOption: !_.isEmpty(desiredMoveIn) && !_.isEmpty(price),
              },
            });
          })
          .catch((err) => {
            reject(err);
          });
      });
    case "UnitsAll":
      store.dispatch(setShowAllUnit(true));
      store.dispatch(setUnitFilters(null));

      return getUnits(`?api_key=${encodedApiKey}&property_id=${propertyId}`);
    case "rescheduleFromTourStatus":
    case "ScheduleTour":
      if (!state.property.enabled_schedule_a_tour) {
        return new Promise((resolve, reject) => {
          const updatedContactUsOptions = updatePhoneNumberOptional(FORM_4_SCHEDULE_TOUR_REPLIES.options, isPhoneNumberRequired);

          const updatedContactUsReplies = {
            ...FORM_4_SCHEDULE_TOUR_REPLIES,
            options: updatedContactUsOptions,
          };
          resolve({ data: updatedContactUsReplies });
        });
      }

      if (context?.length > 0) {
        localStorage.setItem(`${propertyId}-context`, JSON.stringify(context));
      }

      if (context?.length > 0 && context[0]?.tourType) {
        return scheduleTourFormInfo({ skip: "confirmation", isPhoneNumberRequired });
      }

      return new Promise((resolve, reject) => {
        getSelfTourLink(`?api_key=${encodedApiKey}&property_id=${propertyId}`)
          .then((res) => {
            const self_tour_link = res.data;
            store.dispatch(setSelfTourLink(self_tour_link));
            const updatedContactUsOptions = updatePhoneNumberOptional(FORM_4_SCHEDULE_TOUR_REPLIES.options, isPhoneNumberRequired);

            const updatedContactUsReplies = {
              ...FORM_4_SCHEDULE_TOUR_REPLIES,
              options: updatedContactUsOptions,
            };
            resolve({ data: updatedContactUsReplies });
          })
          .catch((err) => {
            reject(err);
          });
      });
    case "Form4ScheduleTour":
      // Guided entry: the user stated nothing, so a tour request made earlier in chat must not
      // steer this form's tour type, date or time.
      store.dispatch(setTourPreference({}));
      // Same for the stored chat context: left behind, it would route this guided form's
      // Submit through the chat adapter instead of tour_confirmation.
      localStorage.removeItem(`${propertyId}-context`);

      return scheduleTourFormInfo({ skip: "", isPhoneNumberRequired: isPhoneNumberRequired });
    case "Form4UpdateSelections":
      return Promise.resolve({ data: FORM_4_UPDATE_SELECTIONS_REPLIES });

    case "PostUpdateSelections": {
      const isPriceFilterEnabled = store.getState().property.isPriceFilter;
      const selections = data || {};

      const updatePayload = buildUpdatePreferencesPayload({
        propertyId,
        entryId,
        sessionId: appSSID || DEFAULT_SESSION_ID,
        bedroomType: selections.bedroom_type || "",
        desiredMoveIn: selections.desired_move_in || "",
        price: isPriceFilterEnabled ? selections.price || "" : "",
      });

      return updatePreferences(updatePayload)
        .then((res) => {
          if (res?.data?.success) {
            return {
              data: POST_UPDATE_SELECTIONS_REPLIES,
              extraData: { botMessages: ["preferenceUpdatedSuccess"] },
            };
          }

          return {
            data: POST_UPDATE_SELECTIONS_REPLIES,
            extraData: { botMessages: ["preferenceUpdateFailed"] },
          };
        })
        .catch(() => ({
          data: POST_UPDATE_SELECTIONS_REPLIES,
          extraData: { botMessages: ["preferenceUpdateFailed"] },
        }));
    }
    case "LikeTour":
    case "DisLikeTour":
    case "SkipTour": {
      const data = filterFaqOptions(POST_SCHEDULE_TOUR_REPLIES);

      return new Promise((resolve, reject) => {
        resolve({ data, extraData: { botMessages: ["whatNext"] } });
      });
    }
    case "LikeContact":
    case "DisLikeContact":
    case "SkipContact": {
      const msg = ["whatNext"];
      const data = filterFaqOptions(POST_CONTACT_US_REPLIES);

      return new Promise((resolve, reject) => {
        resolve({ data, extraData: { botMessages: msg } });
      });
    }
    case "PostScheduleTour": {
      const { showFeedback } = store.getState().property;
      const urlParams = new URLSearchParams(window.location.search);
      const isJAI = urlParams.get("isjai");
      const isChecked = state.params?.checked;
      const appSSID = state.window.appSSID || null;
      const sequence = JSON.parse(localStorage.getItem(`events_${appSSID}`));

      const metadata = {
        sequence: sequence,
      };
      const jsonString = JSON.stringify(metadata);

      return new Promise((resolve, reject) => {
        const payload = {
          property_id: propertyId,
          api_key: apiKey,
          sessionId: appSSID,
          smsOptIn: isChecked,
          metaData: `${jsonString.replace(/"/g, '\\"')}`,
          originator: "chat",
        };

        const payload4Contact = {
          ...checkCustomQues,
          property_id: propertyId,
          api_key: apiKey,
          status: "Active",
          sub_status: "Scheduled",
          sessionId: appSSID,
          flow: "schedule_tour",
          property_website: !isJAI,
          smsOptIn: isChecked,
        };

        const botMessages = [];

        if (!_.isEmpty(bedroomType)) {
          payload.desiredBedrooms = bedroomType;
        }

        if (!_.isEmpty(desiredMoveIn)) {
          payload.desiredMoveIn = desiredMoveIn;
        }

        if (priceRange.length > 0 && priceRange[0] !== "") {
          payload.desiredPriceLow = priceRange[0];
          payload.desiredPriceHigh = priceRange[1] ? priceRange[1] : priceRange[0];
        }

        if (!_.isEmpty(userEmail)) {
          payload.email = userEmail;
          payload4Contact.email = userEmail;
        }

        if (!_.isEmpty(phoneNumber)) {
          payload.telephone = phoneNumber;
          payload4Contact.phone_number = phoneNumber;

          if (store.getState().property.showContactSMSOpt) {
            payload4Contact.smsOptIn = isChecked;
          }
        }

        if (!_.isEmpty(firstName)) {
          payload.firstName = firstName;
          payload4Contact.first_name = firstName;
        }

        if (!_.isEmpty(lastName)) {
          payload.lastName = lastName;
          payload4Contact.last_name = lastName;
        }

        if (!_.isEmpty(selectedTourTime)) {
          const date = moment(selectedTourTime, "YYYY-MM-DD hh:mm:ss A");

          const formattedDate = date.format("YYYY-MM-DD");
          const formattedTime = date.format("hh:mm:ss A");

          payload.date = formattedDate;
          payload.time = formattedTime;
        }

        if (!_.isEmpty(selectedTourType)) {
          payload.appointmentType = selectedTourType;
        }

        const currentTourAction = localStorageContext?.[0]?.tourAction;
        const currentBooking = localStorageContext?.[0]?.booking_found;
        const currentRequiredInfo = localStorageContext?.[0]?.required_info;

        const isRescheduleInquiry =
          currentBooking && !currentRequiredInfo && (currentTourAction === "reschedule_inquiry" || currentTourAction === "status_inquiry");
        // const isScheduleInquiry = currentTourAction === "schedule_inquiry";
        const isScheduleInquiry = SCHEDULE_TOUR_INQUIRIES.includes(currentTourAction);

        if (isScheduleInquiry || isRescheduleInquiry) {
          store.dispatch(setIsDisabled(true));

          const history = getLastConversations(allMessages, 5)
            ?.map((i) => ({
              role: i.speaker === "bot" || i.speaker === "faqBot" ? "assistant" : "user",
              content: i.message,
            }))
            .filter((i) => i.content && i.content.trim() !== "");

          const faqReply = {
            history,
            propertyId,
            orgId,
            booking_id: bookingId,
            message: isRescheduleInquiry ? "rescheduleTour" : "Schedule a Tour",
            conversation_id: conversationID,
            session_id: appSSID || DEFAULT_SESSION_ID,
            graphql_api_key: encodedApiKey,
            tourScheduleEnabled: enabledScheduleTour,
            entry_id: String(entryId ?? ""),
            spanish_support_enabled: !!isMultiLanguageEnabled,
            spanish_toggle: !!isSpanishCurrentLanguage,
          };

          if (isRescheduleInquiry) {
            faqReply["reschedule_information"] = {
              email: payload?.email,
              phoneNumber: payload?.telephone,
              newPreferredDate: payload?.date,
              newPreferredTime: payload?.time,
              tourType: payload?.appointmentType,
              bookingId: bookingId,
            };
          } else if (isScheduleInquiry) {
            faqReply["schedule_information"] = { ...payload };
          }

          return getFAQsReply(faqReply)
            .then(async (faqResponse) => {
              // Trust bookingFailureCause over tourAction: the reply can say "rescheduled" even when
              // the booking failed. Context and booking id are kept so a retry works.
              if (getAmaBookingFailureCause(faqResponse)) {
                let freshSlots = [];

                try {
                  const slotsURL = `?property_id=${propertyId}&api_key=${encodedApiKey}&tour_type=${payload.appointmentType || ""}`;
                  const { data: slotsData } = await getAvailableDates(slotsURL);
                  freshSlots = Array.isArray(slotsData) ? slotsData : [];
                } catch (slotsError) {
                  console.error("Error fetching slots after an unavailable AMA tour slot:", slotsError);
                }

                resolve(
                  recoverFromUnavailableSlot({
                    availableSlots: freshSlots,
                    payload,
                    isPhoneNumberRequired,
                    // The retry form is still chat-adapter driven, so its Cancel keeps telling the adapter.
                    extraReplies: { triggerMLOnCancel: true, ...(isRescheduleInquiry ? { isReschedule: true } : {}) },
                  })
                );

                return;
              }

              // Check retrieval_type to safely access spf data
              const isSpfRetrieval = faqResponse?.data?.retrieval_type === "spf";
              const availableSlots = isSpfRetrieval ? faqResponse?.data?.context?.spf_query_response || {} : {};
              const context = isSpfRetrieval ? faqResponse?.data?.context?.spf || {} : {};

              if (availableSlots && typeof availableSlots === "object" && !Array.isArray(availableSlots)) {
                const entryID = availableSlots?.booking?.entry_id;
                const email = availableSlots?.booking?.contact?.email;
                const bookingId = availableSlots?.booking?.id;

                // Don't wipe the saved booking id when the reply has no booking.
                if (bookingId) {
                  localStorage.setItem(`${propertyId}-bookingId`, bookingId);
                }

                if (entryID && entryId !== entryID) {
                  const currentEntryId = localStorage.getItem(`${propertyId}-entry_id`) || store?.getState()?.property?.entry_id || "";
                  const backendEntryId = entryID;

                  if (!currentEntryId || currentEntryId !== backendEntryId) {
                    setFirstEntryFlag();
                  }

                  updateConversationEntryId(entryID, email, {
                    firstName: availableSlots?.booking?.contact?.firstName || "",
                    lastName: availableSlots?.booking?.contact?.lastName || "",
                    phoneNumber: availableSlots?.booking?.contact?.telephone || "",
                  });
                }
              } else if (context && context.entryId) {
                const currentEntryId = localStorage.getItem(`${propertyId}-entry_id`) || store?.getState()?.property?.entry_id || "";
                const backendEntryId = context.entryId;

                if (!currentEntryId || currentEntryId !== backendEntryId) {
                  setFirstEntryFlag();
                }
              }

              // Add flag to identify chat-adapter response
              resolve({ data: faqResponse?.data, isChatAdapterResponse: true });
            })
            .catch((err) => {
              if (!isManualCancellation(err)) {
                console.error("FAQ Reply Error:", err);
                store.dispatch(setIsDisabled(false));
              }

              reject(err);
            });
        }

        scheduleTour(payload)
          .then(async (res) => {
            // NEW CASE: Handle when API returns only available slots array (expired slot scenario)
            if (Array.isArray(res.data) && res.data.length > 0 && !res.data.booking) {
              resolve(recoverFromUnavailableSlot({ availableSlots: res.data, payload, isPhoneNumberRequired }));
            } else if (res.data.bookingFailureCause === "SLOT_UNAVAILABLE") {
              // Clear tour data so user can select a fresh available slot
              store.dispatch(resetScheduleTourState());
              store.dispatch(setShouldFetchSlots(true));

              resolve({
                data: {
                  ...FEEDBACK_TOUR_REPLIES,
                  title: "oopsSelectSlotNoAvail",
                },
                extraData: { botMessages: ["sorrySelectSlotNoAvail"] },
              });
            } else if (!res.data.bookingFailureCause || res.data.bookingFailureCause === "NONE") {
              localStorage.setItem(`${propertyId}-bookingId`, res.data.booking?.id ?? "");

              payload4Contact.entry_id = res.data.booking.contact.id;

              if (res.data.booking.contact.id) {
                const currentEntryId = localStorage.getItem(`${propertyId}-entry_id`) || store?.getState()?.property?.entry_id || "";
                const backendEntryId = res.data.booking.contact.id;

                if (!currentEntryId || currentEntryId !== backendEntryId) {
                  setFirstEntryFlag();
                }

                createItemRecord(propertyId, appSSID, "scheduled tour", res.data.booking.contact.id);

                updateConversationEntryId(res.data.booking.contact.id, res.data.booking.contact.email, {
                  firstName: res.data.booking.contact.firstName || "",
                  lastName: res.data.booking.contact.lastName || "",
                  phoneNumber: res.data.booking.contact.telephone || "",
                });
              }

              await createNewContact(payload4Contact);

              try {
                const encodedApiKey = encodeURIComponent(apiKey);
                const tourTypeForSlots = selectedTourType || appointmentType;
                const slotsURL = `?property_id=${propertyId}&api_key=${encodedApiKey}&tour_type=${tourTypeForSlots}`;
                const { data: updatedSlots } = await getAvailableDates(slotsURL);

                if (updatedSlots?.length) {
                  handleTourResponse(updatedSlots, true, tourTypeForSlots);
                }
              } catch (slotsError) {
                console.error("Error fetching updated slots after tour scheduling:", slotsError);
              }

              const fullName = `<span class="hayley-font-semibold">${res.data.booking.contact.first_name || ""} ${
                res.data.booking.contact.last_name || ""
              }</span>`;
              const date = `<span class="hayley-font-semibold">${moment(res.data.booking.slot.datetime, "YYYY-MM-DD hh:mm A").format("ddd, MMM Do")}</span>`;
              const time = `<span class="hayley-font-semibold">${moment(res.data.booking.slot.datetime, "YYYY-MM-DD hh:mm A").format("hh:mm a")}</span>`;
              const email = `<span class="hayley-font-semibold">${res.data.booking.contact.email}</span>`;

              if (res.data.already_scheduled) {
                const tourType = formatAppointmentType(res.data.booking.appointmentType);
                botMessages.push(t("alreadyBookedMessage", { tourType, date, time, email }));
                botMessages.push(t("howHelp"));
              } else {
                if (appointmentType === "in_person_tour") {
                  botMessages.push(t("bookedInPersonMessage", { fullName, date, time, email }));
                } else if (appointmentType === "live_video_tour" && res.data.live_tour_link) {
                  botMessages.push(t("bookedLiveVideoMessage", { fullName, date, time, email }));
                } else if (appointmentType === "self_guide_tour" || appointmentType === "self_guided_tour") {
                  botMessages.push(t("bookedSelfGuideMessage", { fullName, date, time, email }));
                }

                if (showFeedback) {
                  botMessages.push(t("howExp"));
                }
              }

              const data = filterFaqOptions(POST_SCHEDULE_TOUR_REPLIES);

              // Clear tour data after successful submission so next time form is opened, it fetches fresh data
              store.dispatch(resetScheduleTourState());
              store.dispatch(setShouldFetchSlots(true));

              resolve({
                data: res.data.already_scheduled ? INITIAL_REPLIES : showFeedback ? FEEDBACK_TOUR_REPLIES : data,
                extraData: { botMessages },
                entryId: res.data.booking.contact.id,
              });
            }
          })
          .catch((err) => reject(err));
      });
    }
    case "PostScheduleTourFromFaq": {
      const history = getLastConversations(allMessages, 5)
        ?.map((i) => ({
          role: i.speaker == "bot" || i.speaker == "faqBot" ? "assistant" : "user",
          content: i.message,
        }))
        .filter((i) => i.content && i.content.trim() !== "");
      // const booking = { booking: { id: bookingId } };

      //making payload for tour details
      const isChecked = state.params?.checked;
      const appSSID = state.window.appSSID || null;
      const tourAction = state.scheduleTour.tourAction || "";
      const sequence = JSON.parse(localStorage.getItem(`events_${appSSID}`));

      const metadata = {
        sequence: sequence,
      };
      const jsonString = JSON.stringify(metadata);

      const payload = {
        property_id: propertyId,
        api_key: apiKey,
        sessionId: appSSID,
        smsOptIn: isChecked,
        metaData: `${jsonString.replace(/"/g, '\\"')}`,
        originator: "chat",
      };

      if (!_.isEmpty(bedroomType)) {
        payload.desiredBedrooms = bedroomType;
      }

      if (!_.isEmpty(desiredMoveIn)) {
        payload.desiredMoveIn = desiredMoveIn;
      }

      if (priceRange.length > 0 && priceRange[0] !== "") {
        payload.desiredPriceLow = priceRange[0];
        payload.desiredPriceHigh = priceRange[1] ? priceRange[1] : priceRange[0];
      }

      if (!_.isEmpty(userEmail)) {
        payload.email = userEmail;
      }

      if (!_.isEmpty(phoneNumber)) {
        payload.telephone = phoneNumber;
      }

      if (!_.isEmpty(firstName)) {
        payload.firstName = firstName;
      }

      if (!_.isEmpty(lastName)) {
        payload.lastName = lastName;
      }

      if (!_.isEmpty(selectedTourTime)) {
        const date = moment(selectedTourTime, "YYYY-MM-DD hh:mm:ss A");

        const formattedDate = date.format("YYYY-MM-DD");
        const formattedTime = date.format("hh:mm:ss A");

        payload.date = formattedDate;
        payload.time = formattedTime;
      }

      if (!_.isEmpty(selectedTourType)) {
        payload.appointmentType = selectedTourType;
      }

      const faqReply = {
        history,
        propertyId,
        orgId: orgId,
        booking_id: bookingId,
        message: tourAction + " " + JSON.stringify(payload) || "",
        conversation_id: conversationID,
        session_id: appSSID || DEFAULT_SESSION_ID,
        graphql_api_key: encodedApiKey,
        tourScheduleEnabled: enabledScheduleTour,
        spanish_support_enabled: !!isMultiLanguageEnabled,
        spanish_toggle: !!isSpanishCurrentLanguage,
      };

      // Add flag to identify chat-adapter response
      return getFAQsReply(faqReply).then((res) => ({ ...res, isChatAdapterResponse: true }));
    }

    case "SelfTour":
      return new Promise((resolve, reject) => {
        const { self_tour_link } = store.getState().property;

        resolve({ data: SELF_TOUR_REPLIES, extraData: self_tour_link });
      });
    case "Form4ContactUs":
      // Guided tile only (no context): drop a stale chat tour context. The AMA entryId route
      // arrives with a context that was just stored above and is read again on submit.
      if (!context?.length) {
        localStorage.removeItem(`${propertyId}-context`);
      }

      return new Promise((resolve, reject) => {
        const updatedContactUsOptions = updatePhoneNumberOptional(FORM_4_CONTACT_US_REPLIES.options, isPhoneNumberRequired);

        const updatedContactUsReplies = {
          ...FORM_4_CONTACT_US_REPLIES,
          options: updatedContactUsOptions,
        };
        resolve({ data: updatedContactUsReplies });
      });
    case "PostContactUs": {
      const { showFeedback } = store.getState().property;
      const isChecked = state.params?.checked;
      let parsedAgentHandoff = {};
      const storedAgentHandoff = localStorage.getItem(`${propertyId}-agentHandOff`);
      parsedAgentHandoff = storedAgentHandoff ? JSON.parse(storedAgentHandoff) : {};

      return new Promise((resolve, reject) => {
        const payload = {
          ...checkCustomQues,
          property_id: propertyId,
          api_key: apiKey,
          status: "Active",
          sub_status: "New",
          sessionId: appSSID,
          flow: "contact_us",
          smsOptIn: isChecked,
          property_website: !isJAI,
        };

        const email = `<span class="hayley-font-semibold">${userEmail}</span>`;

        let queryParam = `?api_key=${encodedApiKey}&property_id=${propertyId}`;
        const botMessage = t("contactSubmitMsg", { email });

        const feedBackMsg = showFeedback ? [botMessage, t("howExp")] : [botMessage];

        if (!_.isEmpty(userEmail)) {
          payload.email = userEmail;
          queryParam += `&email=${encodeURIComponent(userEmail)}`;
        }

        if (!_.isEmpty(phoneNumber)) {
          payload.phone_number = phoneNumber;

          if (store.getState().property.showContactSMSOpt) {
            payload.smsOptIn = isChecked;
          }

          queryParam += `&telephone=${phoneNumber}`;
        }

        if (!_.isEmpty(firstName)) {
          payload.first_name = firstName;
        }

        if (!_.isEmpty(lastName)) {
          payload.last_name = lastName;
        }

        const data = filterFaqOptions(POST_CONTACT_US_REPLIES);

        const obj = {
          data: showFeedback ? FEEDBACK_CONTACT_REPLIES : data,
          extraData: { botMessages: feedBackMsg },
        };

        findUserEntry(queryParam)
          .then((res) => {
            payload.entry_id = res.data.id;

            // const updatedApiKey = store.getState().property.api_key || "";
            // payload.api_key = updatedApiKey;

            createNewContact(payload)
              .then((res) => {
                createItemRecord(propertyId, appSSID, "guest card", res?.data?.id);

                if (res?.data?.id) {
                  const currentEntryId = localStorage.getItem(`${propertyId}-entry_id`) || store?.getState()?.property?.entry_id || "";
                  const backendEntryId = res.data.id;

                  if (!currentEntryId || currentEntryId !== backendEntryId) {
                    setFirstEntryFlag();
                  }

                  updateConversationEntryId(res.data.id, payload.email, {
                    firstName: payload.first_name || "",
                    lastName: payload.last_name || "",
                    phoneNumber: payload.phone_number || "",
                  });

                  if (
                    localStorageContext?.[0]?.need_info === "entryId" ||
                    (parsedAgentHandoff?.need_info === "entry_id" && parsedAgentHandoff?.agent_handoff_triggered)
                  ) {
                    store.dispatch(setIsDisabled(true));

                    const history = getLastConversations(allMessages, 5)
                      ?.map((i) => ({
                        role: i.speaker === "bot" || i.speaker === "faqBot" ? "assistant" : "user",
                        content: i.message,
                      }))
                      .filter((i) => i.content && i.content.trim() !== "");

                    // const booking = { booking: { id: bookingId } };

                    const faqReply = {
                      history,
                      propertyId,
                      orgId,
                      booking_id: bookingId,
                      message: usersFaqMsg || "",
                      conversation_id: conversationID,
                      session_id: appSSID || DEFAULT_SESSION_ID,
                      graphql_api_key: encodedApiKey,
                      tourScheduleEnabled: enabledScheduleTour,
                      entry_id: String(res.data?.id ?? ""),
                      spanish_support_enabled: !!isMultiLanguageEnabled,
                      spanish_toggle: !!isSpanishCurrentLanguage,
                    };

                    return getFAQsReply(faqReply)
                      .then((faqResponse) => {
                        // Add flag to identify chat-adapter response
                        resolve({ data: faqResponse?.data, isChatAdapterResponse: true });
                      })
                      .catch((err) => {
                        if (!isManualCancellation(err)) {
                          console.error("FAQ Reply Error:", err);
                          store.dispatch(setIsDisabled(false));
                        }

                        reject(err);
                      });
                  }
                }

                // fallback for normal case
                resolve(obj);
              })
              .catch((err) => reject(err));
          })
          .catch((err) => {
            if (err.response?.data.error === "Contact Not Found") {
              // const updatedApiKey = store.getState().property.api_key || "";
              // payload.api_key = updatedApiKey;

              createNewContact(payload)
                .then((res) => {
                  createItemRecord(propertyId, appSSID, "guest card", res?.data?.id);

                  if (res?.data?.id) {
                    const currentEntryId = localStorage.getItem(`${propertyId}-entry_id`) || store?.getState()?.property?.entry_id || "";
                    const backendEntryId = res.data.id;

                    if (!currentEntryId || currentEntryId !== backendEntryId) {
                      setFirstEntryFlag();
                    }

                    updateConversationEntryId(res.data.id, payload.email, {
                      firstName: payload.first_name || "",
                      lastName: payload.last_name || "",
                      phoneNumber: payload.phone_number || "",
                    });

                    if (
                      localStorageContext?.[0]?.need_info === "entryId" ||
                      (parsedAgentHandoff?.need_info === "entry_id" && parsedAgentHandoff?.agent_handoff_triggered)
                    ) {
                      store.dispatch(setIsDisabled(true));

                      const history = getLastConversations(allMessages, 5)
                        ?.map((i) => ({
                          role: i.speaker === "bot" || i.speaker === "faqBot" ? "assistant" : "user",
                          content: i.message,
                        }))
                        .filter((i) => i.content && i.content.trim() !== "");

                      // const booking = { booking: { id: bookingId } };

                      const faqReply = {
                        history,
                        propertyId,
                        orgId,
                        booking_id: bookingId,
                        message: usersFaqMsg || "",
                        conversation_id: conversationID,
                        session_id: appSSID || DEFAULT_SESSION_ID,
                        graphql_api_key: encodedApiKey,
                        tourScheduleEnabled: enabledScheduleTour,
                        entry_id: String(res.data?.id ?? ""),
                        spanish_support_enabled: !!isMultiLanguageEnabled,
                        spanish_toggle: !!isSpanishCurrentLanguage,
                      };

                      return getFAQsReply(faqReply)
                        .then((faqResponse) => {
                          // Add flag to identify chat-adapter response
                          resolve({ data: faqResponse?.data, isChatAdapterResponse: true });
                        })
                        .catch((err) => {
                          if (!isManualCancellation(err)) {
                            console.error("FAQ Reply Error:", err);
                            store.dispatch(setIsDisabled(false));
                          }

                          reject(err);
                        });
                    }
                  }

                  resolve(obj);
                })
                .catch((err) => reject(err));
            } else {
              reject(err);
            }
          });
      });
    }
    case "VisitResidentPortal":
      return new Promise((resolve, reject) => {
        getResidentPortalLink(`?api_key=${encodedApiKey}&property_id=${propertyId}`)
          .then((res) => {
            resolve({
              data: VISIT_RESIDENT_PORTAL_REPLIES,
              extraData: res.data,
            });
          })
          .catch((err) => {
            reject(err);
          });
      });
    case "Photos":
      if (photos) {
        return Promise.resolve({ data: photos });
      }

      return new Promise((resolve, reject) => {
        getGallery(`?api_key=${encodedApiKey}&property_id=${propertyId}`)
          .then((res) => {
            if (!res.data) {
              const requestOption = {
                tag: "NO_DATA_SHOWN",
                metric: "Surprise",
                propertyId: propertyId,
                sessionId: appSSID,
                appSubsection: "photos",
              };
              createEventSignal(requestOption);
            }

            resolve({ data: res.data });
          })
          .catch((err) => {
            reject(err);
          });
      });

    case "Gallery":
      return new Promise((resolve, reject) => {
        if (data) {
          resolve({ data: data });
        } else if (store.getState().gallery.selectedId) {
          resolve({ data: data });
        } else {
          reject("No item selected");
        }
      });
    case "CommunityInfo":
      return new Promise((resolve, reject) => {
        getPolicies(`?api_key=${encodedApiKey}&property_id=${propertyId}`)
          .then((res) => {
            if (!res.data) {
              const requestOption = {
                tag: "NO_DATA_SHOWN",
                metric: "Surprise",
                propertyId: propertyId,
                sessionId: appSSID,
                appSubsection: "Community info",
              };
              createEventSignal(requestOption);
            }

            resolve({ data: res.data });
          })
          .catch((err) => {
            reject(err);
          });
      });
    case "FeaturesView":
      return new Promise((resolve, reject) => {
        if (data) {
          store.dispatch(setSelectedBadgeItemId(data.toLowerCase()));
          resolve({ data: null });
        } else if (store.getState().badgeSwitch.selectedId) {
          resolve({ data: null });
        } else {
          reject("No item selected");
        }
      });
    case "Videos":
      return new Promise((resolve, reject) => {
        getVideos(`?api_key=${encodedApiKey}&property_id=${propertyId}`)
          .then((res) => {
            if (!res.data) {
              const requestOption = {
                tag: "NO_DATA_SHOWN",
                metric: "Surprise",
                propertyId: propertyId,
                sessionId: appSSID,
                appSubsection: "videos",
              };
              createEventSignal(requestOption);
            }

            resolve({ data: res.data });
          })
          .catch((err) => {
            reject(err);
          });
      });
    case "VideosGallery":
      return new Promise((resolve, reject) => {
        if (data) {
          store.dispatch(setSelectedVideoSwitchId(data.toLowerCase()));
          resolve({ data: null });
        } else if (store.getState().videos.selectedId) {
          resolve({ data: null });
        } else {
          reject("No item selected");
        }
      });
    case "FAQs":
      return new Promise((resolve, reject) => {
        resolve({ data: FAQS_REPLIES });
      });
    case "DoIQualify":
      return new Promise((resolve, reject) => {
        resolve({ data: DO_I_QUALIFY_REPLIES });
      });
    case "customButton":
      return new Promise((resolve) => {
        resolve({
          data: Array.isArray(customButtonData) ? customButtonData.find((cb) => cb?.id === customButtonId) : undefined,
        });
      });
    case "Init": {
      const dup = structuredClone(INITIAL_REPLIES);

      if (!state.property.enabled_schedule_a_tour) {
        dup.options = dup.options.filter((o) => o.nextOptionsType !== "Form4ScheduleTour");
      }

      // if (!state.property.qualificationButtonEnabled) {
      //   dup.options = dup.options.filter((o) => o.nextOptionsType !== "DoIQualify");
      // }

      dup.options.push(...buildCustomButtonOptions(state.property.customButtons));

      return new Promise((resolve) => {
        resolve({ data: dup });
      });
    }
    case "Cancel": {
      return new Promise((resolve) => {
        resolve({ data: { ...INITIAL_REPLIES, options: [] } });
      });
    }
    default:
      return new Promise((resolve, reject) => {
        resolve({ data: INITIAL_REPLIES });
      });
  }
};

export const makeNextOptions = (optionType, data, extraData, isAMA, skip, context, not_available, resMessage) => {
  const state = store.getState();
  const qualification = store.getState().property.qualificationButton;
  const isPriceFilter = store.getState().property.isPriceFilter;
  const propertyId = store?.getState()?.property?.property_id || "";
  const appSSID = state.window.appSSID || "";
  //payload for case 3 reply
  const bedType = state.params.label.bedroom_type || "";
  const desiredMoveIn = state.params.label.desired_move_in || "";
  const price = state.params.label.price || "";

  switch (optionType) {
    case "BedroomTypes": {
      if (skip === "bedroomType") {
        store.dispatch(setBudgets(data));

        const options =
          data?._form_desired_price_range?.map((item, idx) => ({
            content: {
              component: "NormalOptionTile",
              componentOptions: {
                icon: null,
                label: item.Display,
                callbackOptions: {
                  data: { data: item.value, name: "price" },
                  triggerType: "SAVE_2_LOCAL_STORAGE",
                },
              },
            },
            nextOptionsType: desiredMoveIn ? "PriceBrackets" : "WhenMoveIn",
            message: [{ message: item.Display, speaker: "user" }],
            key: "price range",
            event: "h_click",
            disablePlaceholerMessage: false,
            data: null,
          })) || [];

        const customPriceOption = [
          {
            content: {
              component: "RangeSlider",
              componentOptions: {
                icon: null,
                label: "customRange",
                callbackOptions: {
                  data: { data: data._custom_price_range, name: "price" },
                  triggerType: "SAVE_2_LOCAL_STORAGE",
                },
              },
            },
            nextOptionsType: desiredMoveIn ? "PriceBrackets" : "WhenMoveIn",
            message: [],
            key: "price range",
            event: "h_click",
            disablePlaceholerMessage: false,
            data: null,
          },
          {
            content: {
              component: "NormalOptionTile",
              componentOptions: {
                icon: null,
                label: "any",
                callbackOptions: {
                  data: { data: null, name: "price" },
                  triggerType: "SAVE_2_LOCAL_STORAGE",
                },
              },
            },
            nextOptionsType: desiredMoveIn ? "Units" : "WhenMoveIn",
            message: [{ message: "any", speaker: "user" }],
            key: "price range",
            event: "h_click",
            disablePlaceholerMessage: false,
            data: null,
          },
        ];

        let message = "shareMonthlyBudget";

        if (not_available?.length > 1) {
          const requestOption = {
            tag: "NO_FLOOR_PLAN_SHOWED",
            metric: "Surprise",
            propertyId: propertyId,
            sessionId: appSSID,
            appSubsection: "No floor plan showed",
          };
          createEventSignal(requestOption);
          message = textConstants.multiOptNotAvail;
        } else if (not_available?.includes("bedroom_type")) {
          message = textConstants.bedroomNotAvail;
        } else if (not_available?.includes("price_range")) {
          message = textConstants.budgetNotAvail;
        } else if (not_available?.includes("desired_move_in")) {
          message = textConstants.moveInNotAvail;
        }

        const nextMsgs = [];

        if (resMessage && isAMA) {
          nextMsgs.push({ message: resMessage, speaker: "bot" });
        }

        nextMsgs.push({ message: message, speaker: "bot" });

        return {
          nextReplies: {
            options: [...options, ...customPriceOption],
            renderType: "boxView",
            optionsType: "PriceBrackets",
            checkStorage: false,
            disableResetBox: false,
            title: "",
          },
          nextBotMessages: nextMsgs,
        };
      }

      if (skip === "getPriceRange") {
        const options = data.options.map((o) => {
          let data2Save = "";

          switch (o.content?.componentOptions?.label) {
            case "asap":
              data2Save = moment().add(7, "days").format("YYYY-MM-DD");
              break;
            case "next30Days":
              data2Save = moment().add(30, "days").format("YYYY-MM-DD");
              break;
            case "next60Days":
              data2Save = moment().add(60, "days").format("YYYY-MM-DD");
              break;
            case "notSure":
              data2Save = null;
              break;
            default:
              break;
          }

          const content = {
            ...o.content,
            componentOptions: {
              ...o.content.componentOptions,
              callbackOptions: {
                data: { data: data2Save, name: "desired_move_in" },
                triggerType: "SAVE_2_LOCAL_STORAGE",
              },
            },
          };

          return { ...o, content };
        });

        store.dispatch(setFilterDates(false));

        return {
          nextReplies: { ...data, options },
          nextBotMessages: [
            { message: resMessage, speaker: "bot" },
            { message: "planMove", speaker: "bot" },
          ],
        };
      }

      if (skip === "DesiredMoveInDate" || skip === "floorPlans") {
        const message = t("unitMatchPref", { count: data.length });
        let messages = [{ message: message, speaker: "bot" }];

        if (data?.length > 0) {
          const options = data.map((item) => ({
            content: { component: "UnitItem", componentOptions: null },
            message: [],
            nextOptionsType: null,
            key: item.id,
            event: "h_click",
            disablePlaceholerMessage: false,
            data: item,
          }));

          return {
            nextReplies: {
              options,
              title: null,
              renderType: "unitView",
              optionsType: "Init",
              checkStorage: false,
              disableResetBox: false,
            },
            nextBotMessages: messages,
          };
        }

        if (optionType === "UnitsAll") {
          const requestOption = {
            tag: "NO_FLOOR_PLAN_SHOWED",
            metric: "Surprise",
            propertyId: propertyId,
            sessionId: appSSID,
            appSubsection: "No floor plan showed",
          };
          createEventSignal(requestOption);

          return {
            nextReplies: {
              options: _.filter(INITIAL_REPLY_OPTIONS, (o) => !["find_home"].includes(o.key)),
              title: "",
              renderType: "boxView",
              optionsType: "Init",
              checkStorage: false,
              disableResetBox: false,
            },
            nextBotMessages: [
              {
                message: skip === "floorPlans" ? resMessage || "noAvailFloorPlanWhatNext" : "noAvailUnitWhatNext",
                speaker: "bot",
              },
            ],
          };
        }
      }

      if (skip === "noConditionMatched") {
        return {
          nextReplies: INITIAL_REPLIES,
          nextBotMessages: [
            {
              message: resMessage || "unitNotFound",
              speaker: "bot",
            },
          ],
        };
      }

      store.dispatch(setBedRooms(data));

      const options = data?.map((item, idx) => ({
        content: {
          component: "NormalOptionTile",
          componentOptions: {
            icon: "",
            label: item.value,
            callbackOptions: {
              data: { data: item.value, name: "bedroom_type" },
              triggerType: "SAVE_2_LOCAL_STORAGE",
            },
          },
        },
        nextOptionsType: isPriceFilter ? "PriceBrackets" : "WhenMoveIn",
        message: [{ message: item.value, speaker: "user" }],
        key: "bedrooms",
        event: "h_click",
        disablePlaceholerMessage: false,
        data: null,
      }));
      let message = "howManyBedroom";

      if (not_available?.length > 1) {
        message = textConstants.multiOptNotAvail;
      } else if (not_available?.includes("bedroom_type")) {
        message = textConstants.bedroomNotAvail;
      } else if (not_available?.includes("price_range")) {
        message = textConstants.budgetNotAvail;
      } else if (not_available?.includes("desired_move_in")) {
        message = textConstants.moveInNotAvail;
      }

      let botMessages = [{ message: message, speaker: "bot" }];

      if (resMessage) {
        botMessages.unshift({ message: resMessage, speaker: "bot" });
      }

      return {
        nextReplies: {
          options: !isAMA ? [...options, SHOW_ALL_UNITS_OPTION] : [...options, SHOW_ALL_UNITS_OPTION, CANCEL_OPTION],
          renderType: "boxView",
          optionsType: "BedroomTypes",
          title: "",
          disableResetBox: false,
          checkStorage: false,
        },

        nextBotMessages: botMessages,
      };
    }
    case "PriceBrackets": {
      if (skip === "getPriceRange") {
        const options = data.options.map((o) => {
          let data2Save = "";

          switch (o.content?.componentOptions?.label) {
            case "asap":
              data2Save = moment().add(7, "days").format("YYYY-MM-DD");
              break;
            case "next30Days":
              data2Save = moment().add(30, "days").format("YYYY-MM-DD");
              break;
            case "next60Days":
              data2Save = moment().add(60, "days").format("YYYY-MM-DD");
              break;
            case "notSure":
              data2Save = null;
              break;
            default:
              break;
          }

          const content = {
            ...o.content,
            componentOptions: {
              ...o.content.componentOptions,
              callbackOptions: {
                data: { data: data2Save, name: "desired_move_in" },
                triggerType: "SAVE_2_LOCAL_STORAGE",
              },
            },
          };

          return { ...o, content };
        });

        store.dispatch(setFilterDates(false));

        let msgs = [{ message: "planMove", speaker: "bot" }];

        if (resMessage) {
          msgs.unshift({ message: resMessage, speaker: "bot" });
        }

        return {
          nextReplies: { ...data, options },
          nextBotMessages: msgs,
        };
      }

      if (skip === "DesiredMoveInDate") {
        if (data.length > 0) {
          const options = data.map((item) => ({
            content: { component: "UnitItem", componentOptions: null },
            message: [],
            nextOptionsType: null,
            key: item.id,
            event: "h_click",
            disablePlaceholerMessage: false,
            data: item,
          }));

          const message = skip === "floorPlans" ? t("floorPlanMatchPref", { count: data.length }) : t("unitMatchPref", { count: data.length });

          return {
            nextReplies: {
              options,
              title: null,
              renderType: "unitView",
              optionsType: "Init",
              checkStorage: false,
              disableResetBox: false,
            },
            nextBotMessages: [{ message: message, speaker: "bot" }],
          };
        }

        if (optionType === "UnitsAll") {
          const requestOption = {
            tag: "NO_FLOOR_PLAN_SHOWED",
            metric: "Surprise",
            propertyId: propertyId,
            sessionId: appSSID,
            appSubsection: "No floor plan showed",
          };
          createEventSignal(requestOption);

          return {
            nextReplies: {
              options: _.filter(INITIAL_REPLY_OPTIONS, (o) => !["find_home"].includes(o.key)),
              title: "",
              renderType: "boxView",
              optionsType: "Init",
              checkStorage: false,
              disableResetBox: false,
            },
            nextBotMessages: [
              {
                message: skip === "floorPlans" ? "noAvailFloorPlanWhatNext" : "noAvailUnitWhatNext",
                speaker: "bot",
              },
            ],
          };
        }
      }

      store.dispatch(setBudgets(data));
      const isPriceFilter = store.getState().property.isPriceFilter;

      const options =
        data?._form_desired_price_range?.map((item, idx) => ({
          content: {
            component: "NormalOptionTile",

            componentOptions: {
              icon: null,
              label: item.Display,
              callbackOptions: {
                data: { data: item.value, name: "price" },
                triggerType: "SAVE_2_LOCAL_STORAGE",
              },
            },
          },
          nextOptionsType: desiredMoveIn ? "PriceBrackets" : "WhenMoveIn",
          message: [{ message: item.Display, speaker: "user" }],
          key: "price range",
          event: "h_click",
          disablePlaceholerMessage: false,
          data: null,
        })) || [];

      const customPriceOption = [
        {
          content: {
            component: "RangeSlider",
            componentOptions: {
              icon: null,
              label: "customRange",
              callbackOptions: {
                data: { data: data._custom_price_range, name: "price" },
                triggerType: "SAVE_2_LOCAL_STORAGE",
              },
            },
          },
          nextOptionsType: desiredMoveIn ? "PriceBrackets" : "WhenMoveIn",
          message: [],
          key: "price range",
          event: "h_click",
          disablePlaceholerMessage: false,
          data: null,
        },
        {
          content: {
            component: "NormalOptionTile",
            componentOptions: {
              icon: null,
              label: "any",
              callbackOptions: {
                data: { data: null, name: "price" },
                triggerType: "SAVE_2_LOCAL_STORAGE",
              },
            },
          },
          nextOptionsType: desiredMoveIn ? "Units" : "WhenMoveIn",
          message: [{ message: "any", speaker: "user" }],
          key: "price range",
          event: "h_click",
          disablePlaceholerMessage: false,
          data: null,
        },
      ];

      let message = isPriceFilter ? "shareMonthlyBudget" : "planMove";

      if (not_available?.length > 1) {
        message = textConstants.multiOptNotAvail;
      } else if (not_available?.includes("bedroom_type")) {
        message = textConstants.bedroomNotAvail;
      } else if (not_available?.includes("price_range")) {
        message = textConstants.budgetNotAvail;
      } else if (not_available?.includes("desired_move_in")) {
        message = textConstants.moveInNotAvail;
      }

      return {
        nextReplies: {
          options: [...options, ...customPriceOption],
          renderType: "boxView",
          optionsType: "PriceBrackets",
          checkStorage: false,
          disableResetBox: false,
          title: "",
        },
        nextBotMessages: [{ message: message, speaker: "bot" }],
      };
    }
    case "WhenMoveIn": {
      if (skip === "DesiredMoveInDate") {
        if (data.length > 0) {
          const options = data.map((item) => ({
            content: { component: "UnitItem", componentOptions: null },
            message: [],
            nextOptionsType: null,
            key: item.id,
            event: "h_click",
            disablePlaceholerMessage: false,
            data: item,
          }));

          const message = skip === "floorPlans" ? t("floorPlanMatchPref", { count: data.length }) : t("unitMatchPref", { count: data.length });

          return {
            nextReplies: {
              options,
              title: null,
              renderType: "unitView",
              optionsType: "Init",
              checkStorage: false,
              disableResetBox: false,
            },
            nextBotMessages: [{ message: message, speaker: "bot" }],
          };
        }

        if (optionType === "UnitsAll") {
          const requestOption = {
            tag: "NO_FLOOR_PLAN_SHOWED",
            metric: "Surprise",
            propertyId: propertyId,
            sessionId: appSSID,
            appSubsection: "No floor plan showed",
          };
          createEventSignal(requestOption);

          return {
            nextReplies: {
              options: _.filter(INITIAL_REPLY_OPTIONS, (o) => !["find_home"].includes(o.key)),
              title: "",
              renderType: "boxView",
              optionsType: "Init",
              checkStorage: false,
              disableResetBox: false,
            },
            nextBotMessages: [
              {
                message: skip === "floorPlans" ? "noAvailFloorPlanWhatNext" : "noAvailUnitWhatNext",
                speaker: "bot",
              },
            ],
          };
        }
      }

      const options = data.options.map((o) => {
        let data2Save = "";

        switch (o.content?.componentOptions?.label) {
          case "asap":
            data2Save = moment().add(7, "days").format("YYYY-MM-DD");
            break;
          case "next30Days":
            data2Save = moment().add(30, "days").format("YYYY-MM-DD");
            break;
          case "next60Days":
            data2Save = moment().add(60, "days").format("YYYY-MM-DD");
            break;
          case "notSure":
            data2Save = null;
            break;
          default:
            break;
        }

        const content = {
          ...o.content,
          componentOptions: {
            ...o.content.componentOptions,
            callbackOptions: {
              data: { data: data2Save, name: "desired_move_in" },
              triggerType: "SAVE_2_LOCAL_STORAGE",
            },
          },
        };

        return { ...o, content };
      });

      store.dispatch(setFilterDates(false));

      return {
        nextReplies: { ...data, options },
        nextBotMessages: [{ message: "planMove", speaker: "bot" }],
      };
    }
    case "UnitsAll":
    case "UnitsByRoom":
    case "Units": {
      const units = data?.floor_plans || [];

      store.dispatch(setSearchResult(data?.search_result || false));

      if (units.length > 0) {
        const options = units?.map((item) => ({
          content: { component: "UnitItem", componentOptions: null },
          message: [],
          nextOptionsType: null,
          key: item.id,
          event: "h_click",
          disablePlaceholerMessage: false,
          data: item,
        }));
        let message;

        const apiResponse = {
          buffer_search: data.buffer_search,
          filter_excluded: data.filter_excluded,
        };

        const payload = {
          price_preference: !!price,
          bedroom_preference: !!bedType,
          desired_move_in_date: !!desiredMoveIn,
        };

        let nextMessage;

        if (resMessage && resMessage.trim() !== "") {
          nextMessage = [{ message: resMessage, speaker: "bot", feeDisclaimer: data?.fee_disclaimer || null }];
        } else {
          const allPreferences = Object.values(payload).every(Boolean);

          if (allPreferences && resMessage) {
            message = resMessage;
          } else {
            switch (data?.case?.charAt(0)) {
              case "2":
                message = t(generateDynamicMessage(apiResponse, payload, "floorMatchPref"), {
                  bedroom: bedType,
                  count: units.length,
                });

                break;
              case "3":
                message = t(generateDynamicMessage(apiResponse, payload), {
                  bedroom: bedType,
                  count: units.length,
                });

                break;
              default:
                message = t("floorMatchPref3rd");
            }
          }

          nextMessage =
            optionType === "UnitsAll" && data?.case?.charAt(0) === "2"
              ? [
                  {
                    message: units.length == 1 ? t("genericSingleFloorMatchPref") : t("genericFloorMatchPref", { count: units.length }),
                    speaker: "bot",
                  },
                ]
              : [{ message: message, speaker: "bot" }];

          if (data?.fee_disclaimer && nextMessage.length > 0) {
            nextMessage[nextMessage.length - 1].feeDisclaimer = data.fee_disclaimer;
          }
        }

        return {
          nextReplies: {
            options,
            title: null,
            renderType: "unitView",
            optionsType: "Init",
            checkStorage: false,
            unitCase: data?.case?.charAt(0) || "2",
            disableResetBox: false,
            fees: data?.fees || [],
            feeDisclaimer: data?.fee_disclaimer || null,
            showFeeCalculator: data?.show_fee_calculator ?? false,
            hidePricing: data?.hide_pricing,
            tmlpEnabled: data?.tmlp_enabled,
            allUnitsUrl: data?.all_units_url || null,
            shortTourLink: data?.short_tour_link || null,
            isAMADirectView: extraData?.isAMADirectView || false,
          },
          nextBotMessages: nextMessage,
        };
      }

      if (optionType === "UnitsAll") {
        const requestOption = {
          tag: "NO_FLOOR_PLAN_SHOWED",
          metric: "Surprise",
          propertyId: propertyId,
          sessionId: appSSID,
          appSubsection: "No floor plan showed",
        };
        createEventSignal(requestOption);

        return {
          nextReplies: {
            options: _.filter(INITIAL_REPLY_OPTIONS, (o) => !["find_home"].includes(o.key)),
            title: "",
            renderType: "boxView",
            optionsType: "Init",
            checkStorage: false,
            disableResetBox: false,
          },
          nextBotMessages: [
            {
              message: skip === "floorPlans" ? "noAvailFloorPlanWhatNext" : "noAvailUnitWhatNext",
              speaker: "bot",
            },
          ],
        };
      }

      const state = store.getState();
      const bedroomType = state.params.label.bedroom_type || "";

      const options = [SHOW_ALL_UNITS_OPTION, ..._.filter(INITIAL_REPLY_OPTIONS, (o) => ["schedule_tour", "contact_us"].includes(o.key)), EXPLORE_MORE_OPTION];

      // Resolved here rather than passed on as a key: the sentence needs the bedroom type
      // interpolated, and both consumers (NormalOptionTile's t(label) and checkTranslation)
      // only ever call t() with a bare key, so neither could supply the interpolation.
      // Passing finished text through those t() calls is safe — a non-key returns unchanged.
      // The bedroom noun itself still arrives in English from the API and stays English.
      const bedroomLabel = t("showAllBedroomType", { bedroomType: bedroomType.replace("-", " ") });

      const extraOption = {
        content: {
          component: "NormalOptionTile",
          componentOptions: {
            icon: "",
            label: bedroomLabel,
            callbackOptions: null,
          },
        },
        nextOptionsType: "UnitsByRoom",
        message: [
          {
            message: bedroomLabel,
            speaker: "user",
          },
        ],
        key: "extra_units",
        disablePlaceholerMessage: true,
        data: null,
      };

      return {
        nextReplies: {
          options: extraData.enableExtraOption ? [extraOption, ...options] : options,
          title: "",
          renderType: "boxView",
          optionsType: "Init",
          checkStorage: false,
          disableResetBox: false,
        },
        nextBotMessages: [
          {
            message: "noMatchPrefWhatNext",
            speaker: "bot",
          },
        ],
      };
    }

    case "EspNotEnabled": {
      return {
        nextReplies: INITIAL_REPLIES,
        nextBotMessages: [
          {
            message: resMessage || "chatMustBeEnglish",
            speaker: "bot",
          },
          {
            message: "howHelp",
            speaker: "bot",
          },
        ],
      };
    }

    case "end_of_chat": {
      localStorage.removeItem(`${propertyId}-context`);
      localStorage.removeItem(`${propertyId}-bookingId`);

      return {
        nextReplies: INITIAL_REPLIES,
        nextBotMessages: [
          {
            message: resMessage || t("whatNext"),
            speaker: "bot",
          },
        ],
      };
    }

    case "ScheduleTour": {
      const ctx = context?.[0] ?? {};
      const tourAction = ctx.tourAction ?? null;
      const required_info = ctx.required_info ?? null;
      const need_info = ctx.need_info ?? null;
      const error_code = ctx.error_code ?? null;
      const booking_found = ctx.booking_found ?? null;
      const tourType = ctx.tourType ?? null;

      // A form built from a chat-adapter tour context is ML-driven: its Cancel must tell the
      // chat adapter. Guided routes (menu tile, self-tour "go back", unit popup) carry no context.
      if (tourAction && data) {
        data = { ...data, triggerMLOnCancel: true };
      }

      //adding isFromFaq flag in replies for faq flow
      // data = {
      //   ...data,
      //   isFromFaq: true,
      // };

      let message = state.property.enabled_schedule_a_tour ? resMessage ?? "almostDoneFewMoreDetail" : "noScheduleDetailGet";

      if (!resMessage) {
        if (not_available?.includes("tourType")) {
          message = textConstants.tourNotAvail;
        } else if (not_available?.includes("preferredDate")) {
          message = textConstants.dataNotAvail;
        } else if (not_available?.includes("preferredTime")) {
          message = textConstants.timeNotAvail;
        }
      }

      if (tourAction === "cancel_inquiry") {
        if (required_info && need_info === "entryId") {
          return {
            nextReplies: data ?? INITIAL_REPLIES,
            nextBotMessages: [
              {
                message: resMessage?.trim() || "noBookingFillForm",
                speaker: "bot",
              },
            ],
          };
        }

        if (required_info && !booking_found && error_code === 404) {
          return {
            nextReplies: data ?? INITIAL_REPLIES,
            nextBotMessages: [
              {
                message: resMessage?.trim() || "noBookingFillForm",
                speaker: "bot",
              },
            ],
          };
        }

        if (!required_info && booking_found) {
          return {
            nextReplies: CANCEL_TOUR_REPLIES,
            nextBotMessages: [
              {
                message: resMessage?.trim() || t?.("foundConfirmedTourDetails"),
                speaker: "bot",
                subject: "cancel_inquiry",
              },
              {
                message: "wouldLikeToCancel",
                speaker: "bot",
              },
            ],
          };
        }
      }

      if (tourAction === "cancel_confirmation") {
        if (need_info === "bookingId" && required_info) {
          localStorage.removeItem(`${propertyId}-context`);
          // localStorage.removeItem(`${propertyId}-bookingId`);

          return {
            nextReplies: INITIAL_REPLIES,
            nextBotMessages: [
              {
                message: resMessage?.trim() || "troubleLocatingTourToCancel",
                speaker: "bot",
              },
            ],
          };
        }

        if (!booking_found && !required_info) {
          localStorage.removeItem(`${propertyId}-context`);
          // localStorage.removeItem(`${propertyId}-bookingId`);

          return {
            nextReplies: INITIAL_REPLIES,
            nextBotMessages: [
              {
                message: resMessage?.trim() || "cannotLocateTourSavedInfo",
                speaker: "bot",
              },
            ],
          };
        }

        if (booking_found && !required_info) {
          localStorage.removeItem(`${propertyId}-context`);
          localStorage.removeItem(`${propertyId}-bookingId`);

          return {
            nextReplies: INITIAL_REPLIES,
            nextBotMessages: [
              {
                message: resMessage?.trim() || "bookingDeletedSuccessfully",
                speaker: "bot",
              },
            ],
          };
        }
      }

      if (tourAction === "reschedule_inquiry") {
        if (need_info === "entryId" && required_info) {
          return {
            nextReplies: data ?? INITIAL_REPLIES,
            nextBotMessages: [
              {
                message: t("pleaseShareEmailOrPhone"),
                speaker: "bot",
              },
            ],
          };
        }

        if (booking_found && !required_info) {
          data = {
            ...data,
            isReschedule: true,
          };

          return {
            nextReplies: data ?? INITIAL_REPLIES,
            nextBotMessages: [
              {
                message: resMessage?.trim() || t?.("foundConfirmedTourDetails"),
                speaker: "bot",
                subject: "cancel_inquiry",
              },
              {
                message: "rescheduleTourForMe",
                speaker: "bot",
              },
            ],
          };
        }

        if (!booking_found && required_info && error_code === 404) {
          data = {
            ...data,
            isReschedule: true,
          };

          return {
            nextReplies: data ?? INITIAL_REPLIES,
            nextBotMessages: [
              {
                message: resMessage?.trim() || "noScheduledTourFillForm",
                speaker: "bot",
              },
            ],
          };
        }
      }

      if (tourAction === "reschedule_confirmation" || tourAction === "schedule_confirmation") {
        // if (booking_found && !required_info) {
        localStorage.removeItem(`${propertyId}-context`);
        // localStorage.removeItem(`${propertyId}-bookingId`);

        // Clear tour data after successful submission so next time form is opened, it fetches fresh data
        store.dispatch(resetScheduleTourState());
        store.dispatch(setShouldFetchSlots(true));

        return {
          nextReplies: INITIAL_REPLIES,
          nextBotMessages: [
            {
              // Two whole-sentence keys rather than one key with the verb interpolated:
              // Spanish inflects around the verb ("programado" / "reprogramado"), so a
              // shared template with a swapped word cannot produce both correctly.
              message: resMessage?.trim() || (tourAction === "reschedule_confirmation" ? "tourRescheduledSuccess" : "tourScheduledSuccess"),
              speaker: "bot",
            },
          ],
        };
        // }
      }

      if (tourAction === "status_inquiry") {
        if (required_info && need_info === "entryId" && tourType === "") {
          return {
            nextReplies: data ?? INITIAL_REPLIES,
            nextBotMessages: [
              {
                message: t("donotSeeTourShareEmailOrPhone"),
                speaker: "bot",
              },
            ],
          };
        }

        if (required_info && !booking_found && error_code === 404) {
          return {
            nextReplies: data ?? INITIAL_REPLIES,
            nextBotMessages: [
              {
                message: resMessage?.trim() || "noToursFoundBookNew",
                speaker: "bot",
              },
            ],
          };
        }

        if (booking_found && !required_info) {
          // Scheduling disabled in JAI: Reschedule opens the tour form, so it goes; cancelling an
          // existing booking stays available. (Typed flows are the chat adapter's call — it gets
          // tourScheduleEnabled on every request — this card is built here.)
          const statusReplies = state.property.enabled_schedule_a_tour
            ? RESCHEDULE_TOUR_REPLIES
            : { ...RESCHEDULE_TOUR_REPLIES, options: RESCHEDULE_TOUR_REPLIES.options.filter((o) => o.nextOptionsType !== "rescheduleFromTourStatus") };

          return {
            nextReplies: statusReplies ?? INITIAL_REPLIES,
            nextBotMessages: [
              {
                // Generic on purpose: this used to name one specific property, which was
                // wrong for every other one. The API response carries the real name when
                // it has it, and that path takes precedence.
                message: resMessage?.trim() || "tourIsScheduled",
                speaker: "bot",
                subject: "cancel_inquiry",
              },
              {
                message: "changeInPlan",
                speaker: "bot",
              },
            ],
          };
        }
      }

      if (tourAction === "tour_type_inquiry") {
        if (booking_found && !required_info) {
          localStorage.removeItem(`${propertyId}-context`);
          // localStorage.removeItem(`${propertyId}-bookingId`);

          return {
            nextReplies: INITIAL_REPLIES,
            nextBotMessages: [
              {
                message: resMessage?.trim() || "tourFound",
                speaker: "bot",
              },
            ],
          };
        }
      }

      if (tourAction === "cancel_rejection") {
        return {
          nextReplies: INITIAL_REPLIES,
          nextBotMessages: [
            {
              message: resMessage || t("whatNext"),
              speaker: "bot",
            },
          ],
        };
      }

      return {
        nextReplies: data ?? INITIAL_REPLIES,
        nextBotMessages: [
          {
            message: message || data.response || resMessage || "somethingWentWrong",
            speaker: "bot",
          },
        ],
      };
    }

    case "cancelTourFromTourStatus": {
      return {
        nextReplies: CANCEL_TOUR_REPLIES ?? INITIAL_REPLIES,
        nextBotMessages: [
          {
            message: "wouldLikeToCancel",
            speaker: "bot",
          },
        ],
      };
    }

    case "rescheduleFromTourStatus": {
      // Only reachable from the status reply's Reschedule button, so always chat-adapter driven.
      data = {
        ...data,
        isReschedule: true,
        triggerMLOnCancel: true,
      };

      return {
        nextReplies: data ?? INITIAL_REPLIES,
        nextBotMessages: [
          {
            message: "rescheduleTourForMe",
            speaker: "bot",
          },
        ],
      };
    }

    case "cancelTour": {
      return {
        nextReplies: INITIAL_REPLIES,
        nextBotMessages: [
          {
            message: t("tourGotCanceled"),
            speaker: "bot",
          },
        ],
      };
    }

    case "noDoNotCancel": {
      return {
        nextReplies: INITIAL_REPLIES,
        nextBotMessages: [
          {
            message: t("donotDeleteBooking"),
            speaker: "bot",
          },
        ],
      };
    }

    case "Form4ScheduleTour":
      return {
        nextReplies: data,
        nextBotMessages: [
          { message: state.property.enabled_schedule_a_tour ? resMessage ?? "almostDoneFewMoreDetail" : "noScheduleDetailGet", speaker: "bot" },
        ],
      };
    case "Form4UpdateSelections":
      return {
        nextReplies: data,
        nextBotMessages: [{ message: t("sureUpdatePreference"), speaker: "bot" }],
      };
    case "PostUpdateSelections":
      return {
        nextReplies: data,
        nextBotMessages: _.map(extraData.botMessages, (m) => ({
          message: m,
          speaker: "bot",
        })),
      };
    case "LikeTour":
    case "DisLikeTour":
    case "SkipTour":
    case "LikeContact":
    case "DisLikeContact":
    case "SkipContact":
      return {
        nextReplies: data,
        nextBotMessages: _.map(extraData.botMessages, (m) => ({
          message: m,
          speaker: "bot",
        })),
      };
    case "PostScheduleTour": {
      return {
        nextReplies: data,
        nextBotMessages: _.map(extraData.botMessages, (m) => ({
          message: m,
          speaker: "bot",
        })),
      };
    }
    case "SelfTour": {
      return {
        nextReplies: {
          ...data,
          options: data.options.map((o) => {
            if (o.content.component === "LinkOptionTile") {
              return {
                ...o,
                content: {
                  ...o.content,
                  componentOptions: {
                    ...o.content.componentOptions,
                    link: extraData,
                  },
                },
              };
            }

            return o;
          }),
        },
        nextBotMessages: [],
      };
    }
    case "Form4ContactUs":
      return {
        // The AMA tour route lands here when the chat adapter needs an entryId; the guided tile passes no context.
        nextReplies: context?.[0]?.tourAction ? { ...data, triggerMLOnCancel: true } : data,
        nextBotMessages: [{ message: resMessage?.trim() ? resMessage : "fewMoreDetailGet", speaker: "bot" }],
      };
    case "PostContactUs":
      return {
        nextReplies: data,
        nextBotMessages: _.map(extraData.botMessages, (m) => ({
          message: m,
          speaker: "bot",
        })),
      };
    case "VisitResidentPortal": {
      return {
        nextReplies: {
          ...data,
          options: data.options.map((o) => {
            if (o.content.component === "LinkOptionTile") {
              return {
                ...o,
                content: {
                  ...o.content,
                  componentOptions: {
                    ...o.content.componentOptions,
                    link: extraData,
                  },
                },
              };
            }

            return o;
          }),
        },
        nextBotMessages: [{ message: "wouldVisitResidentPortal", speaker: "bot" }],
      };
    }
    case "Photos": {
      const options = _(data)
        .keys()
        .filter((k) => !_.isEmpty(data[k]))
        .map((k) => ({
          content: {
            component: "NormalOptionTile",
            componentOptions: {
              label: k.toString(),
              icon: null,
              callbackOptions: null,
            },
          },
          nextOptionsType: "Gallery",
          message: [{ message: k, speaker: "user" }],
          key: k.toString().toLowerCase(),
          data: data,
          disablePlaceholerMessage: true,
        }))
        .valueOf();

      if (options.length > 0) {
        return {
          nextReplies: {
            options,
            title: null,
            optionsType: "Photos",
            renderType: "boxView",
            checkStorage: false,
            disableResetBox: false,
          },
          nextBotMessages: [{ message: "herePhotos", speaker: "bot" }],
        };
      }

      return {
        nextReplies: {
          options: _.filter(INITIAL_REPLY_OPTIONS, (o) => ["schedule_tour", "find_home"].includes(o.key)),
          title: "",
          renderType: "boxView",
          optionsType: "Photos",
          checkStorage: false,
          disableResetBox: false,
        },
        nextBotMessages: [{ message: "noPhotoWhatNext", speaker: "bot" }],
      };
    }
    case "Gallery":
      return {
        nextReplies: {
          options: [],
          title: null,
          optionsType: "Gallery",
          renderType: "galleryView",
          checkStorage: false,
          disableResetBox: false,
        },
        nextBotMessages: [],
      };
    case "CommunityInfo": {
      const policies = {};

      _.forEach(data, (value, key) => {
        if (key === "Overview" && value) {
          policies["Overview"] = {
            label: t("propertyOverview"),
            title: t("propertyOverview"),
            data: value,
          };
        } else if (key === "Apartment" && value) {
          policies["Features"] = {
            label: t("features"),
            title: t("appartmentFeat"),
            data: value,
          };
        } else if (key === "Community" && value) {
          policies["Amenities"] = {
            label: t("amenities"),
            title: t("communityAmenities"),
            data: value,
          };
        } else if (key === "Neighborhood" && value && value?.length > 0) {
          policies["Neighborhood"] = {
            label: t("neighborhood"),
            title: t("neighborhoodInfo"),
            data: _.compact(value),
          };
        } else if (key === "Parking" && value && value?.length > 0) {
          policies["Parking"] = {
            label: t("parkPolicy"),
            title: t("parkPolicyInfo"),
            data: _.compact(value),
          };
        } else if (key === "Pet" && value && value?.length > 0) {
          policies["Pet"] = {
            label: t("petPolicy"),
            title: t("petPolicyInfo"),
            data: _.compact(value),
          };
        } else if (key === "Office Hours" && value && value?.length > 0) {
          policies["Hours"] = {
            label: t("officeHours"),
            title: t("officeHourInfo"),
            data: _.compact(value),
          };
        } else if (key === "Promotion Offers" && !(!value.end_date && !value.start_date && !value.image_url && !value.description && !value.desclaimer)) {
          policies["Offer"] = {
            label: t("offer"),
            title: t("offerDetails"),
            data: value,
          };
        }
      });

      const overviewSection = policies["Overview"];

      delete policies["Overview"];

      const reorderedPolicies = overviewSection
        ? {
            Overview: overviewSection,
            ...policies,
          }
        : {
            ...policies,
          };

      store.dispatch(setPolicies(reorderedPolicies));

      store.dispatch(
        setBadgeSwitchItems(
          _(reorderedPolicies)
            .keys()
            .filter((k) => !_.isEmpty(reorderedPolicies[k].data))
            .map((k) => ({
              id: k.toLowerCase(),
              title: reorderedPolicies[k]["label"],
              label: k,
            }))
            .valueOf()
        )
      );

      const options = _(reorderedPolicies)
        .keys()
        .filter((k) => !_.isEmpty(reorderedPolicies[k].data))
        .map((k) => ({
          content: {
            component: "NormalOptionTile",
            componentOptions: {
              label: reorderedPolicies[k]["label"],
              icon: null,
              callbackOptions: null,
            },
          },
          nextOptionsType: "FeaturesView",
          message: [{ message: reorderedPolicies[k]["label"], speaker: "user" }],
          key: k.toString().toLowerCase(),
          data: k,
          disablePlaceholerMessage: true,
        }))
        .valueOf();

      if (options.length > 0) {
        return {
          nextReplies: {
            options,
            title: null,
            optionsType: "CommunityInfo",
            renderType: "boxView",
            checkStorage: false,
            disableResetBox: false,
          },
          nextBotMessages: [{ message: "hereCommunityInfo", speaker: "bot" }],
        };
      }

      return {
        nextReplies: {
          options: _.filter(INITIAL_REPLY_OPTIONS, (o) => ["schedule_tour", "find_home"].includes(o.key)),
          title: "",
          renderType: "boxView",
          optionsType: "CommunityInfo",
          checkStorage: false,
          disableResetBox: false,
        },
        nextBotMessages: [{ message: "noCommunityInfoWhatNext", speaker: "bot" }],
      };
    }
    case "FeaturesView":
      return {
        nextReplies: {
          options: [],
          title: null,
          optionsType: "FeaturesView",
          renderType: "featuresView",
          checkStorage: false,
          disableResetBox: false,
        },
        nextBotMessages: [],
      };
    case "Videos": {
      // Fixed tab order (per product mockups). Unknown categories sort last.
      const CATEGORY_ORDER = ["community_video", "floor_plan_video", "pre_recorded_video", "3d_video"];

      const categoryRank = (k) => {
        const i = CATEGORY_ORDER.indexOf(k);

        return i === -1 ? 999 : i;
      };

      const processedData = {};

      _(data)
        .keys()
        .filter((k) => !_.isEmpty(data[k]))
        .forEach((k) => {
          // Honor the API's `position` field as the display order within a category.
          const postData = _(data[k])
            .filter((d) => !_.isEmpty(d.url))
            .sortBy((d) => Number(d.position) || 0)
            .valueOf();

          if (postData.length > 0) {
            processedData[k] = postData;
          }
        });

      store.dispatch(setVideos(processedData));

      // Single ordered key list reused for both the tabs and the option tiles.
      const orderedKeys = _(processedData).keys().sortBy(categoryRank).valueOf();

      store.dispatch(
        setBadgeSwitchVideos(
          orderedKeys.map((k) => ({
            id: k.toLowerCase(),
            title:
              k === "pre_recorded_video"
                ? "Video Tours"
                : k === "3d_video"
                ? "3D Tours"
                : k === "community_video"
                ? "Community"
                : k === "floor_plan_video"
                ? "Floor Plans"
                : k,
          }))
        )
      );

      const options = orderedKeys.map((k) => ({
        content: {
          component: "NormalOptionTile",
          componentOptions: {
            label: k === "videos" ? "videoTours" : k,
            icon: null,
            callbackOptions: null,
          },
        },
        nextOptionsType: "VideosGallery",
        message: [{ message: k === "videos" ? "videoTours" : k, speaker: "user" }],
        key: k.toString().toLowerCase(),
        data: k,
        disablePlaceholerMessage: true,
      }));

      if (options.length > 0) {
        return {
          nextReplies: {
            options,
            title: null,
            optionsType: "Videos",
            renderType: "boxView",
            checkStorage: false,
            disableResetBox: false,
          },
          nextBotMessages: [{ message: "hereVideos", speaker: "bot" }],
        };
      }

      return {
        nextReplies: {
          options: _.filter(INITIAL_REPLY_OPTIONS, (o) => ["schedule_tour", "find_home", "contact_us"].includes(o.key)),
          title: "",
          renderType: "boxView",
          optionsType: "Videos",
          checkStorage: false,
          disableResetBox: false,
        },
        nextBotMessages: [{ message: "noVideoWhatNext", speaker: "bot" }],
      };
    }
    case "VideosGallery": {
      return {
        nextReplies: {
          options: [],
          title: null,
          optionsType: "VideosGallery",
          renderType: "videosGalleryView",
          checkStorage: false,
          disableResetBox: false,
        },
        nextBotMessages: [],
      };
    }
    case "FAQs":
      return {
        nextReplies: data,
        nextBotMessages: [],
      };
    case "DoIQualify":
      return {
        nextReplies: data,
        nextBotMessages: [{ message: qualification.question, speaker: "bot" }],
      };
    case "customButton": {
      const options = [..._.filter(INITIAL_REPLY_OPTIONS, (o) => ["schedule_tour", "contact_us"].includes(o.key)), EXPLORE_MORE_OPTION];

      return {
        nextReplies: {
          checkStorage: false,
          disableResetBox: true,
          options,
          optionsType: "customButton",
          title: "",
          renderType: "boxView",
        },
        nextBotMessages: [],
      };
    }
    case "EmbedMapView":
      return {
        nextReplies: INITIAL_REPLIES,
        nextBotMessages: [
          {
            message: resMessage || "Here is the map view:",
            speaker: "bot",
            subject: "embed_map_view",
          },
        ],
      };

    case "Init":
      return {
        nextReplies: data,
        nextBotMessages: [{ message: "howHelp", speaker: "bot", createdAt: new Date() }],
      };
    default:
      return {
        nextReplies: data,
        nextBotMessages: [{ message: "howHelp", speaker: "bot", createdAt: new Date() }],
      };
  }
};

function generateDynamicMessage(apiResponse, payload, defaultMsg = "floorMatchPref2nd") {
  const { buffer_search, filter_excluded } = apiResponse || {};
  const { bedroom_preference, price_preference, desired_move_in_date } = payload || {};

  const defaultResponse = defaultMsg || "floorMatchPref2nd";

  const excludedFilters = (filters) => Array.isArray(filter_excluded) && filters.every((filter) => filter_excluded.includes(filter));

  const none = !desired_move_in_date && !price_preference && !bedroom_preference;
  const budgetOnly = price_preference && !desired_move_in_date && !bedroom_preference;
  const moveInOnly = desired_move_in_date && !price_preference && !bedroom_preference;
  const bedroomOnly = bedroom_preference && !desired_move_in_date && !price_preference;
  const allPreferences = desired_move_in_date && bedroom_preference && price_preference;
  const bedroomAndPrice = bedroom_preference && price_preference && !desired_move_in_date;
  const budgetAndMoveIn = desired_move_in_date && price_preference && !bedroom_preference;
  const bedroomAndMoveIn = desired_move_in_date && bedroom_preference && !price_preference;

  if (buffer_search === true && filter_excluded?.length === 0) {
    switch (true) {
      case moveInOnly:
        return "3a_buffer_search_no_filters_desired_move_in";

      case bedroomAndMoveIn:
        return "3a_buffer_search_no_filters_bedroom_desired_move_in";

      case budgetAndMoveIn:
        return "3a_buffer_search_no_filters_budget_desired_move_in";

      case allPreferences:
        return "3a_buffer_search_no_filters_all_preferences";

      default:
        return defaultResponse;
    }
  } else if (buffer_search === false && filter_excluded?.length === 0) {
    switch (true) {
      case bedroomOnly:
        return "2_bedroom_only";

      case budgetOnly:
        return "2_budget_only";

      case moveInOnly:
        return "2_move_in_only";

      case bedroomAndPrice:
        return "2_bedroom_and_price";

      case bedroomAndMoveIn:
        return "2_bedroom_and_move_in";

      case budgetAndMoveIn:
        return "2_budget_and_move_in";

      case allPreferences:
        return "2_all_preferences";

      case none:
        return "floorMatchPref";

      default:
        return defaultResponse;
    }
  } else if (excludedFilters(["bedroom_type", "price_range", "desired_move_in"])) {
    switch (true) {
      case allPreferences:
        return "3d_buffer_search_bedroom_pref_move_in_price";

      default:
        return defaultResponse;
    }
  } else if (buffer_search === false && excludedFilters(["price_range", "bedroom_type"])) {
    switch (true) {
      case allPreferences:
        return "3c1_move_in_date_bedroom_budget";

      case bedroomAndPrice:
        return "3c1_no_move_in_date_bedroom_budget";

      default:
        return defaultResponse;
    }
  } else if (buffer_search === true && excludedFilters(["price_range", "bedroom_type"])) {
    switch (true) {
      case allPreferences:
        return "3c2_move_in_date_bedroom_budget_timeline";

      default:
        return defaultResponse;
    }
  } else if (buffer_search === false && excludedFilters(["price_range"])) {
    switch (true) {
      case budgetOnly:
        return "3b1_no_move_in_no_bedroom_budget";

      case bedroomAndPrice:
        return "3b1_no_buffer_bedroom_budget";

      case budgetAndMoveIn:
        return "3b1_move_in_budget_no_bedroom";

      case allPreferences:
        return "3b1_move_in_budget_bedroom";

      default:
        return defaultResponse;
    }
  } else if (buffer_search === true && excludedFilters(["price_range"])) {
    switch (true) {
      case budgetAndMoveIn:
        return "3b2_desired_move_in_no_bedroom_with_budget";

      case allPreferences:
        return "3b2_desired_move_in_with_bedroom_and_budget";

      default:
        return defaultResponse;
    }
  } else if (buffer_search === false && excludedFilters(["bedroom_type"])) {
    switch (true) {
      case bedroomAndMoveIn:
        return "3c2_no_buffer_search_bedroom_preference_timeline";

      case allPreferences:
        return "3c2_no_buffer_search_bedroom_budget_timeline";

      case bedroomOnly:
        return "3c2_no_buffer_search_bedroom_preference_general";

      case bedroomAndPrice:
        return "3c2_no_buffer_search_bedroom_preference_budget";

      default:
        return defaultResponse;
    }
  } else if (buffer_search === true && excludedFilters(["bedroom_type"])) {
    switch (true) {
      case bedroomAndMoveIn:
        return "3c2_buffer_search_bedroom_pref_timeline_no_price";

      default:
        return defaultResponse;
    }
  } else {
    return defaultResponse;
  }
}

export const sortedByDate = (a, b) => {
  const dateA = new Date(a.createdAt);
  const dateB = new Date(b.createdAt);

  return dateA - dateB;
};

export const validator = (data, type) => {
  switch (type) {
    case "EMAIL":
      return data.length > 0 && /^([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})(:[a-z0-9]+)?$/i.test(data);
    case "PHONE":
      return data.length > 0 && /^\([2-9]\d{2}\) \d{3}-\d{4}$/.test(data);
    case "TEXT_ONLY":
      return data.length > 0 && /^[a-zA-Z\s-]+$/.test(data);
    default:
      return true;
  }
};

export const fetchMascotSettings = async (propertyId) => {
  const myHeaders = getGraphQLHeaders();

  // Mascot is the only GraphQL call returning translatable copy. `locale` is a declared
  // argument on `mascot`, so it rides in the payload rather than on the URL. Declared
  // String! so the variable is accepted whether the schema types the arg String or String!.
  const graphql = JSON.stringify({
    query: `
      query MascotSettings($propertyId: ID!, $locale: String!) {
        mascot(propertyId: $propertyId, locale: $locale) {
          mascotSetting {
            colors {
              primary
              secondary
              tertiary
            }
            icon
            iconUrl
            animation
            quickActions
            welcomeMessage
            firstMessage
            bubbleType
            disclaimer
          }
        }
      }
    `,
    variables: { propertyId, locale: getApiLocale() },
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return await graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions);
};

export const fetchGtmId = (encoded_property_id) => {
  const myHeaders = getGraphQLHeaders();

  const graphql = JSON.stringify({
    query: `query getGtmIdQuery($propertyId: ID!) {\n  websiteGtmScript(\n    propertyId: $propertyId \n  ) {\n    id\n      noscript\n        script\n       hytoursScript\n }\n  }\n`,
    variables: { propertyId: `${encoded_property_id}` },
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions);
};

export const getConversationId = (appSSID) => {
  const propertyId = store?.getState()?.property?.property_id || "";
  const email = store?.getState()?.property?.email || localStorage.getItem(`${propertyId}-email`) || "";
  const entryId = store?.getState()?.property?.entry_id || localStorage.getItem(`${propertyId}-entry_id`) || "";
  const date = moment().format("YYYY-MM-DD h:mm:ss");

  const myHeaders = getGraphQLHeaders();

  const graphql = JSON.stringify({
    query: `mutation{\n  createHayleyConversation(input: {\n    propertyId: "${propertyId}",\n    startAt: "${date}",\n    sessionId: "${appSSID}",\n    entryId: "${entryId}",\n    email: "${email}"\n  }) {\n  hayleyConversation{\n    id\n   }\n}\n}`,
    variables: {},
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions);
};

/**
 * In-flight `createHayleyConversation` request, so concurrent senders share one creation
 * instead of each starting their own. Cleared when it settles, which also means a failed
 * attempt is retried by the next message rather than poisoning the session.
 */
let conversationIdRequest = null;

/**
 * Resolve this session's conversation id, creating the conversation if it does not have one.
 *
 * Every transcript write needs a conversation to attach to. Until this existed, any session
 * that never ran `openChatApp` sent `conversationId: ""` — which the CRM rejects with
 * "Conversation not found", silently losing the transcript. That was reachable in normal use:
 * a mascot quick-action opens the chat directly (HotKeys) without going through openChatApp,
 * and the self-heal effect in App.js is gated on `isParentView`, i.e. standalone only, so an
 * embedded widget never recovered for the rest of the session.
 *
 * Resolves to the id, or to `null` when creation itself failed (reported, and left for the
 * next message to retry).
 */
export const ensureConversationId = () => {
  const existing = store.getState().window.conversationID || "";

  if (existing) {
    return Promise.resolve(existing);
  }

  if (conversationIdRequest) {
    return conversationIdRequest;
  }

  const appSSID = store.getState().window.appSSID || "";

  conversationIdRequest = getConversationId(appSSID)
    .then((res) => res.json())
    .then((res) => {
      const id = res?.data?.createHayleyConversation?.hayleyConversation?.id || null;

      if (id) {
        store.dispatch(setConversationID(id));
      } else {
        // A 200 with no id (GraphQL errors[], schema change) — invisible to the HTTP-status
        // chokepoints, and it silently costs us the whole transcript.
        reportHandledError(new Error("createHayleyConversation returned no id"), {
          area: "conversation_flow",
          kind: "conversation_create_empty",
        });
      }

      return id;
    })
    .catch((err) => {
      reportHandledError(err, { area: "conversation_flow", kind: "conversation_create_failed" });

      return null;
    })
    .finally(() => {
      conversationIdRequest = null;
    });

  return conversationIdRequest;
};

export const getContactInfo = (conversationId, sessionId, entryId) => {
  const myHeaders = getGraphQLHeaders();

  const graphql = JSON.stringify({
    query: `query getContactInfoQuery($conversationId: ID!, $sessionId: String, $entryId: String) {
  getContactInfo(conversationId: $conversationId, sessionId: $sessionId, entryId: $entryId) {
    firstName
    lastName
    email
    phoneNumber
  }
}`,
    variables: { conversationId, sessionId, entryId },
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions);
};

export const initConversationWithMessages = (SSID, questionMsgs = [], answerMsgs = []) => {
  const questionMsg = questionMsgs[0];
  const answerMsg = answerMsgs[0];

  // Reuses this session's conversation when it already has one — this used to create a SECOND
  // conversation unconditionally — and creates one exactly once when it does not. `SSID` is kept
  // in the signature for callers; ensureConversationId reads the same value off the store.
  return ensureConversationId()
    .then((conversationId) => {
      if (!conversationId) {
        // Creation failed and was already reported; sending now would only produce
        // "Conversation not found" rows.
        return;
      }

      if (questionMsg?.message) {
        sendHayleyMessage({
          text: t(questionMsg.message),
          queryType: "question",
          ownerType: "prospect",
          kgNodeValue: questionMsg.message === "findHome" ? "find_an_apartment" : t(questionMsg.message),
        });
      }

      if (answerMsg?.message) {
        sendHayleyMessage({
          text: t(answerMsg.message),
          queryType: "answer",
          ownerType: "agent",
          kgNodeValue: t(answerMsg.message),
        });
      }
    })
    .catch((err) => {
      console.error("initConversationWithMessages error:", err);
    });
};

export const getHayleyChatDisclaimer = (orgId, propertyId) => {
  const myHeaders = getGraphQLHeaders();

  const graphql = JSON.stringify({
    query: `
      query gethayleyChatDisclaimerQuery($orgId: ID!, $propertyId: ID!) {
        hayleyChatDisclaimer(orgId: $orgId, propertyId: $propertyId) {
          orgLevelDisclaimer
          orgLevelDisclaimerEnabled
          propertyLevelDisclaimer
          propertyLevelDisclaimerEnabled
          disclaimerType
        }
      }
    `,
    variables: {
      orgId,
      propertyId,
    },
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions);
};

export const updateHayleyConversation = (entryId, { firstName = "", lastName = "", phoneNumber = "", email = "" } = {}) => {
  const conversationId = store?.getState()?.window?.conversationID || "";

  const myHeaders = getGraphQLHeaders();

  const graphql = JSON.stringify({
    query: `mutation updateHayleyConversation(
  $id: ID!,
  $entryId: String,
  $firstName: String,
  $lastName: String,
  $phoneNumber: String,
  $email: String
) {
  updateHayleyConversation(input: {
    id: $id
    entryId: $entryId
    firstName: $firstName
    lastName: $lastName
    phoneNumber: $phoneNumber
    email: $email
  }) {
    hayleyConversation {
       id
      propertyId
      entryId
      firstName
      lastName
      startAt
      endAt
      meta
    }
  }
}`,
    variables: {
      id: conversationId,
      entryId,
      firstName,
      lastName,
      phoneNumber,
      email,
    },
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions, { silent: true });
};

export const retrieveConversation = (appSSID, pagination, propertyId) => {
  const myHeaders = getGraphQLHeaders();

  const graphqlQuery = {
    query: `query RetrieveHayleyConversation($sessionId: String!, $params: Json, $propertyId: String!) {
        retrieveHayleyConversation(
            sessionId: $sessionId,
            params: $params,
            propertyId: $propertyId
        ) {
            id
            propertyId
            messagesCount
            messages {
                propertyId
                text
                queryType
                createdAt
                id
                feedback
            }
            numberOfTokens
            startAt
            endAt
            summary
            sessionId
            outcome
            entryId
        }
    }`,
    variables: {
      sessionId: appSSID,
      params: pagination,
      propertyId,
    },
  };

  const graphql = JSON.stringify(graphqlQuery);

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions);
};

export const saveConversationFeedback = (conId, feedback = "skip", toolName = "contact_us") => {
  const myHeaders = getGraphQLHeaders();

  const graphql = JSON.stringify({
    query: `mutation{\n  saveConversationFeedback(input: {\n    coversationId: "${conId}",\n   feedback: "${feedback}"\n    toolName: "${toolName}",\n  }) {\n  response \n}\n}`,
    variables: {},
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions, { silent: true });
};

export const translationStatus = (status) => {
  store.dispatch(setTransStatus(status));
};

export const getTranslations = (category) => {
  const myHeaders = getGraphQLHeaders();

  const graphql = JSON.stringify({
    query: `query translationsQuery {
      getTranslations(category: "${category}") {
        english
        spanish
        key
      }
    }`,
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions);
};

export const saveHayleyChatbotFeedback = (params) => {
  const { message, feedback, propertyId, page, email } = params;
  const myHeaders = getGraphQLHeaders();

  let query = `mutation{\n  saveHayleyChatbotFeedback(input: {\n    message: "${message}",\n   feedback: "${feedback}",\n   org_id: "${propertyId}"\n }) {\n  response \n}\n}`;

  if (page === "feedback") {
    let conventionalFeed = "thumbs_up";

    if (feedback === "complaint" || feedback === "negative_feedback") {
      conventionalFeed = "thumbs_down";
    }

    query = `mutation{\n  saveHayleyChatbotFeedback(input: {\n    message: "${message}",\n   feedback: "${conventionalFeed}",\n   feedback_type: "${feedback}",\n   email: "${email}",\n   originator: "mail",\n   org_id: "${propertyId}"\n }) {\n  response \n}\n}`;
  }

  const graphql = JSON.stringify({
    query,
    variables: {},
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions);
};

export const createEventSignal = (data) => {
  const { propertyId, tag, appSubsection, metric, sessionId, entryId, metaData } = data;
  const myHeaders = getGraphQLHeaders();

  const graphql = JSON.stringify({
    query: `mutation{
      trackHayleySignal(input: {
        sessionId: "${sessionId}",
        entryId: "${entryId}",
        tag: "${tag}",
        appSubsection: "${appSubsection}",
        metric: "${metric}",
        propertyId: "${propertyId}",
        responseTime: "0.21",
        processingTime: "0.11",
        statusCode: "200",
        statusMessage: "",
        metadata: "${metaData || "{}"} ",
      }) {
        success
      }
    }`,
    variables: {},
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions, { silent: true });
};

/**
 * The raw transcript write. Assumes a usable `conversationId`; callers come through
 * `createHayleyMessage`, which guarantees one.
 */
const postHayleyMessage = (data) => {
  const { conversationId, propertyId, text, queryType, ownerType, appName, kgNodes = [] } = data;
  const localizedText = String(getLocalizedPayloadText(text ?? ""));

  const myHeaders = getGraphQLHeaders();

  const kgNodesString = kgNodes
    .map((node) => {
      const fields = Object.entries(node)
        .map(([key, value]) => `${key}: "${String(value).toLowerCase().replace(/"/g, '\\"')}"`)
        .join(", ");

      return `{ ${fields} }`;
    })
    .join(", ");

  const graphqlQuery = `
    mutation {
      createHayleyMessage(input: {
        conversationId: "${conversationId}",
        propertyId: "${String(propertyId)}",
        text: "${localizedText.replace(/"/g, '\\"')}",
        queryType: "${queryType}",
        ${ownerType ? `ownerType: "${ownerType}",` : ""}
        appName: "${appName}",
        kgNodes: [ ${kgNodesString} ]
      }) {
        hayleyMessage {
          id
          text
          questionId
          sequenceId
          ownerType
          queryType
          propertyId
          propertyName
          orgId
          orgName
          conversation {
            id
            messagesCount
          }
        }
      }
    }
  `;

  const graphql = JSON.stringify({
    query: graphqlQuery,
    variables: {},
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions);
};

/** Result shape for a write that was deliberately not sent. */
const notSentResult = (reason) => Promise.resolve({ json: () => Promise.resolve({ [reason]: true }) });

/**
 * Write one message to the conversation transcript, creating the conversation first when this
 * session does not have one yet (see ensureConversationId).
 *
 * Callers consume this as `.then((res) => res.json())`, so every branch — sent, ignored or
 * skipped — resolves to something carrying a `json()`.
 */
export const createHayleyMessage = (data, ignore = false) => {
  if (ignore) {
    return notSentResult("ignored");
  }

  if (data?.conversationId) {
    return postHayleyMessage(data);
  }

  return ensureConversationId().then((conversationId) => {
    if (!conversationId) {
      // Creation failed and was already reported. Skipping beats sending conversationId:"" for
      // the CRM to reject with "Conversation not found".
      return notSentResult("skipped");
    }

    return postHayleyMessage({ ...data, conversationId });
  });
};

export const sendHayleyMessage = ({ text, queryType, ownerType, kgNodeValue }) => {
  if (!text) {
    return;
  }

  const propertyId = store.getState().property.property_id || "";

  const kgNodes = queryType === "question" && kgNodeValue ? [{ l0_subject: kgNodeValue }] : [];

  // conversationId is intentionally omitted: createHayleyMessage resolves it, creating the
  // conversation when this session has none. Passing `""` is what lost transcripts before.
  createHayleyMessage({
    propertyId,
    text: getLocalizedPayloadText(text),
    queryType,
    ownerType,
    appName: "HayleyChat",
    kgNodes,
  })
    .then((res) => res.json())
    // fire-and-forget — caller does not consume the parsed response
    .catch((err) => {
      console.warn("[sendHayleyMessage] failed:", err?.message || err);
    });
};

const HARDCODED_SPANISH_PAYLOADS = {
  "Schedule a Tour": "Programe un recorrido",
  "Schedule a tour": "Programe un recorrido",
  "Contact Me": "Contáctame",
  "Contact me": "Contáctame",
  Cancel: "Cancelar",
  Yes: "Sí",
  rescheduleTour: "Reprogramar recorrido",
};

export const getLocalizedPayloadText = (value, forceSpanish = false) => {
  if (typeof value !== "string") {
    return value;
  }

  const isSpanish = forceSpanish || store?.getState()?.property?.isSpanishLanguage || i18n.language === "sp";

  if (!isSpanish) {
    return value;
  }

  if (HARDCODED_SPANISH_PAYLOADS[value]) {
    return HARDCODED_SPANISH_PAYLOADS[value];
  }

  const translated = t(value);

  return translated && translated !== value ? translated : value;
};

export const createItemRecord = (propertyId, sessionId, toolName, entryIdParam) => {
  const myHeaders = getGraphQLHeaders();

  const tool = ITEM_RECORD_TOOL_ALIASES[toolName] ?? toolName;

  const clickAt = moment().format("YYYY-MM-DD h:mm:ss");

  const urlParams = new URLSearchParams(window.location.search);
  const isJAI = urlParams.get("isjai");

  const fallbackEntryId = store?.getState()?.property?.entry_id || localStorage.getItem(`${propertyId}-entry_id`) || "";
  const entryId = entryIdParam || fallbackEntryId;

  const graphql = JSON.stringify({
    query: `mutation{\n  createHayleyItemRecord(input: {\n    propertyId: "${propertyId}",\n   sessionId: "${sessionId}"\n    toolName: "${tool}",\n   clickAt: "${clickAt}",\n   entryId: "${entryId}",\n   propertyWebsite: ${!isJAI}\n   }) {\n  success \n}\n}`,
    variables: {},
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions, { silent: true });
};

export const conversationFeedBack = (messageId, like) => {
  const myHeaders = getGraphQLHeaders();

  const graphql = JSON.stringify({
    query: `mutation{\n  saveHayleyMessageFeedback(input: {\n    id: "${messageId}",\n   feedback: "${
      like || ""
    }"\n   }) {\n  hayleyMessage{\n    text\n    feedback\n    ownerType\n    queryType\n      propertyId\n    propertyName\n    orgId\n    orgName\n     conversation{\n      id\n    messagesCount\n    sessionId\n    entryId\n    }\n  }\n}\n}`,
    variables: {},
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: graphql,
    redirect: "follow",
  };

  return graphqlFetch(`${process.env.REACT_APP_CRM_BASE_URL}/graphql`, requestOptions, { silent: true });
};

export const getSubjectFromNextOptionType = (nextOptionType, kgNodeValue) => {
  const findHomeTypes = ["BedroomTypes", "PriceBrackets", "WhenMoveIn", "Units"];
  const feedbackTypes = ["LikeTour", "DisLikeTour", "SkipTour", "LikeContact", "DisLikeContact", "SkipContact"];

  switch (true) {
    case findHomeTypes.some((type) => nextOptionType.includes(type)):
      return "find_an_apartment";

    case nextOptionType === "Form4ScheduleTour" || nextOptionType === "PostScheduleTour":
      return "Schedule a tour";

    case nextOptionType === "Form4ContactUs" || nextOptionType === "PostContactUs":
      return "Contact me";

    case nextOptionType === "VisitResidentPortal":
      return "Visit Resident Portal";

    case feedbackTypes.some((type) => nextOptionType.includes(type)):
      return "Feedback";

    case nextOptionType === "DoIQualify":
      return "do_i_qualify";

    default:
      return kgNodeValue;
  }
};

export const getMascotColors = (mascotSettings) => {
  const primary = mascotSettings?.colors?.primary || "#26BBED";
  const secondary = mascotSettings?.colors?.secondary || "#333333";

  if (mascotSettings?.icon.includes("_0")) {
    return {
      backgroundFill: secondary,
      frontFill: primary,
    };
  } else if (mascotSettings?.icon.includes("_1")) {
    return {
      backgroundFill: primary,
      frontFill: secondary,
    };
  } else if (mascotSettings?.icon.includes("_2")) {
    return {
      backgroundFill: secondary,
      frontFill: "#FFFFFF",
    };
  } else if (mascotSettings?.icon.includes("_3")) {
    return {
      backgroundFill: primary,
      frontFill: "#FFFFFF",
    };
  } else if (mascotSettings?.icon.includes("_4")) {
    return {
      backgroundFill: primary,
      frontFill: "#FFFFFF",
    };
  } else if (mascotSettings?.icon.includes("_5")) {
    return {
      backgroundFill: "#FFFFFF",
      frontFill: primary,
    };
  } else if (mascotSettings?.icon.includes("_6")) {
    return {
      backgroundFill: primary,
      frontFill: "#555555",
    };
  } else if (mascotSettings?.icon.includes("_7")) {
    return { backgroundFill: "#555555", frontFill: "#FFFFFF" };
  }

  return {
    backgroundFill: secondary,
    frontFill: primary,
  };
};

export const renderMascot = (mascotSettings, position, flipped, customSize, isAnimated) => {
  // When mascotSettings exists but icon is absent, fall back to friendly_1 with default colors
  const effectiveSettings =
    mascotSettings && !mascotSettings.icon
      ? {
          ...mascotSettings,
          icon: "friendly_1",
          colors: {
            primary: mascotSettings?.colors?.primary || "#26BBED",
            secondary: mascotSettings?.colors?.secondary || "#333333",
          },
        }
      : mascotSettings;

  if (effectiveSettings?.icon?.includes("friendly")) {
    return (
      <FriendlyBot
        width={customSize ? customSize.width : position && position === "sticky-right" ? "32" : "80"}
        height={customSize ? customSize.height : position && position === "sticky-right" ? "32" : "72"}
        sWidth={customSize ? customSize.sWidth : position && position === "sticky-right" ? "30" : "72"}
        sHeight={customSize ? customSize.sHeight : position && position === "sticky-right" ? "14" : "18"}
        {...getMascotColors(effectiveSettings)}
        flipped={position === "bottom-left" || flipped}
        isAnimated={isAnimated}
      />
    );
  } else if (effectiveSettings?.icon?.includes("hayley")) {
    return (
      <TheHayley
        width={customSize ? customSize.width : position && position === "sticky-right" ? "32" : "80"}
        height={customSize ? 30 : position && position === "sticky-right" ? "26" : "72"}
        {...getMascotColors(effectiveSettings)}
        isAnimated={isAnimated}
      />
    );
  } else if (effectiveSettings?.icon?.includes("minimalist")) {
    return (
      <TheMinimalist
        width={customSize ? customSize.width : position && position === "sticky-right" ? "32" : "81"}
        height={customSize ? customSize.height : position && position === "sticky-right" ? "32" : "81"}
        {...getMascotColors(effectiveSettings)}
        flipped={position === "bottom-left" || flipped}
        isAnimated={isAnimated}
      />
    );
  }

  return null;
};

export const hayleyAnimation = (animationName) => {
  if (animationName === "pulse") {
    return "pulse";
  } else if (animationName === "pop") {
    return "pop";
  } else if (animationName === "bounce") {
    return "bounce";
  } else if (animationName === "wiggle") {
    return "wiggle";
  }

  return "none";
};

export const areQuickActionsEmpty = (mascotSettings, isTourSchedulingEnabled = true) =>
  getVisibleQuickActions(mascotSettings, isTourSchedulingEnabled).length === 0;

export const convertToTitleCase = (input, exceptions = ["the", "and", "an", "to", "a"]) => {
  if (!input) {
    return "";
  }

  let words = input.replace(/_/g, " ");
  words = words.toLowerCase().split(/\s+/);

  const titleWords = words.map((word, index) => {
    if (index === 0 || !exceptions.includes(word)) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }

    return word;
  });

  return titleWords.join(" ");
};

export const ACTION_KEYS = {
  find_a_home: "find_home",
  schedule_a_tour: "schedule_tour",
};

export const SHOW_ACTION_KEYS = {
  find_a_home: "findHome",
  schedule_a_tour: "scheduleTour",
  contact_us: "contactMe",
  photos: "photos",
  videos: "videos",
  community_info: "communityInfo",
  visit_resident_portal: "residentalPortal",
};

export const isQaEnvironment = () => {
  return process.env.REACT_APP_CRM_BASE_URL.includes("qa.hyly.us") || window.location.host.includes("localhost");
};

export const getDateString = (date) => {
  if (!moment(date).isValid() || !date) {
    return "- -";
  }

  if (moment(date).isBefore(moment().startOf("day"))) {
    return t("availableNow");
  }

  if (moment(date).isSame(moment(), "day")) {
    return t("fromToday");
  }

  if (moment(date).isSame(moment().add(1, "day"), "day")) {
    return t("fromTomorrow");
  }

  return t("fromDate", { date: moment(date).format("DD MMM YYYY") });
};

export const convertGifToPng = async (gifImageSource) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = gifImageSource;

    img.onload = function () {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      const staticImageData = canvas.toDataURL("image/png");

      resolve(staticImageData);
    };

    img.onerror = function () {
      reject(new Error("Failed to load the GIF image."));
    };
  });
};

export const checkTranslation = (arr) => {
  if (!arr?.length) {
    return [];
  }

  const res = arr.map((v) => {
    // Keep typed text as-is; t() used to change it (e.g. "yes" became "Sí").
    if (v?.isUserTyped) {
      return v;
    }

    const checkLoader = loaders.includes(v.message);

    let loaderMsg = null;

    if (checkLoader) {
      const spliting = t(v.message)?.split(" ");

      if (spliting.length > 1) {
        const displayParts = spliting.slice(0, -1);

        loaderMsg =
          `<div class="hayley-inline-block">${displayParts.join(" ")}</div>` +
          " " +
          `<div class="hayley-inline-block">${spliting[spliting.length - 1].replace("{{dotLoader}}", ` ${dotLoader}`)}</div>`;
      } else {
        loaderMsg = `<div class="hayley-inline-block">${spliting[spliting.length - 1].replace("{{dotLoader}}", ` ${dotLoader}`)}</div>`;
      }
    }

    const value = checkLoader ? loaderMsg : t(v.message);

    return { ...v, message: value };
  });

  return res;
};

export const addUTMScript = (noscriptString, scriptString, hytoursScript) => {
  if (noscriptString || scriptString || hytoursScript) {
    if (scriptString) {
      var script = document.createElement("script");
      script.src = "";
      let dataScript = scriptString.split("script>");
      let fun = dataScript[1].split(";</");
      script.innerHTML = fun[0];
      document.head.appendChild(script);
    }

    if (hytoursScript) {
      console.info("hytoursScript", hytoursScript);

      var hytoursScriptElement = document.createElement("script");
      const scriptRegex = /<script[^>]*>([\s\S]*?)<\/script>/g;

      const scriptMatches = [...hytoursScript.matchAll(scriptRegex)];

      let combinedScript = "";
      for (const match of scriptMatches) {
        combinedScript += match[1].trim();
      }
      hytoursScriptElement.textContent = combinedScript;
      document.head.appendChild(hytoursScriptElement);
    }

    if (noscriptString) {
      let rootDiv = document.getElementById("hayley-area");
      var noscript = document.createElement("noscript");
      let data = noscriptString.split("iframe");
      noscript.innerHTML = `<iframe` + data[1] + `iframe>`;
      document.body.insertBefore(noscript, rootDiv);
    }
  }
};

export const convertToCamelCase = (inputString) => {
  return inputString.replace(/_([a-z])/g, function (match, p1) {
    return p1.toUpperCase();
  });
};

const APPOINTMENT_TYPE_I18N_KEYS = {
  "In-Person Tour": "inPersonTour",
  "Live Video Tour": "liveVideoTour",
  "Self Guided Tour": "selfGuidedTour",
};

/**
 * Booking `appointmentType` stays English by contract — APPOINTMENT_TYPES matches on the exact
 * string — so anything user-facing has to be translated here. Unknown values pass through.
 */
export const formatAppointmentType = (appointmentType) => {
  const key = APPOINTMENT_TYPE_I18N_KEYS[appointmentType];

  return key ? `${t(key)} ${t("tour")}` : appointmentType;
};

// Builds the opening bubble from the config in force right now. Reads the store by default so
// non-React callers (restartChatHandle) and React callers agree on a single shape; pass the
// pieces explicitly when you already hold them.
//
// `type: "translated"` is recorded for parity with the other writers but is inert — the message
// reducers ignore it and run checkTranslation regardless, which is a no-op on resolved prose.
export const buildGreetingMessage = (overrides = {}) => {
  const state = store.getState();

  const mascotSettings = "mascotSettings" in overrides ? overrides.mascotSettings : state.mascotSetting?.mascotSettings;
  const chatbotTitleDetails = "chatbotTitleDetails" in overrides ? overrides.chatbotTitleDetails : state.property?.chatbotTitleDetails;
  const isMultiLanguage = "isMultiLanguage" in overrides ? overrides.isMultiLanguage : state.property?.isMultiLanguage;

  // Drop a title that belongs to the language we have just switched away from. Restarting the
  // chat in the few hundred ms before the refetched title lands would otherwise splice the old
  // locale's name into the new locale's sentence — "Hi I'm Título de prueba, your Virtual
  // Leasing Agent…". Falling back to the generic name keeps the sentence coherent; the real
  // title is filled in by refreshGreeting the moment it arrives.
  const titleLocale = chatbotTitleDetails?.locale;
  const titleIsCurrent = !titleLocale || titleLocale === getApiLocale();

  return {
    message: resolveGreetingText({
      firstMessage: mascotSettings?.firstMessage,
      name: titleIsCurrent ? chatbotTitleDetails?.chatbot_title : null,
      isMultiLanguage,
    }),
    speaker: "bot",
    type: "translated",
    createdAt: new Date().toISOString(),
    isInitMessage: true,
  };
};

export const restartChatHandle = () => {
  // Read current Redux state
  const state = store.getState();
  const isDisabled = state.optionBox.isDisabled;

  // Cancel all active requests if any are in progress
  if (isDisabled) {
    cancelAllRequests();
    store.dispatch(setIsDisabled(false));
  }

  const mascotSettings = state.mascotSetting.mascotSettings;
  const chatbotTitleDetails = state.property.chatbotTitleDetails;
  const isMultiLanguage = state.property.isMultiLanguage;
  const propertyId = state.property.property_id || "";

  // A restart ends the chat-adapter tour conversation too; a leftover context would make the
  // next guided form submit and cancel through the chat adapter.
  localStorage.removeItem(`${propertyId}-context`);
  localStorage.removeItem(`${propertyId}-agentHandOff`);

  // Reset UI state
  store.dispatch(updateFaq(false));
  store.dispatch(setShowLangChange(true));
  store.dispatch(setActiveOptionComp(null));
  store.dispatch(setShowAllOptions(false));
  store.dispatch(moveToTop());
  store.dispatch(resetSession());

  store.dispatch(restartChatMessages(buildGreetingMessage({ mascotSettings, chatbotTitleDetails, isMultiLanguage })));

  store.dispatch(setReplies(INITIAL_REPLIES));

  getNextOptions("Init")
    .then((res) => {
      const { nextReplies } = makeNextOptions("Init", res.data, res.extraData || undefined);

      store.dispatch(setReplies(nextReplies));
      store.dispatch(setOptionsTypeLogs([]));
    })
    .catch((err) => {
      if (!isManualCancellation(err)) {
        console.error("Error fetching next options after restart:", err);
      }
    });
};

export const getFormattedDisclaimer = (data) => {
  let parsedData = { title: "", message: "", message_es: "" };
  try {
    let cleaned = data.replaceAll("=>", ":");
    cleaned = cleaned.replace(/\\([^"\\bfnrtu0-9])/g, "$1");
    parsedData = JSON.parse(cleaned);
  } catch (err) {
    console.error("Failed to parse disclaimer:", err);
    reportHandledError(err, { area: "disclaimer", kind: "parse" });
  }

  return "title" in parsedData ? parsedData : { title: "", message: "", message_es: "" };
};

// export const errorReqReplies = () => {
//   const errorMessage = [{ message: "flowErrorMessage", speaker: "bot" }];

//   const allMessages = store.getState().messages.messages;

//   const findActiveMessage = allMessages?.find((v) => v.optionsType && v.optionsType === "Init");

//   const findKey = findActiveMessage?.key || null;

//   const replyOptions = [
//     // { ...RETRY_OPTION, data, nextOptionsType, message: messages },
//     { ...EXPLORE_MORE_OPTION, data: findKey },
//   ];

//   const reply = { ...RETRY_OPTION_REPLIES, options: replyOptions };

//   store.dispatch(addNewMessage(errorMessage));

//   store.dispatch(setReplies(reply));
// };

export const formatMessageEdit = (type = "Init", label = null, from = null) => {
  const propertyId = store.getState().property.property_id || "";
  const conversationID = store.getState().window.conversationID || "";

  const isTriggerHayleyMessage = type && HAYLEY_MESSAGE_EDIT_TYPES.includes(type) && from && HAYLEY_EXTRA_OPTION_SOURCES.includes(from);

  if (isTriggerHayleyMessage) {
    createHayleyMessage({
      conversationId: conversationID || "",
      propertyId,
      text: t(label) || "",
      queryType: "question",
      ownerType: "prospect",
      appName: "HayleyChat",
      kgNodes: [{ l0_subject: type === "BedroomTypes" ? "find_an_apartment" : t(label) }],
    })
      .then((res) => res.json())
      // fire-and-forget — caller does not consume the parsed response
      .catch((err) => {
        console.warn("[formatMessageEdit] createHayleyMessage failed:", err?.message || err);
      });
  }

  store.dispatch(setActiveOptionComp(null));
  store.dispatch(setShowAllOptions(false));

  getNextOptions(type)
    .then((res) => {
      const { nextReplies, nextBotMessages } = makeNextOptions(type, res.data, res.extraData || undefined);

      const labelMsg = label
        ? [{ message: label, createdAt: new Date(), speaker: "user", optionsType: defaultEditFormats.includes(type) ? "Init" : null }]
        : [];

      store.dispatch(filterEditMessages([...labelMsg, ...nextBotMessages]));

      const options = nextReplies?.options?.filter((o) => from !== o.key);

      store.dispatch(setReplies({ ...nextReplies, options }));
      store.dispatch(setOptionsTypeLogs(store.getState().replies.optionsTypeLogs?.slice(0, -1)));

      const allMsgs = [...labelMsg, ...nextBotMessages];
      const lastMsg = allMsgs[allMsgs.length - 1];

      if (((type === "Init" && (from === "formCancel" || from === "feedbackCancel")) || isTriggerHayleyMessage) && lastMsg) {
        createHayleyMessage({
          conversationId: conversationID,
          propertyId,
          text: t(lastMsg.message) || "",
          queryType: "answer",
          ownerType: "agent",
          appName: "HayleyChat",
          kgNodes: [],
        })
          .then((res) => res.json())
          // fire-and-forget — caller does not consume the parsed response
          .catch((err) => {
            console.warn("[formatMessageEdit] createHayleyMessage failed:", err?.message || err);
          });
      }
    })
    .catch((err) => {
      console.error("error formatMessageEdit", err);

      /** @todo Add error handling part */
    });
};

export const loadFaqChat = (ssid, pagination = { page: 1, per_page: 20 }, propertyId) => {
  return retrieveConversation(ssid, pagination, propertyId)
    .then((response) => response.json())
    .then((res) => {
      if (res?.data) {
        const messages = res.data?.retrieveHayleyConversation?.messages?.map((v, i) => {
          const speaker = v.queryType == "question" ? "user" : "hyly";

          const linksHandle = speaker == "hyly" ? detectAndGenerateLinks(v.text) : v.text;

          const message = {
            message: linksHandle,
            feedback: v.feedback || null,
            id: v.id || null,
            speaker,
            createdAt: v.createdAt,
          };

          return message;
        });

        return messages;
      }
    })
    .catch((err) => {
      console.error("error loadFaqChat", err);
      throw err;
    });
};

export const addOptionMsg = (type = "Init", message = []) => {
  store.dispatch(setActiveOptionComp(null));

  store.dispatch(addNewMessage([{ message: `typing`, speaker: "bot", createdAt: new Date() }]));
  store.dispatch(setIsDisabled(true));

  getNextOptions(type)
    .then((res) => {
      store.dispatch(removeAndAddMessages([]));

      if (res?.data?.floor_plans && !["UnitsAll", "UnitsByRoom", "Units"].includes(type)) {
        type = "UnitsAll";
      }

      const { nextReplies, nextBotMessages } = makeNextOptions(type, res.data, res.extraData || undefined);

      const options = nextReplies?.options;

      let duplicateMessages = [...message, ...nextBotMessages] || [];
      let newMessages = [];

      duplicateMessages?.forEach((msg) => {
        let message = { ...msg, createdAt: new Date().toISOString() };
        newMessages.push(message);
      });

      if (["unitView"].includes(nextReplies?.renderType)) {
        newMessages.push({
          key: compMsgKeys[nextReplies?.renderType] || nextReplies?.renderType,
          speaker: "comp",
          replies: nextReplies,
          createdAt: new Date().toISOString(),
        });
      }

      store.dispatch(setIsDisabled(false));
      store.dispatch(addNewMessage(newMessages));
      store.dispatch(setReplies({ ...nextReplies, options }));
      store.dispatch(setOptionsTypeLogs(store.getState().replies.optionsTypeLogs?.slice(0, -1)));
    })
    .catch((err) => {
      if (!isManualCancellation(err)) {
        store.dispatch(removeAndAddMessages([{ message: ERROR_MSG, speaker: "bot", subject: "", createdAt: new Date() }]));
        store.dispatch(setIsDisabled(false));
      }
    });
};

export const ALL_CHAT_SUBJECTS = [
  "agent_handoff",
  "do_i_qualify",
  "find_an_apartment",
  "schedule_a_tour",
  "schedule_inquiry",
  "cancel_inquiry",
  "reschedule_inquiry",
  "status_inquiry",
  "schedule_confirmation",
  "cancel_confirmation",
  "reschedule_confirmation",
  "photos",
  "videos",
  "resident_portal",
  "contact us",
  "positive_feedback",
  "negative_feedback",
  "unsatisfactory_answer",
  "schedule",
  "neighborhood",
  "neighborhood summary",
  "neighborhood_summary",
  "contact",
  "contact_summary",
  "embed_map_view",
  "esp_not_enabled",
];

export const NEGATIVE_SUBJECTS = [
  "negative_sentiment",
  "family_friendly_housing",
  "gender_inclusiveness",
  "community_security",
  "cultural_inclusivity",
  "accessibility_features",
  "prompt_injection",
  "seeking_clarification",
  "complaint_filing",
  "off_topic_questions",
  "unsatisfactory_answer",
];

// Returns a translation KEY for today/yesterday, or a formatted date otherwise.
// The caller renders it through t(): this feeds the floating scroll pill, whose value is
// set inside a scroll handler that a language switch never re-runs, so resolving here
// would freeze the label in the language active at scroll time. A formatted date is not a
// key, so t() passes it through unchanged. Mirrors the inline day separators in
// MessagesList, which build the same two labels the same way.
export const formatTimestamp = (timestamp) => {
  const today = moment().startOf("day");
  const messageDay = moment(timestamp).startOf("day");

  if (today.isSame(messageDay, "day")) {
    return "today";
  } else if (today.subtract(1, "days").isSame(messageDay, "day")) {
    return "yesterday";
  }

  return moment(timestamp).format("MMM DD, YYYY");
};

export const isMarkdown = (str) => {
  const markdownRegex = /(\s*[-_*]{3,}\s*)|(^#+\s)|(\[.*?\]\(.*?\))|(```[\s\S]*?```)|(`[^`]*?`)|(^>\s)|(^[-*+]\s)/gm;

  return markdownRegex.test(str);
};

export const extractUniqueDates = (data) => {
  const uniqueDates = new Set();

  data.forEach((item) => {
    const date = item.time_slot.split(" ")[0];
    uniqueDates.add(date);
  });

  return Array.from(uniqueDates);
};

export const filterByDate = (data, givenDate) => {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.filter((item) => {
    const date = moment(item.time_slot, "YYYY-MM-DD hh:mm:ss A").format("YYYY-MM-DD"); // Ensure consistent format for Safari compatibility

    return date === givenDate;
  });
};

export const findTimeSlot = (time, timeArray) => {
  const givenTime = moment(time, "YYYY-MM-DD hh:mm:ss A"); // Explicitly format the date

  const foundSlot = timeArray.find((slot) => {
    const slotTime = moment(slot.time_slot, "YYYY-MM-DD hh:mm:ss A");

    return givenTime.isSame(slotTime);
  });

  return foundSlot ? foundSlot.time_slot : "";
};

export const getAmPmSlotsSeparated = (data) => {
  const amTimeSlots = data.filter(({ value }) => moment(value, "YYYY-MM-DD hh:mm:ss A").format("a") === "am"); // Fix formatting string for Safari
  const pmTimeSlots = data.filter(({ value }) => moment(value, "YYYY-MM-DD hh:mm:ss A").format("a") === "pm");

  return {
    morning: amTimeSlots,
    afternoon: pmTimeSlots,
  };
};

export const getFormattedTimeSlots = (data) => {
  return data.map((slot) => ({
    label: moment(slot.time_slot, "YYYY-MM-DD hh:mm:ss A").format("h:mm a"), // Specify format for consistency
    value: slot.time_slot,
  }));
};

export function findTourData(availableSlots, key) {
  for (let obj of availableSlots) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return obj[key];
    }
  }

  return null;
}

export function mergeData(newData, oldData) {
  // Define the set of keys that should be merged
  const keysToMerge = new Set(["live_video_tour", "in_person_tour", "self_guided_tour"]);
  const merged = {};

  // Iterate over oldData and store relevant keys in the merged object
  oldData.forEach((obj) => {
    Object.keys(obj).forEach((key) => {
      if (keysToMerge.has(key)) {
        merged[key] = obj[key];
      }
    });
  });

  // Iterate over newData and override relevant keys in the merged object
  newData.forEach((obj) => {
    Object.keys(obj).forEach((key) => {
      if (keysToMerge.has(key)) {
        merged[key] = obj[key];
      }
    });
  });

  // Convert the merged object back to an array of objects
  return Object.entries(merged).map(([key, value]) => ({ [key]: value }));
}

export const getBookingDateTime = (booking) => booking?.slot?.datetime || booking?.start_at || "";

export const TOUR_TYPE_ORDER = ["in_person_tour", "live_video_tour", "self_guided_tour"];

// Returns the AMA booking failure reason, or "" when the booking went through (null / "NONE").
export const getAmaBookingFailureCause = (res) => {
  if (res?.data?.retrieval_type !== "spf") {
    return "";
  }

  const spfQueryResponse = res?.data?.context?.spf_query_response;
  const cause = spfQueryResponse && !Array.isArray(spfQueryResponse) ? spfQueryResponse.bookingFailureCause : null;

  return cause && cause !== "NONE" ? cause : "";
};

export const getAmaSlotList = (spfQueryResponse) => {
  if (Array.isArray(spfQueryResponse)) {
    return spfQueryResponse;
  }

  if (Array.isArray(spfQueryResponse?.available_slots)) {
    return spfQueryResponse.available_slots;
  }

  return [];
};

const TOUR_SLOT_FORMAT = "YYYY-MM-DD hh:mm:ss A";
const PREFERRED_TIME_FORMATS = ["HH:mm", "HH:mm:ss", "H:mm", "h:mm A", "h:mm a", "hh:mm A", "hh:mm a"];

// "In-Person Tour" / "Live Video Tour" / "Self-Guided Tour" / "in_person_tour" → TOUR_TYPE_ORDER key, else "".
export const toTourTypeKey = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  if (TOUR_TYPE_ORDER.includes(value)) {
    return value;
  }

  if (APPOINTMENT_TYPES[value]) {
    return APPOINTMENT_TYPES[value];
  }

  const normalized = value.toLowerCase().replace(/[^a-z]/g, "");

  return TOUR_TYPE_ORDER.find((type) => type.replace(/_/g, "") === normalized) || "";
};

// Normalises a chat-adapter time ("11:00", "11:00:00", "11:00 AM") to "HH:mm", else "".
const toPreferredTime = (value) => {
  if (!value) {
    return "";
  }

  const parsed = moment(String(value).trim(), PREFERRED_TIME_FORMATS, true);

  return parsed.isValid() ? parsed.format("HH:mm") : "";
};

// What the user asked for, read only from structured fields of the chat-adapter reply:
//   tour type:   spf.tourType → booking.appointmentType → first enabled type
//   date + time: spf.preferredDate/preferredTime when a date is given, else the booking's slot.
// Date and time always come from the same source so a new date is never paired with the old time.
export const resolveAmaTourPreference = ({ spfContext, spfQueryResponse, enabledTypes } = {}) => {
  const booking = spfQueryResponse && !Array.isArray(spfQueryResponse) ? spfQueryResponse.booking : null;
  const isEnabled = (type) => !!type && !!enabledTypes?.[type];

  const requestedType = [toTourTypeKey(spfContext?.tourType), toTourTypeKey(booking?.appointmentType)].find(isEnabled);
  const tourType = requestedType || TOUR_TYPE_ORDER.find(isEnabled) || "";

  let date = "";
  let time = "";
  const preferredDate = moment(spfContext?.preferredDate || "", "YYYY-MM-DD", true);

  if (preferredDate.isValid()) {
    date = preferredDate.format("YYYY-MM-DD");
    time = toPreferredTime(spfContext?.preferredTime);
  } else {
    // parseZone keeps the property's wall-clock time ("08:30-04:00" stays 08:30), which is how
    // the slot list is written; converting to the visitor's timezone would shift it.
    const bookedAt = moment.parseZone(getBookingDateTime(booking), moment.ISO_8601);

    if (bookedAt.isValid()) {
      date = bookedAt.format("YYYY-MM-DD");
      time = bookedAt.format("HH:mm");
    }
  }

  return { tourType, date, time };
};

// Picks the slot to preselect inside ONE tour type's slots (other types are never consulted):
//   date + time found → that slot; date found only → first time on that date; else the first slot.
export const pickPreferredSlot = (slotData, { date, time } = {}) => {
  if (!Array.isArray(slotData) || !slotData.length) {
    return null;
  }

  const dates = extractUniqueDates(slotData);
  const isDateOffered = !!date && dates.includes(date);
  const chosenDate = isDateOffered ? date : dates[0];
  const daySlots = filterByDate(slotData, chosenDate);

  if (!daySlots.length) {
    return null;
  }

  // The time is only matched on the requested date — on a fallback date it would be a coincidence.
  const matched = isDateOffered && time ? daySlots.find((slot) => moment(slot.time_slot, TOUR_SLOT_FORMAT).format("HH:mm") === time) : null;

  return { date: chosenDate, time_slot: (matched || daySlots[0]).time_slot };
};

export const handleTourResponse = (res, isAvailableDates = false, tourType = "", time) => {
  const state = store.getState();
  res = res ?? [];
  const isSpfRetrieval = res?.data?.retrieval_type === "spf";
  const spfContext = isSpfRetrieval ? res.data?.context?.spf || {} : {};
  const availableSlots = isAvailableDates ? res : isSpfRetrieval ? getAmaSlotList(res.data?.context?.spf_query_response) : [];
  // Optional-chained on the guided branch too: every current caller guards against an empty
  // array first, but an unguarded res[0] here is the same trap that firstTimeSlots[0] was.
  const slotData = isAvailableDates ? res?.[0]?.[tourType] : findTourData(availableSlots, tourType || spfContext?.tourType);
  const appSSID = state.window.appSSID || "";
  const propertyId = state.property.property_id || "";
  const oldAllData = state.scheduleTour.allData || [];
  const resolvedTourType = tourType || spfContext?.tourType || "";
  const preference = selectTourPreference(state);

  const bailNoSlots = () => {
    store.dispatch(setAvailableSlots(null));
    store.dispatch(setAvailableDates([]));
    store.dispatch(setSelectedDate(""));
    store.dispatch(setSlotsLoading(false));

    const requestOption = {
      tag: "NO_SAT_SLOT",
      metric: "Surprise",
      propertyId: propertyId,
      sessionId: appSSID,

      appSubsection: "No slot in SAT",
    };
    createEventSignal(requestOption);
  };

  if (!slotData) {
    bailNoSlots();

    return;
  }

  const dates = extractUniqueDates(slotData);
  // The chat-adapter does not reliably lead with the requested slot, so the user's own request
  // (see resolveAmaTourPreference) is matched here — but only while loading that same tour type.
  const hasPreference = !!preference.tourType && preference.tourType === resolvedTourType && !!preference.date;
  const preferred = hasPreference ? pickPreferredSlot(slotData, preference) : null;
  const selectedDate = preferred?.date || dates[0];
  const selectedDaySlots = filterByDate(slotData, selectedDate);

  if (!selectedDaySlots.length) {
    bailNoSlots();

    return;
  }

  let selectedTimeSlot = preferred?.time_slot || selectedDaySlots[0].time_slot;

  if (time && !preferred) {
    const tSlot = findTimeSlot(time, selectedDaySlots);

    if (tSlot) {
      selectedTimeSlot = tSlot;
    }
  }

  const properTimeSlots = getAmPmSlotsSeparated(getFormattedTimeSlots(selectedDaySlots));
  // mergeData(newData, oldData): the second argument is overridden by the first, so fresh slots go first.
  const mergedNewOldData = mergeData(isAvailableDates ? res : availableSlots, oldAllData);

  store.dispatch(setAllData(mergedNewOldData));
  store.dispatch(setAvailableDates(dates));
  store.dispatch(setAvailableSlots({ date: dates, time: properTimeSlots }));
  store.dispatch(setSelectedOptions({ tourType: resolvedTourType, date: selectedDate, time: selectedTimeSlot }));

  store.dispatch(setSelectedDate(""));

  if (!isAvailableDates) {
    const preferredDate = spfContext?.preferredDate || "";

    const labelTime = spfContext?.preferredTime
      ? moment(`${preferredDate} ${spfContext?.preferredTime}`, "YYYY-MM-DD HH:mm").format("YYYY-MM-DD h:mm:ss A")
      : "";

    // Merge, never replace: setLabelItems assigns the whole object, and blowing it away here
    // dropped bedroom_type / desired_move_in / price, which the guest-card payload reads later.
    store.dispatch(setLabelItems({ ...(store.getState().params.label || {}), appointment_type: tourType || "", date: preferredDate, time: labelTime }));
  } else {
    store.dispatch(setSlotsLoading(false));
  }

  store.dispatch(setShouldFetchSlots(false));
};

export const applyAmaTourContext = ({ res, resContext, spfQueryResponse, availableSlots, propertyId }) => {
  const state = store.getState();

  const enabledTypes = {
    in_person_tour: state.property.in_person_tour,
    live_video_tour: state.property.live_video_tour,
    self_guided_tour: state.property.self_guided_tour,
  };

  const hasEnabledType = TOUR_TYPE_ORDER.some((type) => enabledTypes[type]);

  if (hasEnabledType) {
    const preference = resolveAmaTourPreference({ spfContext: resContext, spfQueryResponse, enabledTypes });
    const typeSlots = Array.isArray(availableSlots) ? findTourData(availableSlots, preference.tourType) : null;

    // A new request starts from a clean slot state, so a previous reply's slots can never be
    // mixed into this one. The preference is set after the reset, which would otherwise clear it.
    store.dispatch(resetScheduleTourState());
    store.dispatch(setTourPreference(preference));

    if (Array.isArray(typeSlots) && typeSlots.length > 0) {
      // Deferred by a tick: the caller is still mid-dispatch and the form has not mounted yet.
      setTimeout(() => {
        handleTourResponse(res, false, preference.tourType);
      }, 0);

      store.dispatch(setShouldFetchSlots(false));
    } else {
      // No slots for the requested type in this reply (none at all, or only other types'):
      // the form fetches that type from tour_availability and applies the same preference.
      store.dispatch(setShouldFetchSlots(true));
    }
  }

  const bookingDetails = spfQueryResponse?.booking ?? {};

  if (resContext?.booking_found && !resContext?.required_info) {
    if (bookingDetails && Object.keys(bookingDetails).length > 0) {
      localStorage.setItem(`${propertyId}-booking_details`, JSON.stringify(bookingDetails));
      localStorage.setItem(`${propertyId}-bookingId`, bookingDetails?.id);
    }
  }

  store.dispatch(setTourAction(resContext?.tourAction));
};

export const getFormattedDate = (date) => {
  // "From Today"/"From Tomorrow" arrive as English API copy. Stripping "From"
  // left the English noun behind, so resolve each to its own key instead.
  if (date.includes("From Today")) {
    return t("today");
  }

  if (date.includes("From Tomorrow")) {
    return t("tomorrow");
  }

  if (date.includes("From")) {
    const options = { year: "numeric", month: "short", day: "numeric" };
    const formattedDate = new Date(date.replace("From ", "").replace(/-/g, "/")).toLocaleDateString("en-US", options); // Replace hyphens to ensure compatibility

    return formattedDate;
  }

  return date;
};

// Error bubbles are stored as *translated text*, not as the ERROR_MSG key: checkTranslation()
// runs t() inside the messages-slice reducers before the message reaches the store. So a bare
// `message !== ERROR_MSG` check never matches and error copy leaks into the history we send to
// the ML adapter. Match all three forms it can take:
//   1. the raw key — paths that skip checkTranslation (removeAndAddNoTransMessages,
//      formatMessages with type:"translated"), or a boot where translations failed to load;
//   2. LEGACY_ERROR_MSG — pre-migration copy still sitting in older localStorage history;
//   3. the flowErrorMessage copy in *every* loaded language, since a conversation can switch
//      language midway and history may hold either rendering.
export const isErrorMessage = (text) => {
  if (!text) {
    return false;
  }

  if (text === ERROR_MSG || text === LEGACY_ERROR_MSG) {
    return true;
  }

  const bundles = i18n?.store?.data || {};

  return Object.values(bundles).some((bundle) => bundle?.translation?.[ERROR_MSG] === text);
};

const collapseWhitespace = (value) => (typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "");

export const isLoaderMessage = (text) => {
  if (!text || typeof text !== "string") {
    return false;
  }

  if (loaders.includes(text)) {
    return true;
  }

  if (text.includes("bounce-dot")) {
    return true;
  }

  const plain = collapseWhitespace(containsHtmlTags(text) ? htmlToPlainText(text) : text);

  if (!plain) {
    return false;
  }

  const bundles = i18n?.store?.data || {};

  return Object.values(bundles).some((bundle) =>
    loaders.some((key) => {
      const template = bundle?.translation?.[key];

      return template ? collapseWhitespace(template.replace("{{dotLoader}}", "")) === plain : false;
    })
  );
};

export function getLastConversations(messages, maxUserMessagesPerConversation = 5) {
  const filteredMessages = (messages || [])
    // Typed text is never an error or loader bubble, even if it matches one.
    .filter((m) => (m.isUserTyped || (!isErrorMessage(m.message) && !isLoaderMessage(m.message))) && !isSubmittedFormDetailsMessage(m))
    .map((m) => ({
      ...m,
      // Typed text is already plain; don't strip anything from it.
      message: m.isUserTyped ? m.message : normalizeHistoryMessage(m.message, m.isHtml),
    }));
  let userMessageCount = 0;
  let totalMessagesCount = 0;

  for (let i = filteredMessages.length - 1; i >= 0; i--) {
    const message = filteredMessages[i];
    totalMessagesCount++;

    if (message.speaker === "user") {
      userMessageCount++;
    }

    if (userMessageCount >= maxUserMessagesPerConversation) {
      return filteredMessages.slice(-totalMessagesCount);
    }
  }

  return filteredMessages.slice(-totalMessagesCount);
}

const isSubmittedFormDetailsMessage = (message) => message?.speaker === "user" && message?.isHtml === true;

const normalizeHistoryMessage = (message, isHtml = false) => {
  if (typeof message !== "string") {
    return message;
  }

  return isHtml || containsHtmlTags(message) ? htmlToPlainText(message) : message;
};

const containsHtmlTags = (value) => /<\/?[a-z][\s\S]*>/i.test(value);

// Escape text for HTML. Uses the entities htmlToPlainText decodes, so history gets the original back.
export const escapeHtml = (str) =>
  String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const htmlToPlainText = (html) => {
  if (typeof html !== "string") {
    return html;
  }

  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export const getLocalStorageContextDetails = (propertyId) => {
  try {
    const storedContext = localStorage.getItem(`${propertyId}-context`);
    const parsedContext = storedContext ? JSON.parse(storedContext) || [] : [];
    const firstItem = parsedContext?.[0] || {};

    return {
      contextList: parsedContext,
      currentTourAction: firstItem?.tourAction,
      currentBooking: firstItem?.booking_found,
      currentRequiredInfo: firstItem?.required_info,
      currentSubject: firstItem?.subject,
      currentNeedInfo: firstItem?.need_info,
    };
  } catch (error) {
    console.error(`Failed to parse localStorage context for propertyId: ${propertyId}`, error);
    reportHandledError(error, { area: "conversation_context", kind: "parse" });

    return {
      contextList: [],
      currentTourAction: undefined,
      currentBooking: undefined,
      currentRequiredInfo: undefined,
      currentSubject: undefined,
      currentNeedInfo: undefined,
    };
  }
};

export const parseEndDate = (dateText) => {
  if (!dateText) {
    return "";
  }

  const trimmed = dateText.trim();

  // 1. Strict slash formats: MM/DD/YYYY and single-digit variants
  const slashFormats = ["MM/DD/YYYY", "M/D/YYYY", "MM/D/YYYY", "M/DD/YYYY"];

  for (const fmt of slashFormats) {
    const d = moment(trimmed, fmt, true);

    if (d.isValid()) {
      return d.format("MM/DD/YYYY");
    }
  }

  // 2. ISO format: YYYY-MM-DD
  const iso = moment(trimmed, "YYYY-MM-DD", true);

  if (iso.isValid()) {
    return iso.format("MM/DD/YYYY");
  }

  // 3. Month-name first: extract "Feb 10, 2026" / "February 10 2026" / "Feb 10,2026"
  //    from any surrounding text (e.g. "Ends On Feb 10, 2026")
  const monthFirstMatch = trimmed.match(/([A-Za-z]{3,9}\.?\s+\d{1,2}[,\s]+\d{4})/);

  if (monthFirstMatch) {
    const candidate = monthFirstMatch[1].replace(/\s+/g, " ").trim();

    const parsed = moment(
      candidate,
      ["MMM D, YYYY", "MMM DD, YYYY", "MMMM D, YYYY", "MMMM DD, YYYY", "MMM D YYYY", "MMM DD YYYY", "MMMM D YYYY", "MMMM DD YYYY"],
      false
    );

    if (parsed.isValid()) {
      return parsed.format("MM/DD/YYYY");
    }
  }

  // 4. Day-first: extract "10 Feb 2026" / "10 February 2026"
  const dayFirstMatch = trimmed.match(/(\d{1,2}\s+[A-Za-z]{3,9}\.?\s+\d{4})/);

  if (dayFirstMatch) {
    const candidate = dayFirstMatch[1].replace(/\s+/g, " ").trim();
    const parsed = moment(candidate, ["D MMM YYYY", "DD MMM YYYY", "D MMMM YYYY", "DD MMMM YYYY"], false);

    if (parsed.isValid()) {
      return parsed.format("MM/DD/YYYY");
    }
  }

  return "";
};

export const formatCommonDates = (date, format = "MMM DD, YYYY") => {
  if (!date) {
    return null;
  }

  let parsedDate = null;

  // Handle cases where date contains "/"
  if (date.includes("/")) {
    const parts = date.split("/");
    const firstPart = parseInt(parts[0], 10);

    if (parts[0].length === 4) {
      // Year-first format: "YYYY/MM/DD"
      parsedDate = moment(date, "YYYY/MM/DD", true);
    } else if (parts[2] && parts[2].length === 2) {
      // 2-digit year: "DD/MM/YY" or "MM/DD/YY"
      parsedDate = firstPart > 12 ? moment(date, "DD/MM/YY", true) : moment(date, "MM/DD/YY", true);
    } else {
      parsedDate = firstPart > 12 ? moment(date, "DD/MM/YYYY", true) : moment(date, "MM/DD/YYYY", true);
    }
  } else {
    // General case: Try all formats
    const formats = [
      "YYYY-MM-DD",
      "DD-MM-YYYY",
      "MM-DD-YYYY",
      "DD MMM YYYY",
      "DD MMMM YYYY",
      "MMM DD, YYYY",
      "MMM D, YYYY",
      "MMMM DD, YYYY",
      "MMMM D, YYYY",
      "YYYY.MM.DD",
      "DD.MM.YYYY",
      "MM.DD.YYYY",
      "DD MMM, YYYY",
      "D MMM YYYY",
      "D MMMM YYYY",
      "YYYY MMM DD",
      "YYYY MMMM DD",
      "DD MMM YY",
      "DD-MM-YY",
      "DD.MM.YY",
    ];
    parsedDate = moment(date, formats, true);
  }

  return parsedDate.isValid() ? parsedDate.format(format) : null;
};

export function getUrlParam(key) {
  const urlParams = new URLSearchParams(window.location.search);

  return urlParams.get(key);
}

// Move specified tour type element as the first element of array
export const moveTourTypeToFront = (allData, clickedTourType) => {
  const tourTypeItem = allData.find((item) => item[clickedTourType]); // Find the matching item

  if (!tourTypeItem) {
    return allData; // If not found, return the array as is
  }

  // Remove the tourTypeItem from the array and put it at the front
  const filteredData = allData.filter((item) => item !== tourTypeItem);

  return [tourTypeItem, ...filteredData]; // Put the found item first and concatenate the rest
};

export const updateConversationEntryId = (entryId, email, { firstName = "", lastName = "", phoneNumber = "" } = {}) => {
  store.dispatch(setEntryId(entryId));
  store.dispatch(setEmail(email));

  updateHayleyConversation(entryId, { firstName, lastName, phoneNumber, email });
};

// If it's a JSON array string, it extracts the first value.
export const getFirstBedroomTypeQuery = (queryParams) => {
  if (!queryParams) {
    return "";
  }

  const urlParams = new URLSearchParams(queryParams);
  const rawBedroomType = urlParams.get("bedroom_type");

  let bedroomType = rawBedroomType;

  try {
    if (rawBedroomType?.startsWith("[")) {
      bedroomType = JSON.parse(rawBedroomType)[0];
    }
  } catch {
    // Fail silently and use the original value
  }

  urlParams.delete("bedroom_type");

  if (bedroomType) {
    urlParams.append("bedroom_type", bedroomType);
  }

  return `?${urlParams.toString()}`;
};

export const resolveNeighborhoodData = (context, resSubject, spfQueryResponse) => {
  try {
    if (resSubject !== "neighborhood" && resSubject !== "neighborhood_summary") {
      return spfQueryResponse;
    }

    const ctx = context || {};
    const directVectors = ctx.relevant_vectors;
    const nestedVectors = ctx.neighborhood_summary_vectors?.relevant_vectors;
    const vectorsObject = ctx.neighborhood_summary_vectors;

    if (Array.isArray(directVectors) && directVectors.length > 0) {
      return directVectors;
    }

    if (Array.isArray(nestedVectors) && nestedVectors.length > 0) {
      return nestedVectors;
    }

    if (resSubject === "neighborhood" && vectorsObject !== null && typeof vectorsObject === "object" && !Array.isArray(vectorsObject)) {
      const { relevant_vectors: _rv, ...categoryEntries } = vectorsObject;

      return Object.keys(categoryEntries).length > 0 ? categoryEntries : [];
    }

    return [];
  } catch (_err) {
    return resSubject === "neighborhood" || resSubject === "neighborhood_summary" ? [] : spfQueryResponse;
  }
};

export const resolveNeighborhoodSummary = (context, neighborhoodSummary, responseText) => {
  return context?.summary || neighborhoodSummary || responseText || "";
};

// Safety net for the branches that hand a reply to OptionsBox and leave the loading
// state for the routing to clear. OptionsBox cancels this the moment it starts
// routing, so it only fires when routing never starts at all — otherwise the UI
// would hang forever with no error. See docs/ama-routing-hang-root-cause.md.
//
// The gap it guards (dispatch -> render -> passive effect) is normally under 16ms, so
// this is deliberately generous. Firing early is the costlier mistake: it cancels the
// routing, leaving the user with the answer text but no buttons/form. Firing late only
// delays recovery of a flow that would otherwise have hung forever.
export const AMA_ROUTING_WATCHDOG_MS = 8000;

let amaRoutingWatchdog = null;

export const clearAmaRoutingWatchdog = () => {
  if (amaRoutingWatchdog) {
    clearTimeout(amaRoutingWatchdog);
    amaRoutingWatchdog = null;
  }
};

export const armAmaRoutingWatchdog = (fallbackMessage, messageId) => {
  clearAmaRoutingWatchdog();

  amaRoutingWatchdog = setTimeout(() => {
    amaRoutingWatchdog = null;

    // Optional-chained because this runs detached in a timer, where a throw would be
    // uncaught. A null amaReply means routing already consumed the reply.
    if (!store?.getState()?.replies?.amaReply) {
      return;
    }

    // Nobody routed it — surface the answer and unlock the UI.
    store.dispatch(setAMAReply(null));
    store.dispatch(removeAndAddMessages([{ message: fallbackMessage || ERROR_MSG, id: messageId, speaker: "faqBot", createdAt: new Date() }]));
    store.dispatch(setReplies(INITIAL_REPLIES));
    store.dispatch(setIsDisabled(false));
  }, AMA_ROUTING_WATCHDOG_MS);
};

export const handleAMAResponse = (res, skipLanguageCheck = false) => {
  if (!res?.data || typeof res.data !== "object" || Array.isArray(res.data)) {
    store.dispatch(removeAndAddMessages([{ message: ERROR_MSG, speaker: "bot", subject: "", createdAt: new Date() }]));
    store.dispatch(setIsDisabled(false));

    return;
  }

  const state = store.getState();
  const isPerformanceReport = state.replies.isPerformanceReport;
  const isPriceFilter = state.property.isPriceFilter;
  const propertyId = state.property.property_id || "";
  const SSID = state.window.appSSID || "";
  const apiKey = state.property.api_key || "";
  const isMultiLanguageSupported = !!state.property.isMultiLanguage;
  const isPhoneNumberRequired = state.property.isPhoneNumberRequired;

  async function getScheduleStatus() {
    const url = `${process.env.REACT_APP_API_BASE_URL}/apartments/schedule_status?property_id=${propertyId}&api_key=${apiKey}&entry_session_id=${SSID}`;

    try {
      const response = await axios.get(url);

      return response.data;
    } catch (error) {
      console.error("Error fetching schedule status:", error);
      throw error;
    }
  }

  createItemRecord(propertyId, SSID, "faqs");

  const LANG_KEYS = {
    es: "sp",
    en: "en",
  };

  const lang = LANG_KEYS[res?.data?.language];

  if (lang && lang !== i18n.language && !skipLanguageCheck && isMultiLanguageSupported) {
    const isSpanish = lang === "sp";

    if (isSpanish) {
      store.dispatch(
        removeAndAddMessages([
          {
            message: "detectedSpanish",
            speaker: "bot",
            createdAt: new Date().toISOString(),
          },
        ])
      );

      store.dispatch(setReplies(LANG_CHANGE_REPLIES));
      store.dispatch(setIsDisabled(false));
      store.dispatch(setAMAReply(null));
      store.dispatch(setAMAResponse(res));

      return;
    }
  }

  let resSubject;

  if (res?.data?.intent === "availability_inquiry") {
    resSubject = "find_an_apartment";
  } else if (res?.data?.sub_intent === "neighborhood_specific") {
    resSubject = "neighborhood";
  } else {
    resSubject = res?.data?.sub_intent || "";
  }

  const isSpfRetrieval = res?.data?.retrieval_type === "spf";
  const resContext = isSpfRetrieval ? res?.data?.context?.spf || {} : {};
  const spfQueryResponse = isSpfRetrieval ? res?.data?.context?.spf_query_response || {} : {};
  const availableSlots = isSpfRetrieval ? getAmaSlotList(res?.data?.context?.spf_query_response) : [];

  // Neighborhood flows — unified source resolution across all neighborhood sub-intents
  const isNeighborhoodSubject = resSubject === "neighborhood" || resSubject === "neighborhood_summary";
  const neighborhoodData = resolveNeighborhoodData(res?.data?.context, resSubject, spfQueryResponse);

  const neighborhoodSummaryText = isNeighborhoodSubject
    ? resolveNeighborhoodSummary(res?.data?.context, res?.data?.neighborhood_summary, extractResponseMessage(res?.data?.response))
    : res?.data?.neighborhood_summary || "";

  let imageUrls;

  if (resSubject === "photos") {
    imageUrls = isSpfRetrieval ? spfQueryResponse || {} : {};
  } else {
    imageUrls = res?.data?.context?.images?.length > 0 ? JSON.parse(res?.data?.context?.images) : [];
  }

  if (SCHEDULE_TOUR_SUBJECTS.includes(resSubject)) {
    applyAmaTourContext({ res, resContext, spfQueryResponse, availableSlots, propertyId });
  }

  if (resSubject === "find_an_apartment") {
    const payloadT = { bedroom_type: `${resContext?.bedroomPreference}` };

    if (resContext?.priceRange) {
      payloadT["price"] = resContext?.priceRange.split("-");
    }

    if (resContext?.desiredMoveInDate) {
      payloadT["desired_move_in"] = resContext?.desiredMoveInDate;
    }

    localStorage.setItem(`${propertyId}-context`, JSON.stringify([resContext]));
    // Merge, not replace — setLabelItems assigns the whole object, so a bare payload here wiped
    // the contact fields the guest-card form had already collected.
    store.dispatch(setLabelItems({ ...(store.getState().params.label || {}), ...payloadT }));
  }

  let isUnitAvailable = resContext?.bedroomPreference && resContext?.desiredMoveInDate && spfQueryResponse?.floor_plans?.length > 0;

  if (isPriceFilter) {
    isUnitAvailable = isUnitAvailable && resContext?.priceRange;
  }

  // If intent is "apartment_inquiry" then don't ask follow ups, show the units straight up
  if (res?.data?.sub_intent === "apartment_inquiry") {
    isUnitAvailable = true;
  }

  if (isUnitAvailable) {
    store.dispatch(setSearchResult(spfQueryResponse?.search_result || false));
  }

  const isContact = resSubject.includes("contact") || resSubject.includes("contact_summary");
  const isAgentHandoff = res?.data?.agent_handoff?.agent_handoff_triggered;

  if (resSubject && (resSubject.includes("links") || resSubject.includes("location"))) {
    const responseMessage = extractResponseMessage(res?.data?.response);
    const hasShortUrl = /maps\.app\.goo\.gl|goo\.gl\/(maps|[A-Za-z0-9]+)/.test(responseMessage);
    const hasRegularMapsUrl = responseMessage.includes("google.com/maps") && !hasShortUrl;

    if (hasRegularMapsUrl) {
      resSubject = "embed_map_view";
    }
  }

  const agentHandoff = res?.data?.agent_handoff ?? {};

  const feeSummaryFlag = res?.data?.context?.spf?.fee_utilities_summary || false;

  // The fee summary travels to the chat bubble without a floor plan list, so any
  // scope name the API left blank has to be recovered here, while the search
  // response is still in hand. Named fees pass through untouched.
  const feeSummaryResponse = withResolvedScopeNames(res?.data?.context?.fee_summary_response || null, {
    floorPlans: spfQueryResponse?.floor_plans,
  });

  if (optionsBySubject[resSubject]) {
    const extractedResponseMessage = extractResponseMessage(res?.data?.response) || ERROR_MSG;

    store.dispatch(
      setAMAReply({
        description: extractedResponseMessage,
        message: extractedResponseMessage,
        id: res.data.message_id,
        summary: neighborhoodSummaryText,
        neighborhood: neighborhoodData,
        speaker: "faqBot",
        subject: resSubject,
        context: isSpfRetrieval ? (res.data.context?.spf ? [res.data.context.spf] : []) : [],
        createdAt: new Date(),
        available_tourTypes: res.data?.available_tourTypes,
        not_available: spfQueryResponse?.filter_excluded,
        follow_up: res.data?.follow_up,
        availableUnits: isUnitAvailable
          ? {
              ...spfQueryResponse,
              fee_disclaimer: spfQueryResponse?.fee_disclaimer,
            }
          : null,
        // Follow-up path (filters incomplete): surface the disclaimer when tmlp_enabled + fee_disclaimer.
        feeDisclaimer:
          resSubject === "find_an_apartment" && !isUnitAvailable && spfQueryResponse?.tmlp_enabled === true && spfQueryResponse?.fee_disclaimer
            ? spfQueryResponse.fee_disclaimer
            : null,
        images: imageUrls,
        photos: imageUrls,
        fee_utilities_summary: feeSummaryFlag,
        fee_summary_response: feeSummaryResponse,
      })
    );

    // The loader is deliberately left for OptionsBox to clear once it routes —
    // removeAndAddMessages strips the LAST message, so clearing it here would eat a
    // real message. Bound the wait instead.
    armAmaRoutingWatchdog(extractedResponseMessage, res.data.message_id);
  } else if (isAgentHandoff && !HANDOFF_EXCLUDED_SUBJECTS.includes(resSubject)) {
    if (agentHandoff?.need_info === "entry_id") {
      const isEmergencyMaintenance = resSubject === "emergency_maintenance" || (Array.isArray(resSubject) && resSubject.includes("emergency_maintenance"));

      const contactFormReplies = {
        checkStorage: true,
        disableResetBox: false,
        options: updatePhoneNumberOptional(FORM_4_CONTACT_US_OPTIONS, isPhoneNumberRequired),
        optionsType: "Form4ContactUs",
        title: "Enter First Name and Last Name",
        renderType: "multiInputView",
        // Handoff raised inside a tour conversation: its Cancel still tells the chat adapter.
        triggerMLOnCancel: !!resContext?.tourAction,
      };

      const message = isEmergencyMaintenance ? buildEmergencyMessage(store.getState().property.phone) : t("leasing_agent_will_contact");

      localStorage.setItem(`${propertyId}-agentHandOff`, JSON.stringify(agentHandoff));

      store.dispatch(setIsDisabled(false));

      store.dispatch(
        removeAndAddMessages([
          {
            message,
            speaker: "bot",
            createdAt: new Date().toISOString(),
          },
        ])
      );

      store.dispatch(setReplies(contactFormReplies));
    } else {
      store.dispatch(
        removeAndAddMessages([
          {
            message: extractResponseMessage(res?.data?.response) || "leasingAgentContactSoon",
            id: res.data.message_id,
            speaker: "faqBot",
            createdAt: new Date(),
          },
        ])
      );

      store.dispatch(setReplies(INITIAL_REPLIES));
      store.dispatch(setIsDisabled(false));
    }
  } else if (isContact) {
    const entryId = localStorage.getItem(`${propertyId}-entry_id`);

    if (entryId) {
      store.dispatch(
        removeAndAddMessages([
          {
            message: t("already_filled_contact_form"),
            id: res.data.message_id,
            speaker: "faqBot",
            createdAt: new Date(),
          },
        ])
      );

      store.dispatch(setReplies(INITIAL_REPLIES));
      store.dispatch(setIsDisabled(false));
    } else {
      store.dispatch(setIsDisabled(false));

      store.dispatch(
        removeAndAddMessages([
          {
            message: t("leasing_agent_will_contact"),
            speaker: "bot",
            createdAt: new Date().toISOString(),
          },
        ])
      );

      let rep = {
        checkStorage: true,
        disableResetBox: false,
        options: updatePhoneNumberOptional(FORM_4_CONTACT_US_OPTIONS, isPhoneNumberRequired),
        optionsType: "Form4ContactUs",
        title: "Enter First Name and Last Name",
        renderType: "multiInputView",
      };

      store.dispatch(setReplies(rep));
    }
  } else {
    store.dispatch(
      removeAndAddMessages([
        {
          message: extractResponseMessage(res?.data?.response) || ERROR_MSG,
          id: res.data.message_id,
          summary: neighborhoodSummaryText,
          neighborhood: neighborhoodData,
          speaker: "faqBot",
          subject: resSubject,
          createdAt: new Date(),
          available_tourTypes: res.data.available_tourTypes,
          follow_up: res.data?.follow_up,
          images: imageUrls,
          fee_utilities_summary: feeSummaryFlag,
          fee_summary_response: feeSummaryResponse,
        },
      ])
    );

    store.dispatch(setIsDisabled(false));
  }

  if (res?.data?.performance_logger?.[0]?.table && isPerformanceReport && reportIds.includes(propertyId)) {
    store.dispatch(addNewMessage([{ message: "performanceReportText", speaker: "bot", createdAt: new Date() }]));
    store.dispatch(addNewMessage([{ key: "report", speaker: "comp", compData: res?.data?.performance_logger?.[0]?.table, createdAt: new Date() }]));
  }

  // If subject is an array then take first element
  const subject = Array.isArray(resSubject) ? resSubject[0] : resSubject;

  // Increment only if `subject` is NOT in `FOLLOW_UP_KEYS`
  let hasFollowUpKey = FOLLOW_UP_KEYS.includes(subject);
  hasFollowUpKey = true; // disable follow up temporarily
  let storedCount = localStorage.getItem(`${propertyId}-follow-up-count`);

  if (!hasFollowUpKey) {
    storedCount = (parseInt(storedCount) || 0) + 1;
    localStorage.setItem(`${propertyId}-follow-up-count`, storedCount);
  }

  if (parseInt(storedCount) >= 3 && store.getState().property.enabled_schedule_a_tour && !ALL_CHAT_SUBJECTS.filter((o) => o !== "schedule").includes(subject)) {
    getScheduleStatus().then((alreadyScheduled) => {
      if (alreadyScheduled === false) {
        localStorage.setItem(`${propertyId}-follow-up-count`, 0);

        store.dispatch(
          addNewMessage([
            {
              message: "wouldYouSchedule",
              id: res.data.message_id,
              speaker: "faqBot",
              subject: "schedule",
              createdAt: new Date(),
              follow_up: res.data.follow_up,
            },
          ])
        );
      }
    });
  }
};

export function getLocalizedText(response) {
  // Case 1: Already a string → return directly
  if (typeof response === "string") {
    return response;
  }

  // Case 2: Object with translations
  if (response && typeof response === "object") {
    const hasEn = Object.prototype.hasOwnProperty.call(response, "en");
    const hasEs = Object.prototype.hasOwnProperty.call(response, "es");

    if (hasEn && hasEs) {
      // Normalize i18n language to match API keys
      const currentLang = i18n.language === "sp" ? "es" : "en";

      return response[currentLang] || response.en; // fallback to English
    }
  }

  // Case 3: Unexpected format → return as-is
  return response;
}

export const extractResponseMessage = (response) => {
  if (typeof response === "string") {
    return response;
  }

  if (response && typeof response === "object" && !Array.isArray(response)) {
    if (response.headline && typeof response.headline === "string") {
      return response.headline;
    }

    if (response.description && typeof response.description === "string") {
      return response.description;
    }

    if (response.message && typeof response.message === "string") {
      return response.message;
    }
  }

  return "";
};

export const processDisclaimerHtml = (htmlString) => {
  if (!htmlString) {
    return "";
  }

  // Use a temporary DOM element to parse the HTML string.
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  // Find all anchor tags within the parsed HTML.
  const links = tempDiv.querySelectorAll("a");

  // Iterate over each link and add the target attribute if it's missing.
  links.forEach((link) => {
    // Check if the link already has a 'target' attribute to avoid overwriting.
    if (!link.hasAttribute("target")) {
      link.setAttribute("target", "_blank");
    }
  });

  // Return the modified HTML string.
  return tempDiv.innerHTML;
};

// Utility function to convert URLs in text to clickable links
export const linkifyText = (text) => {
  if (!text || typeof text !== "string") {
    return text;
  }

  const urlRegex = /(https?:\/\/[^\s]+)/g;

  return text.replace(urlRegex, (url) => {
    let cleanUrl = url;
    let trailingPunctuation = "";

    const lastChar = url.slice(-1);

    if ([".", ",", "!", "?", ";", ":"].includes(lastChar)) {
      cleanUrl = url.slice(0, -1);
      trailingPunctuation = lastChar;
    }

    return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" style="color: #26BBED; text-decoration: underline;">${cleanUrl}</a>${trailingPunctuation}`;
  });
};

export const hexToRgba = (hex, opacity) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  return hex;
};

// Any letter or digit, in any language. No `g` flag: it makes .test() stateful.
const HAS_LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;
const HAS_EMOJI = /\p{Extended_Pictographic}/u;

export const AMA_INPUT_ERRORS = {
  MAX_LENGTH: "MAX_LENGTH",
  EMOJI_ONLY: "EMOJI_ONLY",
  SYMBOLS_ONLY: "SYMBOLS_ONLY",
};

// Translation key and params for each error's alert text.
export const getAmaInputErrorCopy = (errorType) => {
  switch (errorType) {
    case AMA_INPUT_ERRORS.MAX_LENGTH:
      return { key: "errorMaxLength", params: { maxLength: AMA_INPUT_MAX_LENGTH } };
    case AMA_INPUT_ERRORS.EMOJI_ONLY:
      return { key: "errorEmoji", params: {} };
    case AMA_INPUT_ERRORS.SYMBOLS_ONLY:
      return { key: "errorPureSymbol", params: {} };
    default:
      return null;
  }
};

/**
 * Returns an AMA_INPUT_ERRORS value, or null if the text can be sent (empty input is ignored).
 * SQL/HTML-looking text is allowed on purpose: it's shown as plain text and never runs.
 * Length uses string.length, like the input's maxLength (an emoji counts as 2).
 */
export const validateAMAInput = (input) => {
  const text = typeof input === "string" ? input.trim() : "";

  if (!text) {
    return null;
  }

  if (text.length > AMA_INPUT_MAX_LENGTH) {
    return AMA_INPUT_ERRORS.MAX_LENGTH;
  }

  if (!HAS_LETTER_OR_DIGIT.test(text)) {
    return HAS_EMOJI.test(text) ? AMA_INPUT_ERRORS.EMOJI_ONLY : AMA_INPUT_ERRORS.SYMBOLS_ONLY;
  }

  return null;
};

export const hasDateInText = (text) => {
  if (!text || typeof text !== "string" || text.trim() === "") {
    return false;
  }

  const str = text.trim();

  const regexPatterns = [
    // Month name formats: "Nov 20, 2025", "November 20 2025"
    /\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\.?\s+\d{1,2}(?:,)?\s*\d{2,4}\b/gi,

    // Numeric year-first formats: "2025-11-20", "2025/11/20"
    /\b\d{4}[/-]\d{1,2}[/-]\d{1,2}\b/g,

    // Numeric day-first formats: "20-11-2025", "20/11/25"
    /\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/g,

    // Textual + numeric hybrids like "20th Nov 2025"
    /\b\d{1,2}(?:st|nd|rd|th)?\s+(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?),?\s*\d{2,4}\b/gi,
  ];

  for (const regex of regexPatterns) {
    const match = str.match(regex);

    if (match && match.length > 0) {
      const dateCandidate = match[0];

      if (
        moment(dateCandidate, moment.ISO_8601, true).isValid() ||
        moment(dateCandidate, ["MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "MMM D, YYYY", "MMMM D, YYYY"], true).isValid()
      ) {
        return true;
      }

      return true;
    }
  }

  return false;
};

export const sortByPriceThenName = (items) => {
  const toNumeric = (price) => {
    if (typeof price === "number") {
      return price;
    }

    if (typeof price === "string") {
      // grab the first number if present, e.g. "50/mo" -> 50
      const m = price.match(/-?\d+(\.\d+)?/);

      if (m) {
        return parseFloat(m[0]);
      }
    }

    return Number.NEGATIVE_INFINITY; // treat non-numeric as lowest
  };

  return [...items].sort((a, b) => {
    const pa = toNumeric(a.price);
    const pb = toNumeric(b.price);

    // 1) numeric price desc
    if (Number.isFinite(pa) && Number.isFinite(pb)) {
      if (pb !== pa) {
        return pb - pa;
      }
    } else if (Number.isFinite(pa) && !Number.isFinite(pb)) {
      return -1; // numbers before text
    } else if (!Number.isFinite(pa) && Number.isFinite(pb)) {
      return 1; // text after numbers
    }

    // 2) tie-breaker: alphabetical by name
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
};

export const getPropertyId = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const property_id = urlParams.get("property_id");

  return property_id;
};
